import CommunityNavBadge from "../components/CommunityNavBadge";
import "../styles/BioIndexCommunityMobile.css";
import "../styles/BioIndexProfile.css";
import "../styles/BioIndexProfileCover.css";
import "../styles/BioIndexProfileFriends.css";
import "../styles/BioIndexProfileTools.css";
import "../styles/BioIndexProfileImageAdjust.css";
import "../styles/BioIndexProfileLiveEditor.css";
import "../styles/BioIndexProfileMobileIsolated.css";
import {
  PROFILE_COUNTRIES,
  getProfileCountry,
} from "../utils/profileCountries";
/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import ProfileCollectionViewer from "../components/CollectionViewer";
import ProfileCollections from "../components/ProfileCollections";
import ProfileGenusCards from "../components/ProfileGenusCards";
import ProfileTaxonomyChallenge from "../components/ProfileTaxonomyChallenge";
import ProfileSocial from "../components/ProfileSocial";
import ProfileFavorites from "../components/ProfileFavorites";
import ProfilePhotoViewer from "../components/ProfilePhotoViewer";
import ProfileTaxonomicCoverage from "../components/ProfileTaxonomicCoverage";
import ProfileRecentUploads from "../components/RecentUploads";
import LocationPickerModal from "../components/LocationPickerModal";
import commonNameTagIcon from "../assets/common-name-tag.png";
import scientificNameTagIcon from "../assets/scientific-name-tag.png";
import defaultProfile from "../assets/default.png";

import { useEffect, useLayoutEffect, useState, useMemo, useRef } from "react";
import { db, storage, auth } from "../firebase";
import { signOut } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  doc,
  getDoc,
  addDoc,
  setDoc,
  getDocs,
  collection,
  onSnapshot,
  query,
  where,
  limit,
  deleteDoc,
  updateDoc,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import {
  Link,
  useParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  generaByFamily,
  familiesByOrder,
  ordersByClass,
  classesByPhylum,
  phylaByKingdom,
} from "../utils/taxonomy";
import SearchModal from "../components/SearchModal";
import useAdminClaim from "../hooks/useAdminClaim";
import { speciesNames } from "../utils/speciesNames";
import { TITLES, evaluateTitles } from "../utils/titles";

function buildTaxonomyParentLookup(groups) {
  const lookup = new Map();

  Object.entries(groups || {}).forEach(([parent, children]) => {
    (children || []).forEach((child) => {
      lookup.set(
        String(child || "")
          .toLowerCase()
          .trim(),
        String(parent || "")
          .toLowerCase()
          .trim(),
      );
    });
  });

  return lookup;
}

const FAMILY_BY_GENUS = buildTaxonomyParentLookup(generaByFamily);
const ORDER_BY_FAMILY = buildTaxonomyParentLookup(familiesByOrder);
const CLASS_BY_ORDER = buildTaxonomyParentLookup(ordersByClass);
const PHYLUM_BY_CLASS = buildTaxonomyParentLookup(classesByPhylum);
const KINGDOM_BY_PHYLUM = buildTaxonomyParentLookup(phylaByKingdom);

function scheduleIdleTask(callback, timeout = 1200) {
  if (typeof window === "undefined") return () => {};

  if (typeof window.requestIdleCallback === "function") {
    const taskId = window.requestIdleCallback(callback, { timeout });
    return () => window.cancelIdleCallback(taskId);
  }

  const taskId = window.setTimeout(callback, 100);
  return () => window.clearTimeout(taskId);
}

function normalizeScientificName(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

function getRankingSpeciesCount(profileData = {}) {
  const rawCount =
    profileData.rankingSpeciesCount ?? profileData.speciesCount ?? 0;
  const numericCount = Number(rawCount);

  return Number.isFinite(numericCount) && numericCount > 0
    ? Math.floor(numericCount)
    : 0;
}

// Crop coordinates are stored with the profile; original uploads remain untouched.
const DEFAULT_PORTRAIT_CROP = { x: 50, y: 42, zoom: 1 };
const DEFAULT_COVER_CROP = { x: 50, y: 45, zoom: 1 };

function normalizeProfileImageCrop(raw, defaults) {
  const clamp = (value, min, max, fallback) => {
    const number = Number(value);
    return Number.isFinite(number)
      ? Math.min(max, Math.max(min, number))
      : fallback;
  };
  return {
    x: clamp(raw?.x, 0, 100, defaults.x),
    y: clamp(raw?.y, 0, 100, defaults.y),
    zoom: clamp(raw?.zoom, 1, 2.75, defaults.zoom),
  };
}

function profileImageCropStyle(rawCrop, defaults) {
  const crop = normalizeProfileImageCrop(rawCrop, defaults);
  return {
    objectPosition: `${crop.x}% ${crop.y}%`,
    transformOrigin: `${crop.x}% ${crop.y}%`,
    transform: `scale(${crop.zoom})`,
  };
}

function ProfileRankingMarkers({ rankings, loading }) {
  const globalRank = rankings
    ? `#${rankings.globalPosition.toLocaleString()}`
    : loading
      ? "…"
      : "—";

  const friendRank = rankings?.friendPosition
    ? `#${rankings.friendPosition.toLocaleString()}`
    : loading
      ? "…"
      : "—";

  return (
    <div
      className="flex shrink-0 items-center gap-2.5 text-[10px] text-[#374049]/55"
      title="Ranked by documented species. Tied users share the same position."
    >
      <span className="inline-flex items-center gap-1 whitespace-nowrap">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="h-3.5 w-3.5 text-[#01767A]"
        >
          <path d="M8 4h8v4a4 4 0 0 1-8 0V4Z" />
          <path d="M8 6H5v1a4 4 0 0 0 4 4" />
          <path d="M16 6h3v1a4 4 0 0 1-4 4" />
          <path d="M12 12v4M9 20h6M10 16h4v4h-4Z" />
        </svg>
        <span>BioIndex</span>
        <strong className="font-semibold text-[#374049]">{globalRank}</strong>
      </span>

      <span aria-hidden="true" className="h-3 w-px bg-[#93CA87]/60" />

      <span className="inline-flex items-center gap-1 whitespace-nowrap">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="h-3.5 w-3.5 text-[#01767A]"
        >
          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        <span>Friends</span>
        <strong className="font-semibold text-[#374049]">{friendRank}</strong>
      </span>
    </div>
  );
}

function normalizeDate(value) {
  if (!value) return { year: "NA", month: "NA", day: "NA" };

  if (typeof value === "object") {
    return {
      year: value.year || "NA",
      month: value.month || "NA",
      day: value.day || "NA",
    };
  }

  return { year: "NA", month: "NA", day: "NA" };
}

function formatDateForDisplay(dateVal) {
  const d = normalizeDate(dateVal);
  if (!d.year || d.year === "NA") return "Unknown";

  const monthNames = {
    "01": "January",
    "02": "February",
    "03": "March",
    "04": "April",
    "05": "May",
    "06": "June",
    "07": "July",
    "08": "August",
    "09": "September",
    10: "October",
    11: "November",
    12: "December",
  };

  if (!d.month || d.month === "NA") return d.year;
  const monthName = monthNames[d.month] || d.month;
  if (!d.day || d.day === "NA") return `${monthName} ${d.year}`;

  return `${String(Number(d.day))} ${monthName} ${d.year}`;
}

function hasMappedLocation(form) {
  return form?.locationLat != null && form?.locationLng != null;
}

function displayLocation(form) {
  return form?.locationName || form?.location || "";
}

function formToLocationData(form) {
  return {
    name: form?.locationName || form?.location || "",
    lat: form?.locationLat ?? null,
    lng: form?.locationLng ?? null,
    country: form?.locationCountry || "",
    adminArea: form?.locationAdminArea || "",
    city: form?.locationCity || "",
    placeId: form?.locationPlaceId || "",
  };
}

function isBioIndexProfilePhoto(url) {
  if (!url) return false;

  const value = String(url);

  return (
    value.includes("firebasestorage.googleapis.com") &&
    (value.includes("/o/profile%2F") || value.includes("/o/profile/"))
  );
}

async function createUserNotification(recipientUid, notificationData) {
  if (!recipientUid || !notificationData?.dedupeKey) return;

  await addDoc(collection(db, "users", recipientUid, "notifications"), {
    ...notificationData,
    createdAt: serverTimestamp(),
  });
}

async function removeFriendRequestNotification(recipientUid, requestId) {
  if (!recipientUid || !requestId) return;

  const notificationsSnapshot = await getDocs(
    query(
      collection(db, "users", recipientUid, "notifications"),
      where("friendRequestId", "==", requestId),
    ),
  );

  const matchingNotifications = notificationsSnapshot.docs.filter(
    (notificationDocument) =>
      notificationDocument.data().type === "friend_request",
  );

  await Promise.all(
    matchingNotifications.map((notificationDocument) =>
      deleteDoc(notificationDocument.ref),
    ),
  );
}

function getUserBlockId(blockerUid, blockedUid) {
  return `${blockerUid}__${blockedUid}`;
}

const DAYS = [
  "NA",
  ...Array.from({ length: 31 }, (_, i) => String(i + 1).padStart(2, "0")),
];
const MONTHS = [
  { value: "NA", label: "NA" },
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];
const YEARS = [
  "NA",
  ...Array.from({ length: 80 }, (_, i) => String(new Date().getFullYear() - i)),
];

function ensureSingleDefault(forms) {
  const arr = Array.isArray(forms) ? [...forms] : [];
  if (!arr.length) return arr;

  if (!arr.some((f) => f?.isDefault)) arr[0] = { ...arr[0], isDefault: true };
  else {
    let found = false;
    for (let i = 0; i < arr.length; i++) {
      if (arr[i]?.isDefault) {
        if (!found) found = true;
        else arr[i] = { ...arr[i], isDefault: false };
      }
    }
  }

  return arr;
}

function profileAuditSnapshot(profileData = {}) {
  return {
    name: String(profileData.name || ""),
    country: String(profileData.country || ""),
    bio: String(profileData.bio || ""),
    photoURL: String(profileData.photoURL || ""),
    coverPhotoURL: String(profileData.coverPhotoURL || ""),
    photoCrop: normalizeProfileImageCrop(
      profileData.photoCrop,
      DEFAULT_PORTRAIT_CROP,
    ),
    coverCrop: normalizeProfileImageCrop(
      profileData.coverCrop,
      DEFAULT_COVER_CROP,
    ),
  };
}

function observationAuditSnapshot(speciesData = {}, formData = {}) {
  return {
    speciesId: String(speciesData.id || ""),
    scientificName: String(speciesData.name || ""),
    commonName: String(speciesData.common || ""),
    formId: String(formData.id || ""),
    formURL: String(formData.url || ""),
    label: String(formData.label || ""),
    date: {
      day: String(formData?.date?.day || "NA"),
      month: String(formData?.date?.month || "NA"),
      year: String(formData?.date?.year || "NA"),
    },
    location: {
      name: String(formData.locationName || formData.location || ""),
      lat:
        typeof formData.locationLat === "number" ? formData.locationLat : null,
      lng:
        typeof formData.locationLng === "number" ? formData.locationLng : null,
      country: String(formData.locationCountry || ""),
      adminArea: String(formData.locationAdminArea || ""),
      city: String(formData.locationCity || ""),
      placeId: String(formData.locationPlaceId || ""),
    },
    variant: String(formData.variant || ""),
    notes: String(formData.notes || ""),
    collections: Array.isArray(formData.collections)
      ? formData.collections.map((collectionName) => String(collectionName))
      : [],
    isDefault: !!formData.isDefault,
  };
}

function BioIndexProfileMobileNav({
  onSearch,
  onVoiceSearch,
  explorePath = "/explore",
}) {
  const [exploreOpen, setExploreOpen] = useState(false);
  const exploreMenuRef = useRef(null);

  useEffect(() => {
    if (!exploreOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!exploreMenuRef.current?.contains(event.target)) {
        setExploreOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setExploreOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [exploreOpen]);

  const openSearch = () => {
    setExploreOpen(false);
    onSearch?.();
  };
  const openVoiceSearch = () => {
    setExploreOpen(false);
    onVoiceSearch?.();
  };

  return (
    <nav className="bioindex-m-bottom-bar" aria-label="Primary navigation">
      <div className="bioindex-m-bottom-grid">
        <Link className="bioindex-m-bottom-item" to="/" aria-label="Home">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 10.8 12 3l9 7.8V21h-6v-7H9v7H3Z" />
          </svg>
          <span>Home</span>
        </Link>

        <div className="bioindex-m-explore-wrap" ref={exploreMenuRef}>
          <button
            type="button"
            className={`bioindex-m-bottom-item bioindex-m-explore-trigger${exploreOpen ? " bioindex-m-explore-active" : ""}`}
            onClick={() => setExploreOpen((open) => !open)}
            aria-expanded={exploreOpen}
            aria-controls="bioindex-m-explore-menu"
            aria-label="Explore or Search"
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="9" />
              <path d="m15.9 8.1-2.2 5.5-5.5 2.2 2.2-5.5Z" />
            </svg>
            <span>Explore</span>
          </button>

          {exploreOpen && (
            <div
              className="bioindex-m-explore-menu"
              id="bioindex-m-explore-menu"
              role="group"
              aria-label="Explore options"
            >
              <Link
                to={explorePath}
                className="bioindex-m-explore-choice"
                onClick={() => setExploreOpen(false)}
              >
                <span
                  className="bioindex-m-explore-choice-icon"
                  aria-hidden="true"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="8.6" />
                    <path d="m15.6 8.4-2.2 5-5 2.2 2.2-5Z" />
                  </svg>
                </span>
                <span>Explore taxonomy</span>
              </Link>
              <button
                type="button"
                className="bioindex-m-explore-choice"
                onClick={openSearch}
              >
                <span
                  className="bioindex-m-explore-choice-icon"
                  aria-hidden="true"
                >
                  <img src="/Search.png" alt="" />
                </span>
                <span>Search BioIndex</span>
              </button>
              <button
                type="button"
                className="bioindex-m-explore-choice"
                onClick={openVoiceSearch}
              >
                <span
                  className="bioindex-m-explore-choice-icon"
                  aria-hidden="true"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <rect x="9" y="2" width="6" height="12" rx="3" />
                    <path d="M5 10a7 7 0 0 0 14 0M12 17v5m-4 0h8" />
                  </svg>
                </span>
                <span>Voice Search</span>
              </button>
            </div>
          )}
        </div>

        <Link
          className="bioindex-m-bottom-item bioindex-m-add"
          to="/upload"
          aria-label="Add Photos"
        >
          <span className="bioindex-m-add-circle" aria-hidden="true">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.9"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M4 7h3l2-2h6l2 2h3a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2Z" />
              <circle cx="12" cy="13" r="3.4" />
            </svg>
          </span>
          <span>Add Photos</span>
        </Link>

        <div
          className="bioindex-m-bottom-item bioindex-m-current"
          aria-label="Profile"
          aria-current="page"
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="12" cy="7.5" r="4" />
            <path d="M4.5 21v-2.7a7.5 7.5 0 0 1 15 0V21" />
          </svg>
          <span>Profile</span>
        </div>

        <Link
          to="/forum"
          className="bioindex-m-bottom-item"
          aria-label="Community"
        >
          <span className="bioindex-m-current-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              aria-hidden="true"
            >
              <circle cx="9" cy="8" r="3" />
              <path d="M2.5 20v-2a6.5 6.5 0 0 1 13 0v2M17 5.5a3 3 0 0 1 0 5.5M17 14a5.5 5.5 0 0 1 4 5.5" />
            </svg>
            <CommunityNavBadge />
          </span>
          <span>Community</span>
        </Link>
      </div>
    </nav>
  );
}

function MobileProfileName({ name }) {
  const nameRef = useRef(null);

  useLayoutEffect(() => {
    const element = nameRef.current;
    if (!element) return;

    const fitToTwoLines = () => {
      element.style.fontSize = "";
      const originalSize = parseFloat(
        window.getComputedStyle(element).fontSize,
      );
      if (!originalSize) return;

      const fits = (size) => {
        element.style.fontSize = `${size}px`;
        const lineHeight = parseFloat(
          window.getComputedStyle(element).lineHeight,
        );
        return element.getBoundingClientRect().height <= lineHeight * 2 + 1;
      };

      if (fits(originalSize)) {
        element.style.fontSize = "";
        return;
      }

      let minimum = 12;
      let maximum = originalSize;

      for (let i = 0; i < 10; i += 1) {
        const middle = (minimum + maximum) / 2;
        if (fits(middle)) minimum = middle;
        else maximum = middle;
      }

      element.style.fontSize = `${minimum}px`;
    };

    let frame;
    const scheduleFit = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fitToTwoLines);
    };

    fitToTwoLines();

    let previousWidth = element.parentElement?.clientWidth || 0;
    const observer = new ResizeObserver(([entry]) => {
      const width = entry?.contentRect.width || 0;
      if (Math.abs(width - previousWidth) < 1) return;
      previousWidth = width;
      scheduleFit();
    });

    if (element.parentElement) observer.observe(element.parentElement);

    window.addEventListener("resize", scheduleFit);

    let active = true;
    document.fonts?.ready.then(() => {
      if (active) scheduleFit();
    });

    return () => {
      active = false;
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener("resize", scheduleFit);
    };
  }, [name]);

  return (
    <h1 ref={nameRef} style={{ fontFamily: "Cormorant Garamond" }}>
      {name}
    </h1>
  );
}

export default function ProfilePage() {
  const { uid } = useParams();
  const { isAdmin, adminLoading } = useAdminClaim();
  const [searchParams] = useSearchParams();

  const profileUID = uid || auth.currentUser.uid;
  const isOwnProfile = profileUID === auth.currentUser.uid;

  const adminModeRequested = searchParams.get("adminMode") === "true";

  const waitingForAdminModeCheck =
    adminModeRequested && !isOwnProfile && adminLoading;

  const isAdminManagingProfile =
    adminModeRequested && !adminLoading && isAdmin && !isOwnProfile;

  const profileExplorePath = isOwnProfile
    ? "/explore"
    : isAdminManagingProfile
      ? `/explore/${profileUID}?adminMode=true`
      : `/explore/${profileUID}`;

  const canEditProfileContent = isOwnProfile || isAdminManagingProfile;

  const canEditObservationMetadata = isOwnProfile || isAdminManagingProfile;

  const canReclassifyObservations = isOwnProfile || isAdminManagingProfile;

  const canManageAccountStatus = isAdminManagingProfile;

  const canManageOwnAccount = isOwnProfile;

  const canDeleteObservations = isAdminManagingProfile;

  const canManageCollections = isAdminManagingProfile;

  const canUseProfileSocialControls = !isOwnProfile && !isAdminManagingProfile;

  const [profile, setProfile] = useState({
    name: "",
    location: "",
    country: "",
    bio: "",
    photoURL: "",
    coverPhotoURL: "",
    favorites: [],
  });

  const isProfileSuspended = profile?.accountStatus?.status === "suspended";

  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [deletingProfile, setDeletingProfile] = useState(false);

  const [profileNameDraft, setProfileNameDraft] = useState("");
  const [profileBioDraft, setProfileBioDraft] = useState("");
  const [profileCountryDraft, setProfileCountryDraft] = useState("");
  const [pendingProfilePhoto, setPendingProfilePhoto] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState("");
  const [pendingProfileCover, setPendingProfileCover] = useState(null);
  const [profileCoverPreview, setProfileCoverPreview] = useState("");
  const [useDefaultProfileCover, setUseDefaultProfileCover] = useState(false);
  const [profileBioExpanded, setProfileBioExpanded] = useState(false);
  const [profileEditError, setProfileEditError] = useState("");
  const [portraitCropDraft, setPortraitCropDraft] = useState(
    DEFAULT_PORTRAIT_CROP,
  );
  const [coverCropDraft, setCoverCropDraft] = useState(DEFAULT_COVER_CROP);
  // Mobile and desktop share image URLs but store independent focal positions.
  const [mobilePortraitCropDraft, setMobilePortraitCropDraft] = useState(
    DEFAULT_PORTRAIT_CROP,
  );
  const [mobileCoverCropDraft, setMobileCoverCropDraft] =
    useState(DEFAULT_COVER_CROP);
  const [isMobileFraming, setIsMobileFraming] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(max-width: 639px)").matches,
  );
  useEffect(() => {
    const media = window.matchMedia("(max-width: 639px)");
    const update = () => setIsMobileFraming(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  const [profileEditorFocus, setProfileEditorFocus] = useState(null);

  const [accountStatusAction, setAccountStatusAction] = useState(null);
  const [accountStatusReason, setAccountStatusReason] = useState("");
  const [accountStatusError, setAccountStatusError] = useState("");
  const [accountStatusSaving, setAccountStatusSaving] = useState(false);
  const [adminDeleteAccountOpen, setAdminDeleteAccountOpen] = useState(false);
  const [adminAccountDeleteReason, setAdminAccountDeleteReason] = useState("");
  const [adminAccountDeleteConfirmation, setAdminAccountDeleteConfirmation] =
    useState("");
  const [adminAccountDeleteError, setAdminAccountDeleteError] = useState("");
  const [adminAccountDeleteSaving, setAdminAccountDeleteSaving] =
    useState(false);

  const [speciesWithPhotos, setSpeciesWithPhotos] = useState([]);
  const [collections, setCollections] = useState({});
  const [favSelectorOpen, setFavSelectorOpen] = useState(false);
  const [favSlotIndex, setFavSlotIndex] = useState(null);
  const [selectedSpeciesForFav, setSelectedSpeciesForFav] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);

  const [savedMessage, setSavedMessage] = useState(false);
  const [friends, setFriends] = useState([]);
  const [friendPreviewColumns, setFriendPreviewColumns] = useState(6);
  const [friendPreviewDesktop, setFriendPreviewDesktop] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 1024px)").matches,
  );
  const [blockedUserIds, setBlockedUserIds] = useState([]);
  const [blockedByUserIds, setBlockedByUserIds] = useState([]);
  const [blockRelationshipsLoading, setBlockRelationshipsLoading] =
    useState(true);
  const [manageProfileOpen, setManageProfileOpen] = useState(false);
  const [blockedUsersOpen, setBlockedUsersOpen] = useState(false);
  const [blockingUserId, setBlockingUserId] = useState("");
  const [requestsReceived, setRequestsReceived] = useState([]);
  const [requestsSent, setRequestsSent] = useState([]);
  const [friendsModalOpen, setFriendsModalOpen] = useState(false);
  const [requestsModalOpen, setRequestsModalOpen] = useState(false);
  const [discoverModalOpen, setDiscoverModalOpen] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [deletingOwnObservation, setDeletingOwnObservation] = useState(false);
  const [favViewOpen, setFavViewOpen] = useState(false);
  const [favViewSpecies, setFavViewSpecies] = useState(null);
  const [favViewForm, setFavViewForm] = useState(null);
  const [favViewImageLoaded, setFavViewImageLoaded] = useState(false);
  const [favViewFullscreen, setFavViewFullscreen] = useState(false);
  const [recentUploadViewerIndex, setRecentUploadViewerIndex] = useState(null);

  const [collectionViewOpen, setCollectionViewOpen] = useState(false);
  const [collectionPhotos, setCollectionPhotos] = useState([]);
  const [collectionIndex, setCollectionIndex] = useState(0);
  const [collectionName, setCollectionName] = useState("");
  const [collectionImageLoaded, setCollectionImageLoaded] = useState(false);
  const [collectionFullscreen, setCollectionFullscreen] = useState(false);
  const [favCollectionsPopoverOpen, setFavCollectionsPopoverOpen] =
    useState(false);
  const [collectionIntroOpen, setCollectionIntroOpen] = useState(false);
  const [collectionIntro, setCollectionIntro] = useState(null);
  const [collectionMeta, setCollectionMeta] = useState({});
  const [collectionSelectorOpen, setCollectionSelectorOpen] = useState(false);
  const [selectedSpeciesForCollection, setSelectedSpeciesForCollection] =
    useState(null);
  const [selectedPhotoForCollection, setSelectedPhotoForCollection] =
    useState(null);
  const [confirmAddOpen, setConfirmAddOpen] = useState(false);
  const [photoAddedMessage, setPhotoAddedMessage] = useState(false);

  const [editingForm, setEditingForm] = useState(null);
  const [editingSpecies, setEditingSpecies] = useState(null);
  const [profileLocationPickerOpen, setProfileLocationPickerOpen] =
    useState(false);
  const [renamingPhoto, setRenamingPhoto] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const [renameCommon, setRenameCommon] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);
  const [renameError, setRenameError] = useState(null);
  const [renameSuggestions, setRenameSuggestions] = useState([]);
  const [renameActiveIndex, setRenameActiveIndex] = useState(0);
  const [renameActiveField, setRenameActiveField] = useState(null);

  const [adminDeleteTarget, setAdminDeleteTarget] = useState(null);
  const [adminDeleteReason, setAdminDeleteReason] = useState("");
  const [adminDeleteError, setAdminDeleteError] = useState("");
  const [deletingObservation, setDeletingObservation] = useState(false);

  const [editingCollection, setEditingCollection] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");

  const [loadingProfile, setLoadingProfile] = useState(true);
  const [profileRefreshKey, setProfileRefreshKey] = useState(0);

  const [sectionLoading, setSectionLoading] = useState({
    profile: true,
    taxonomy: true,
    collections: true,
    quiz: true,
    social: true,
  });
  const [sectionProgress, setSectionProgress] = useState({
    profile: 0,
    taxonomy: 0,
    collections: 0,
    quiz: 0,
    social: 0,
  });
  const [friendsTaxa, setFriendsTaxa] = useState({});
  const [friendsTaxaLoading, setFriendsTaxaLoading] = useState(false);
  const [friendQuizCollections, setFriendQuizCollections] = useState({});
  const [ownTaxa, setOwnTaxa] = useState(null);
  const [quizType, setQuizType] = useState("scientific-from-photo");
  const [questionCount, setQuestionCount] = useState(10);

  const [filterType, setFilterType] = useState("");
  const [filterValue, setFilterValue] = useState("");
  const [quizFriendUID, setQuizFriendUID] = useState("");

  const [quizSpecies, setQuizSpecies] = useState([]);
  const [showGenusHint, setShowGenusHint] = useState(false);
  const [showCollectionsHint, setShowCollectionsHint] = useState(false);

  const [taxonViewOpen, setTaxonViewOpen] = useState(false);
  const [taxonViewLabel, setTaxonViewLabel] = useState("");
  const [taxonViewItems, setTaxonViewItems] = useState([]);
  const [taxonSortMode, setTaxonSortMode] = useState("scientific");
  const [taxonomyNotice, setTaxonomyNotice] = useState("");

  const [recentUploads, setRecentUploads] = useState([]);
  const [favoriteGenera, setFavoriteGenera] = useState([]);
  const [genusModalOpen, setGenusModalOpen] = useState(false);
  const [editingGenera, setEditingGenera] = useState(false);
  const [genusSlotIndex, setGenusSlotIndex] = useState(null);
  const [genusPickerOpen, setGenusPickerOpen] = useState(false);
  const [selectedGenus, setSelectedGenus] = useState(null);
  const [savingGenus, setSavingGenus] = useState(false);

  const [titleModalOpen, setTitleModalOpen] = useState(false);
  const [selectedTitle] = useState("Beginner");
  const [pendingTitle, setPendingTitle] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);
  const [titleViewMode, setTitleViewMode] = useState("all");
  const [unlockedTitles, setUnlockedTitles] = useState([
    {
      id: "beginner",
      label: "Beginner",
      description: "Starting your collection",
    },
  ]);

  const profileModalHistoryPushedRef = useRef(false);
  const activeProfileModalRef = useRef(false);
  const backgroundProfileRefreshRef = useRef(false);
  const profilePhotoInputRef = useRef(null);
  const profileCoverInputRef = useRef(null);
  const profileEditorDialogRef = useRef(null);
  const cropDragRef = useRef(null);
  const friendPreviewStripRef = useRef(null);
  const profileLoadGenerationRef = useRef(0);
  const recentUploadTouchStartRef = useRef(null);
  const PROFILE_CACHE_VERSION = 8;
  const profileCacheKey = `bioindex_profile_cache_v${PROFILE_CACHE_VERSION}_${profileUID}`;

  function readOwnProfileCache() {
    if (!isOwnProfile) return null;

    try {
      const raw = localStorage.getItem(profileCacheKey);
      if (!raw) return null;

      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  function saveOwnProfileCache(data) {
    if (!isOwnProfile) return;

    try {
      const raw = localStorage.getItem(profileCacheKey);
      const existing = raw ? JSON.parse(raw) : {};

      localStorage.setItem(
        profileCacheKey,
        JSON.stringify({
          ...existing,
          ...data,
          cachedAt: Date.now(),
        }),
      );
    } catch {
      // Ignore localStorage failures.
    }
  }

  function updateSectionProgress(section, progress) {
    setSectionProgress((prev) => ({
      ...prev,
      [section]: progress,
    }));
  }

  function finishSectionLoading(section) {
    setSectionProgress((prev) => ({
      ...prev,
      [section]: 100,
    }));

    setSectionLoading((prev) => ({
      ...prev,
      [section]: false,
    }));
  }

  function SectionRefreshOverlay({ show, progress, label = "Refreshing" }) {
    if (!show) return null;

    return (
      <div className="absolute inset-0 z-20 bg-white/75 backdrop-blur-[1px] rounded-3xl flex flex-col items-center justify-center text-center">
        <div className="relative w-12 h-12 mb-3">
          <div className="absolute inset-0 rounded-full border-4 border-[#93ca87]/40" />
          <div className="absolute inset-0 rounded-full border-4 border-[#24c3be] border-t-transparent animate-spin" />
        </div>

        <div className="text-xs text-slate-600">{label}</div>
      </div>
    );
  }

  useEffect(() => {
    return () => {
      if (profilePhotoPreview) {
        URL.revokeObjectURL(profilePhotoPreview);
      }
    };
  }, [profilePhotoPreview]);

  useEffect(() => {
    return () => {
      if (profileCoverPreview) URL.revokeObjectURL(profileCoverPreview);
    };
  }, [profileCoverPreview]);

  useEffect(() => {
    setProfileBioExpanded(false);
  }, [profileUID]);

  useEffect(() => {
    if (isEditing) return;

    setProfileEditorFocus(null);
    setPendingProfilePhoto(null);
    setProfilePhotoPreview("");
    setPendingProfileCover(null);
    setProfileCoverPreview("");
    setUseDefaultProfileCover(false);
    setProfileEditError("");

    if (profilePhotoInputRef.current) profilePhotoInputRef.current.value = "";
    if (profileCoverInputRef.current) profileCoverInputRef.current.value = "";
  }, [isEditing]);

  useEffect(() => {
    if (!isEditing) return undefined;

    function handleProfileEditorKeyDown(event) {
      if (event.key === "Escape" && !savingProfile) {
        setIsEditing(false);
      }
    }

    window.addEventListener("keydown", handleProfileEditorKeyDown);

    return () => {
      window.removeEventListener("keydown", handleProfileEditorKeyDown);
    };
  }, [isEditing, savingProfile]);

  function openProfileEditor() {
    if (!canEditProfileContent) return;

    setProfileNameDraft(profile.name || "");
    setProfileBioDraft(profile.bio || "");
    setProfileCountryDraft(profile.country || "");
    setPendingProfilePhoto(null);
    setProfilePhotoPreview("");
    setPendingProfileCover(null);
    setProfileCoverPreview("");
    setUseDefaultProfileCover(false);
    setPortraitCropDraft(
      normalizeProfileImageCrop(profile.photoCrop, DEFAULT_PORTRAIT_CROP),
    );
    setCoverCropDraft(
      normalizeProfileImageCrop(profile.coverCrop, DEFAULT_COVER_CROP),
    );
    setMobilePortraitCropDraft(
      normalizeProfileImageCrop(profile.photoCropMobile, DEFAULT_PORTRAIT_CROP),
    );
    setMobileCoverCropDraft(
      normalizeProfileImageCrop(profile.coverCropMobile, DEFAULT_COVER_CROP),
    );
    setProfileEditError("");

    if (profilePhotoInputRef.current) profilePhotoInputRef.current.value = "";
    if (profileCoverInputRef.current) profileCoverInputRef.current.value = "";

    setIsEditing(true);
  }

  // Only the profile owner can open the editor directly from the hero images.
  function openProfileImageEditor(section) {
    if (!isOwnProfile) return;
    setProfileEditorFocus(section);
    openProfileEditor();
  }

  // Edit against the original profile card, now presented as a modal.
  // Lock background scrolling and keep keyboard focus inside the editing interface.
  useEffect(() => {
    if (!isEditing) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const dialog = profileEditorDialogRef.current;
    const focusFrame = window.requestAnimationFrame(() => dialog?.focus());

    const trapFocus = (event) => {
      if (event.key !== "Tab" || !dialog) return;
      const focusable = Array.from(
        dialog.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => element.getClientRects().length > 0);
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === dialog)
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (document.activeElement === last || document.activeElement === dialog)
      ) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", trapFocus);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", trapFocus);
    };
  }, [isEditing]);

  // Dragging the images is the only positioning control: no zoom or XY sliders.
  function beginCropDrag(event, kind) {
    if (savingProfile || event.button !== 0) return;
    setProfileEditorFocus(kind);
    const rect = event.currentTarget.getBoundingClientRect();
    cropDragRef.current = {
      kind,
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      width: rect.width,
      height: rect.height,
      crop:
        kind === "cover"
          ? isMobileFraming
            ? mobileCoverCropDraft
            : coverCropDraft
          : isMobileFraming
            ? mobilePortraitCropDraft
            : portraitCropDraft,
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  }

  function moveCropDrag(event, kind) {
    const drag = cropDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId || drag.kind !== kind)
      return;
    const sensitivity = 100 / Math.max(0.5, drag.crop.zoom);
    const x =
      drag.crop.x - ((event.clientX - drag.x) / drag.width) * sensitivity;
    const y =
      drag.crop.y - ((event.clientY - drag.y) / drag.height) * sensitivity;
    const defaults =
      kind === "cover" ? DEFAULT_COVER_CROP : DEFAULT_PORTRAIT_CROP;
    const setter =
      kind === "cover"
        ? isMobileFraming
          ? setMobileCoverCropDraft
          : setCoverCropDraft
        : isMobileFraming
          ? setMobilePortraitCropDraft
          : setPortraitCropDraft;
    setter(normalizeProfileImageCrop({ ...drag.crop, x, y }, defaults));
  }

  function endCropDrag(event) {
    if (cropDragRef.current?.pointerId === event.pointerId) {
      cropDragRef.current = null;
      if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    }
  }

  function closeProfileEditor() {
    if (savingProfile) return;

    setProfileEditorFocus(null);
    setIsEditing(false);
  }

  function clearPendingProfilePhoto() {
    setPendingProfilePhoto(null);
    setProfilePhotoPreview("");
    setPortraitCropDraft(
      normalizeProfileImageCrop(profile.photoCrop, DEFAULT_PORTRAIT_CROP),
    );
    setMobilePortraitCropDraft(
      normalizeProfileImageCrop(profile.photoCropMobile, DEFAULT_PORTRAIT_CROP),
    );
    setProfileEditError("");

    if (profilePhotoInputRef.current) {
      profilePhotoInputRef.current.value = "";
    }
  }

  function handleProfilePhotoSelection(event) {
    const file = event.target.files?.[0] || null;

    if (!file) {
      clearPendingProfilePhoto();
      return;
    }

    const allowedImageTypes = new Set([
      "image/png",
      "image/jpeg",
      "image/webp",
      "image/gif",
    ]);

    if (!allowedImageTypes.has(file.type)) {
      setPendingProfilePhoto(null);
      setProfilePhotoPreview("");
      setProfileEditError("Please select a PNG, JPEG, WebP, or GIF image.");

      event.target.value = "";
      return;
    }

    if (file.size > 8 * 1024 * 1024) {
      setPendingProfilePhoto(null);
      setProfilePhotoPreview("");
      setProfileEditError("Profile photos cannot exceed 8 MB.");

      event.target.value = "";
      return;
    }

    setProfileEditError("");
    setPendingProfilePhoto(file);
    setProfilePhotoPreview(URL.createObjectURL(file));
    setPortraitCropDraft(DEFAULT_PORTRAIT_CROP);
    setMobilePortraitCropDraft(DEFAULT_PORTRAIT_CROP);
  }

  function clearPendingProfileCover() {
    setPendingProfileCover(null);
    setProfileCoverPreview("");
    setCoverCropDraft(
      normalizeProfileImageCrop(profile.coverCrop, DEFAULT_COVER_CROP),
    );
    setMobileCoverCropDraft(
      normalizeProfileImageCrop(profile.coverCropMobile, DEFAULT_COVER_CROP),
    );
    setUseDefaultProfileCover(false);
    setProfileEditError("");
    if (profileCoverInputRef.current) profileCoverInputRef.current.value = "";
  }

  function resetProfileCoverToDefault() {
    setPendingProfileCover(null);
    setProfileCoverPreview("");
    setUseDefaultProfileCover(true);
    setCoverCropDraft(DEFAULT_COVER_CROP);
    setMobileCoverCropDraft(DEFAULT_COVER_CROP);
    setProfileEditError("");
    if (profileCoverInputRef.current) profileCoverInputRef.current.value = "";
  }

  function handleProfileCoverSelection(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setProfileEditError("Choose a PNG, JPEG, or WebP cover image.");
      event.target.value = "";
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      setProfileEditError("Cover images cannot exceed 8 MB.");
      event.target.value = "";
      return;
    }

    setPendingProfileCover(file);
    setProfileCoverPreview(URL.createObjectURL(file));
    setCoverCropDraft(DEFAULT_COVER_CROP);
    setMobileCoverCropDraft(DEFAULT_COVER_CROP);
    setUseDefaultProfileCover(false);
    setProfileEditError("");
  }

  function normalizeTimestamp(value) {
    if (!value) return 0;
    if (typeof value === "number") return value;
    if (value.seconds) return value.seconds * 1000;
    return Number(value) || 0;
  }

  function getCachedSectionTime(cachedData, section) {
    return normalizeTimestamp(cachedData?.sectionUpdatedAt?.[section]);
  }

  function getRemoteSectionTime(profileData, section) {
    return normalizeTimestamp(profileData?.sectionUpdatedAt?.[section]);
  }

  function isSectionStale(cachedData, profileData, section) {
    if (!cachedData) return true;

    const cachedTime = getCachedSectionTime(cachedData, section);
    const remoteTime = getRemoteSectionTime(profileData, section);

    if (!cachedTime) return true;
    if (!remoteTime) return true;

    return cachedTime < remoteTime;
  }

  const blockedRelationshipUserIdSet = useMemo(
    () => new Set([...blockedUserIds, ...blockedByUserIds]),
    [blockedUserIds, blockedByUserIds],
  );

  const profileInteractionBlocked =
    !isOwnProfile &&
    !isAdminManagingProfile &&
    blockedRelationshipUserIdSet.has(profileUID);

  const visibleProfiles = useMemo(
    () =>
      profiles.filter(
        (candidate) => !blockedRelationshipUserIdSet.has(candidate.uid),
      ),
    [profiles, blockedRelationshipUserIdSet],
  );

  const visibleFriends = useMemo(
    () =>
      friends.filter(
        (friendUid) => !blockedRelationshipUserIdSet.has(friendUid),
      ),
    [friends, blockedRelationshipUserIdSet],
  );

  // Match the number of friend previews to the actual sidebar width.
  // Desktop displays two rows, reserving the last position for +N when needed.
  // Tablet keeps the existing six friends +N; mobile hides this preview.
  useEffect(() => {
    if (!isOwnProfile || loadingProfile) return undefined;

    const strip = friendPreviewStripRef.current;
    if (!strip) return undefined;

    const updatePreviewColumns = () => {
      const desktop = window.matchMedia("(min-width: 1024px)").matches;
      setFriendPreviewDesktop(desktop);
      if (!desktop) return;

      const width = strip.clientWidth;
      if (width > 0) {
        const avatarSize = 43;
        const gap = 8;
        setFriendPreviewColumns(
          Math.max(2, Math.floor((width + gap) / (avatarSize + gap))),
        );
      }
    };

    updatePreviewColumns();
    const observer =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updatePreviewColumns)
        : null;
    observer?.observe(strip);
    window.addEventListener("resize", updatePreviewColumns);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updatePreviewColumns);
    };
  }, [isOwnProfile, loadingProfile, visibleFriends.length]);

  const sortedFriendPreviewUids = useMemo(
    () =>
      [...visibleFriends].sort((a, b) => {
        const profileA = visibleProfiles.find((person) => person.uid === a);
        const profileB = visibleProfiles.find((person) => person.uid === b);
        return (
          getRankingSpeciesCount(profileB) - getRankingSpeciesCount(profileA) ||
          (profileA?.name || "").localeCompare(profileB?.name || "")
        );
      }),
    [visibleFriends, visibleProfiles],
  );
  const friendPreviewSlots = friendPreviewDesktop
    ? friendPreviewColumns * 2
    : 7;
  const friendPreviewPhotoLimit =
    sortedFriendPreviewUids.length > friendPreviewSlots
      ? friendPreviewSlots - 1
      : friendPreviewDesktop
        ? friendPreviewSlots
        : 6;
  const shownFriendPreviewUids = sortedFriendPreviewUids.slice(
    0,
    friendPreviewPhotoLimit,
  );
  const remainingFriendPreviewCount =
    sortedFriendPreviewUids.length - shownFriendPreviewUids.length;

  const friendCollectionOptions = useMemo(() => {
    const profileByUid = new Map(
      visibleProfiles
        .filter((candidate) => candidate?.uid)
        .map((candidate) => [candidate.uid, candidate]),
    );

    return visibleFriends
      .map((friendUid) => {
        const friendProfile = profileByUid.get(friendUid) || {};
        const collectionsForFriend = friendQuizCollections[friendUid] || {};
        const collectionNames = Object.keys(collectionsForFriend).sort((a, b) =>
          a.localeCompare(b, undefined, { sensitivity: "base" }),
        );

        return {
          uid: friendUid,
          name: friendProfile.name || "BioIndex Friend",
          collections: collectionNames,
        };
      })
      .filter((friend) => friend.collections.length > 0)
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      );
  }, [friendQuizCollections, visibleFriends, visibleProfiles]);

  const profileRankings = useMemo(() => {
    if (!profiles.length) return null;

    const directoryByUid = new Map(
      profiles
        .filter((candidate) => candidate?.uid)
        .map((candidate) => [candidate.uid, candidate]),
    );

    const storedProfile = directoryByUid.get(profileUID) || profile;
    const freshlyCalculatedCount = Number(stats?.totalSpecies);
    const speciesCount = Number.isFinite(freshlyCalculatedCount)
      ? freshlyCalculatedCount
      : getRankingSpeciesCount(storedProfile);

    directoryByUid.set(profileUID, {
      ...storedProfile,
      uid: profileUID,
      rankingSpeciesCount: speciesCount,
      speciesCount,
    });

    const allProfiles = Array.from(directoryByUid.values());
    const globalPosition =
      1 +
      allProfiles.filter(
        (candidate) => getRankingSpeciesCount(candidate) > speciesCount,
      ).length;

    const currentUid = auth.currentUser?.uid || "";
    const friendCircleUids = new Set([currentUid, ...friends].filter(Boolean));
    const belongsToFriendCircle = friendCircleUids.has(profileUID);
    const friendCircleProfiles = Array.from(friendCircleUids)
      .map((friendUid) => directoryByUid.get(friendUid))
      .filter(Boolean);

    const friendPosition = belongsToFriendCircle
      ? 1 +
        friendCircleProfiles.filter(
          (candidate) => getRankingSpeciesCount(candidate) > speciesCount,
        ).length
      : null;

    return {
      speciesCount,
      globalPosition,
      globalTotal: allProfiles.length,
      friendPosition,
      friendTotal: friendCircleProfiles.length,
    };
  }, [friends, profile, profileUID, profiles, stats?.totalSpecies]);

  const visibleRequestsReceived = useMemo(
    () =>
      requestsReceived.filter(
        (request) => !blockedRelationshipUserIdSet.has(request.uid),
      ),
    [requestsReceived, blockedRelationshipUserIdSet],
  );

  const visibleRequestsSent = useMemo(
    () =>
      requestsSent.filter(
        (targetUid) => !blockedRelationshipUserIdSet.has(targetUid),
      ),
    [requestsSent, blockedRelationshipUserIdSet],
  );

  const activeProfileModal = useMemo(
    () =>
      favSelectorOpen ||
      favViewOpen ||
      collectionSelectorOpen ||
      confirmAddOpen ||
      genusModalOpen ||
      genusPickerOpen ||
      searchOpen ||
      manageProfileOpen ||
      blockedUsersOpen ||
      titleModalOpen ||
      taxonViewOpen ||
      collectionIntroOpen ||
      collectionViewOpen,
    [
      favSelectorOpen,
      favViewOpen,
      collectionSelectorOpen,
      confirmAddOpen,
      genusModalOpen,
      genusPickerOpen,
      searchOpen,
      manageProfileOpen,
      blockedUsersOpen,
      titleModalOpen,
      taxonViewOpen,
      collectionIntroOpen,
      collectionViewOpen,
    ],
  );

  const fullScreenProfileOverlayOpen =
    collectionViewOpen ||
    collectionSelectorOpen ||
    confirmAddOpen ||
    collectionIntroOpen ||
    genusModalOpen;

  useEffect(() => {
    if (!fullScreenProfileOverlayOpen) return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousBodyOverscrollBehavior =
      document.body.style.overscrollBehavior;
    const previousHtmlOverscrollBehavior =
      document.documentElement.style.overscrollBehavior;

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    document.body.style.overscrollBehavior = "none";
    document.documentElement.style.overscrollBehavior = "none";

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.body.style.overscrollBehavior = previousBodyOverscrollBehavior;
      document.documentElement.style.overscrollBehavior =
        previousHtmlOverscrollBehavior;
    };
  }, [fullScreenProfileOverlayOpen]);

  useEffect(() => {
    if (!taxonViewOpen) setTaxonomyNotice("");
  }, [taxonViewOpen]);

  useEffect(() => {
    if (!favViewOpen) {
      setRecentUploadViewerIndex(null);
    }
  }, [favViewOpen]);

  useEffect(() => {
    if (
      !favViewOpen ||
      recentUploadViewerIndex === null ||
      editingForm ||
      renamingPhoto
    ) {
      recentUploadTouchStartRef.current = null;
      return undefined;
    }

    function showRecentUploadAtIndex(nextIndex) {
      if (nextIndex < 0 || nextIndex >= recentUploads.length) return;

      const nextUpload = recentUploads[nextIndex];
      const nextSpecies = speciesWithPhotos.find(
        (species) => species.id === nextUpload.speciesId,
      );

      if (!nextSpecies) return;

      const nextForm =
        (nextSpecies.forms || []).find((form) => form.url === nextUpload.url) ||
        nextUpload;

      setRecentUploadViewerIndex(nextIndex);
      setFavViewSpecies(nextSpecies);
      setFavViewForm(nextForm);
      setFavViewImageLoaded(false);
      setFavViewFullscreen(false);
      setFavCollectionsPopoverOpen(false);
    }

    function handleRecentUploadTouchStart(event) {
      if (event.touches.length !== 1) return;

      const interactiveElement = event.target.closest?.(
        "button, input, textarea, select, a",
      );

      if (interactiveElement) return;

      recentUploadTouchStartRef.current = {
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
      };
    }

    function handleRecentUploadTouchEnd(event) {
      const touchStart = recentUploadTouchStartRef.current;
      recentUploadTouchStartRef.current = null;

      if (!touchStart || event.changedTouches.length !== 1) return;

      const deltaX = event.changedTouches[0].clientX - touchStart.x;
      const deltaY = event.changedTouches[0].clientY - touchStart.y;

      if (Math.abs(deltaX) < 55 || Math.abs(deltaX) <= Math.abs(deltaY)) {
        return;
      }

      showRecentUploadAtIndex(recentUploadViewerIndex + (deltaX < 0 ? 1 : -1));
    }

    function handleRecentUploadKeyDown(event) {
      if (event.key === "ArrowLeft") {
        showRecentUploadAtIndex(recentUploadViewerIndex - 1);
      }

      if (event.key === "ArrowRight") {
        showRecentUploadAtIndex(recentUploadViewerIndex + 1);
      }
    }

    document.addEventListener("touchstart", handleRecentUploadTouchStart, {
      passive: true,
    });

    document.addEventListener("touchend", handleRecentUploadTouchEnd, {
      passive: true,
    });

    window.addEventListener("keydown", handleRecentUploadKeyDown);

    return () => {
      document.removeEventListener("touchstart", handleRecentUploadTouchStart);

      document.removeEventListener("touchend", handleRecentUploadTouchEnd);

      window.removeEventListener("keydown", handleRecentUploadKeyDown);
    };
  }, [
    editingForm,
    favViewOpen,
    recentUploadViewerIndex,
    recentUploads,
    renamingPhoto,
    speciesWithPhotos,
  ]);

  useEffect(() => {
    profileLoadGenerationRef.current += 1;

    setProfile({
      name: "",
      location: "",
      country: "",
      bio: "",
      photoURL: "",
      coverPhotoURL: "",
      favorites: [],
      title: "Beginner",
    });
    setStats(null);
    setSpeciesWithPhotos([]);
    setCollections({});
    setRecentUploads([]);
    setCollectionMeta({});
    setQuizSpecies([]);
    setFilterType("");
    setFilterValue("");
    setQuizFriendUID("");
    setFriendQuizCollections({});
    setFavoriteGenera([]);
    setUnlockedTitles([
      {
        id: "beginner",
        label: "Beginner",
        description: "Starting your collection",
      },
    ]);

    setFavViewOpen(false);
    setFavViewSpecies(null);
    setFavViewForm(null);
    setRecentUploadViewerIndex(null);
    setCollectionViewOpen(false);
    setCollectionPhotos([]);
    setCollectionName("");
    setEditingForm(null);
    setEditingSpecies(null);
    setProfileLocationPickerOpen(false);
    setLoadingProfile(true);
    setSectionLoading({
      profile: true,
      taxonomy: true,
      collections: true,
      quiz: true,
      social: true,
    });
    setSectionProgress({
      profile: 0,
      taxonomy: 0,
      collections: 0,
      quiz: 0,
      social: 0,
    });
  }, [profileUID]);

  useEffect(() => {
    let cancelled = false;

    async function loadOwnTaxa() {
      if (!auth.currentUser || isOwnProfile) return;

      try {
        const snap = await getDocs(
          collection(db, "users", auth.currentUser.uid, "species"),
        );

        const taxa = {
          species: new Set(),
          genus: new Set(),
          family: new Set(),
          order: new Set(),
          class: new Set(),
          phylum: new Set(),
          kingdom: new Set(),
        };

        snap.forEach((doc) => {
          const data = doc.data();
          if (!data.genus) return;

          const t = resolveTaxonomy(data.genus);

          if (data.name) taxa.species.add(data.name);
          if (t.genus) taxa.genus.add(t.genus);
          if (t.family) taxa.family.add(t.family);
          if (t.order) taxa.order.add(t.order);
          if (t.className) taxa.class.add(t.className);
          if (t.phylum) taxa.phylum.add(t.phylum);
          if (t.kingdom) taxa.kingdom.add(t.kingdom);
        });

        if (cancelled) return;
        setOwnTaxa(taxa);
      } catch {
        if (cancelled) return;
        setOwnTaxa(null);
      }
    }

    const cancelLoadStart = scheduleIdleTask(() => {
      loadOwnTaxa();
    });

    return () => {
      cancelled = true;
      cancelLoadStart();
    };
    // The current profile load already produces ownTaxa for the signed-in user.
    // Only friend pages need this separate comparison dataset.
  }, [isOwnProfile]);

  useEffect(() => {
    activeProfileModalRef.current = activeProfileModal;

    if (activeProfileModal && !profileModalHistoryPushedRef.current) {
      window.history.pushState(
        { ...(window.history.state || {}), profileModal: true },
        "",
        window.location.href,
      );

      profileModalHistoryPushedRef.current = true;
    }

    if (!activeProfileModal) {
      profileModalHistoryPushedRef.current = false;
    }
  }, [activeProfileModal]);

  useEffect(() => {
    function handleProfileModalBack() {
      if (!activeProfileModalRef.current) return;

      setFavSelectorOpen(false);
      setFavViewOpen(false);
      setFavViewFullscreen(false);
      setFavCollectionsPopoverOpen(false);
      setCollectionSelectorOpen(false);
      setConfirmAddOpen(false);
      setFriendsModalOpen(false);
      setRequestsModalOpen(false);
      setDiscoverModalOpen(false);
      setGenusModalOpen(false);
      setGenusPickerOpen(false);
      setSearchOpen(false);
      setIsEditing(false);
      setManageProfileOpen(false);
      setBlockedUsersOpen(false);
      setTitleModalOpen(false);
      setTaxonViewOpen(false);
      setCollectionIntroOpen(false);
      setCollectionViewOpen(false);
      setCollectionFullscreen(false);
      setEditingForm(null);
      setEditingSpecies(null);

      activeProfileModalRef.current = false;
      profileModalHistoryPushedRef.current = false;
    }

    window.addEventListener("popstate", handleProfileModalBack);

    return () => {
      window.removeEventListener("popstate", handleProfileModalBack);
    };
  }, []);

  // Keep cached coverage, collections, genus cards and quiz species in sync
  // when an administrator updates the profile while this page is open.
  useEffect(() => {
    if (!profileUID) return undefined;
    let initialized = false;
    let previousTaxonomyTime = 0;
    let previousCollectionsTime = 0;
    return onSnapshot(
      doc(db, "profile", profileUID),
      (snapshot) => {
        if (!snapshot.exists()) return;
        const stamps = snapshot.data()?.sectionUpdatedAt || {};
        const normalize = (value) =>
          value?.seconds ? value.seconds * 1000 : Number(value) || 0;
        const taxonomyTime = normalize(stamps.taxonomy);
        const collectionsTime = normalize(stamps.collections);
        if (
          initialized &&
          (taxonomyTime > previousTaxonomyTime ||
            collectionsTime > previousCollectionsTime)
        ) {
          setProfileRefreshKey((value) => value + 1);
        }
        initialized = true;
        previousTaxonomyTime = Math.max(previousTaxonomyTime, taxonomyTime);
        previousCollectionsTime = Math.max(
          previousCollectionsTime,
          collectionsTime,
        );
      },
      (error) => {
        if (error?.code !== "permission-denied") {
          console.warn("Could not watch profile taxonomy updates:", error);
        }
      },
    );
  }, [profileUID]);

  useEffect(() => {
    if (
      !isOwnProfile &&
      (blockRelationshipsLoading ||
        waitingForAdminModeCheck ||
        profileInteractionBlocked)
    ) {
      return undefined;
    }

    let cancelled = false;
    const loadGeneration = profileLoadGenerationRef.current;
    const isCurrentProfileLoad = () =>
      !cancelled && profileLoadGenerationRef.current === loadGeneration;

    backgroundProfileRefreshRef.current = false;

    const cachedProfileData = readOwnProfileCache();

    if (cachedProfileData) {
      if (cachedProfileData.profile) {
        setProfile(cachedProfileData.profile);
      }

      if (cachedProfileData.stats) {
        setStats(cachedProfileData.stats);
      }

      if (cachedProfileData.favoriteGenera) {
        setFavoriteGenera(cachedProfileData.favoriteGenera);
      }

      if (cachedProfileData.speciesWithPhotos) {
        setSpeciesWithPhotos(cachedProfileData.speciesWithPhotos);

        if (isOwnProfile) {
          setOwnTaxa(
            cachedProfileData.ownTaxaValues
              ? deserializeTaxa(cachedProfileData.ownTaxaValues)
              : buildTaxaFromSpeciesList(cachedProfileData.speciesWithPhotos),
          );
        }
      }

      if (cachedProfileData.collections) {
        setCollections(cachedProfileData.collections);
      }

      if (cachedProfileData.recentUploads) {
        setRecentUploads(cachedProfileData.recentUploads);
      }

      if (cachedProfileData.collectionMeta) {
        setCollectionMeta(cachedProfileData.collectionMeta);
      }

      if (cachedProfileData.titleUserData) {
        const recalculatedTitles = evaluateTitles(
          cachedProfileData.titleUserData,
        );

        const hasBeginner = recalculatedTitles.some(
          (title) => title.id === "beginner",
        );

        setUnlockedTitles(
          hasBeginner
            ? recalculatedTitles
            : [
                {
                  id: "beginner",
                  label: "Beginner",
                  description: "Starting your collection",
                },
                ...recalculatedTitles,
              ],
        );
      } else if (cachedProfileData.unlockedTitles) {
        setUnlockedTitles(cachedProfileData.unlockedTitles);
      }

      if (cachedProfileData.quizSpecies) {
        setQuizSpecies(cachedProfileData.quizSpecies);
      } else if (cachedProfileData.speciesWithPhotos) {
        setQuizSpecies(
          cachedProfileData.speciesWithPhotos.map((sp) => ({
            name: sp.name,
            genus: sp.name?.split(" ")[0] || "",
          })),
        );
      }

      setLoadingProfile(false);

      setSectionLoading({
        profile: true,
        taxonomy: true,
        collections: true,
        quiz: true,
        social: true,
      });

      setSectionProgress({
        profile: 10,
        taxonomy: 10,
        collections: 10,
        quiz: 10,
        social: 10,
      });
    }

    async function loadProfile(profileSnap = null) {
      setSectionLoading((prev) => ({ ...prev, profile: true }));
      updateSectionProgress("profile", 25);

      const snap =
        profileSnap || (await getDoc(doc(db, "profile", profileUID)));

      if (!isCurrentProfileLoad()) return;

      updateSectionProgress("profile", 70);

      if (snap.exists()) {
        const data = snap.data();

        const normalizedProfile = {
          ...data,
          photoURL: isBioIndexProfilePhoto(data.photoURL) ? data.photoURL : "",
          title: data.title || "Beginner",
        };

        setProfile(normalizedProfile);

        saveOwnProfileCache({
          profile: normalizedProfile,
          favoriteGenera: data.favoriteGenera || favoriteGenera,
          sectionUpdatedAt: {
            ...(readOwnProfileCache()?.sectionUpdatedAt || {}),
            profile: getRemoteSectionTime(data, "profile") || Date.now(),
          },
        });

        if (data.favoriteGenera) {
          setFavoriteGenera(data.favoriteGenera);
        }
      }

      if (!isCurrentProfileLoad()) return;

      finishSectionLoading("profile");
      setLoadingProfile(false);
    }

    let unsubProfiles = null;
    const cancelProfileDirectoryStart = scheduleIdleTask(() => {
      if (!isCurrentProfileLoad()) return;

      unsubProfiles = onSnapshot(
        collection(db, "profile"),
        (snap) => {
          if (!isCurrentProfileLoad()) return;

          const baseProfiles = snap.docs.map((d) => ({
            uid: d.id,
            ...d.data(),
          }));

          setProfiles(baseProfiles);
          finishSectionLoading("social");
        },
        (error) => {
          if (!isCurrentProfileLoad()) return;

          if (error?.code === "permission-denied") {
            console.warn(
              "Profile directory access is unavailable while the account is restricted.",
            );
          } else {
            console.error("Failed to load profile directory:", error);
          }

          setProfiles([]);
          finishSectionLoading("social");
        },
      );
    });

    async function loadStats(profileSnapForGenera = null) {
      setSectionLoading((prev) => ({ ...prev, taxonomy: true }));
      updateSectionProgress("taxonomy", 15);

      const snap = await getDocs(
        collection(db, "users", profileUID, "species"),
      );

      if (!isCurrentProfileLoad()) return;

      updateSectionProgress("taxonomy", 40);

      const counts = {
        kingdom: {},
        phylum: {},
        class: {},
        order: {},
        family: {},
        genus: {},
      };

      const speciesCounts = {};
      const uniqueSpeciesKeys = new Set();
      const speciesDocumentsByKey = new Map();
      const malformedSpeciesDocuments = [];
      let validGenusDocumentCount = 0;

      const speciesWithPhotosLocal = [];
      let totalPhotos = 0;
      const locationSet = new Set();
      const uploads = [];
      const countrySpeciesMap = {};
      const regionSpeciesMap = {};
      const speciesCountryMap = {};
      const loadedTaxa = createEmptyTaxa();

      snap.forEach((docSnap) => {
        const data = docSnap.data();
        const genus = data?.genus?.trim();
        const genusKey = genus?.toLowerCase();
        if (!genus || !genusKey) return;
        validGenusDocumentCount++;

        const scientificName = String(data?.name || "").trim();
        const scientificNameKey = normalizeScientificName(scientificName);

        if (scientificNameKey) {
          uniqueSpeciesKeys.add(scientificNameKey);

          if (!speciesDocumentsByKey.has(scientificNameKey)) {
            speciesDocumentsByKey.set(scientificNameKey, []);
          }

          speciesDocumentsByKey.get(scientificNameKey).push({
            documentId: docSnap.id,
            scientificName,
            genus,
            photoCount: Array.isArray(data.forms) ? data.forms.length : 0,
          });
        } else {
          malformedSpeciesDocuments.push({
            documentId: docSnap.id,
            scientificName: data?.name ?? null,
            genus,
            photoCount: Array.isArray(data.forms) ? data.forms.length : 0,
          });
        }

        if (scientificName) {
          speciesCounts[scientificName] =
            (speciesCounts[scientificName] || 0) + 1;
        }

        counts.genus[genus] = (counts.genus[genus] || 0) + 1;

        const taxonomy = resolveTaxonomy(genus);
        if (scientificName) loadedTaxa.species.add(scientificName);
        loadedTaxa.genus.add(taxonomy.genus);

        const family = taxonomy.family;
        if (!family) return;
        loadedTaxa.family.add(family);
        counts.family[family] = (counts.family[family] || 0) + 1;

        const order = taxonomy.order;
        if (!order) return;
        loadedTaxa.order.add(order);
        counts.order[order] = (counts.order[order] || 0) + 1;

        const className = taxonomy.className;
        if (!className) return;
        loadedTaxa.class.add(className);
        counts.class[className] = (counts.class[className] || 0) + 1;

        const phylum = taxonomy.phylum;
        if (!phylum) return;
        loadedTaxa.phylum.add(phylum);
        counts.phylum[phylum] = (counts.phylum[phylum] || 0) + 1;

        const kingdom = taxonomy.kingdom;
        if (!kingdom) return;
        loadedTaxa.kingdom.add(kingdom);
        counts.kingdom[kingdom] = (counts.kingdom[kingdom] || 0) + 1;

        if (data.forms && data.forms.length > 0) {
          totalPhotos += data.forms.length;

          const speciesCountries = new Set();
          const speciesRegions = new Set();

          data.forms.forEach((f) => {
            if (f.location && f.location !== "") {
              locationSet.add(f.location);
            }

            const country = String(f.locationCountry || "")
              .trim()
              .toLowerCase();
            const region = String(f.locationAdminArea || "")
              .trim()
              .toLowerCase();

            if (country) {
              speciesCountries.add(country);

              if (scientificName) {
                if (!speciesCountryMap[scientificName]) {
                  speciesCountryMap[scientificName] = {};
                }

                speciesCountryMap[scientificName][country] =
                  (speciesCountryMap[scientificName][country] || 0) + 1;
              }
            }

            if (region) speciesRegions.add(region);

            uploads.push({
              ...f,
              url: f.url,
              scientific: data.name,
              common: data.common || "",
              speciesId: docSnap.id,
              created: f.createdAt?.seconds || 0,
            });
          });

          speciesCountries.forEach((country) => {
            countrySpeciesMap[country] = (countrySpeciesMap[country] || 0) + 1;
          });

          speciesRegions.forEach((region) => {
            regionSpeciesMap[region] = (regionSpeciesMap[region] || 0) + 1;
          });

          speciesWithPhotosLocal.push({
            id: docSnap.id,
            name: data.name,
            common: data.common || "",
            genus,
            forms: data.forms,
          });
        }
      });

      if (!isCurrentProfileLoad()) return;

      const duplicateSpeciesDocuments = [...speciesDocumentsByKey.entries()]
        .filter(([, documents]) => documents.length > 1)
        .map(([normalizedName, documents]) => ({
          normalizedName,
          extraDocumentCount: documents.length - 1,
          documents,
        }));

      const totalSpecies = uniqueSpeciesKeys.size;

      if (
        duplicateSpeciesDocuments.length > 0 ||
        malformedSpeciesDocuments.length > 0
      ) {
        console.groupCollapsed(`[BioIndex species audit] ${profileUID}`);
        console.log("Profile UID:", profileUID);
        console.log(
          "Species documents with a valid genus:",
          validGenusDocumentCount,
        );
        console.log("Unique normalized species:", totalSpecies);

        if (duplicateSpeciesDocuments.length > 0) {
          console.log("Duplicate species document groups:");

          duplicateSpeciesDocuments.forEach((group) => {
            console.group(group.normalizedName);
            console.table(group.documents);
            console.groupEnd();
          });
        }

        if (malformedSpeciesDocuments.length > 0) {
          console.log("Documents missing a usable scientific name:");
          console.table(malformedSpeciesDocuments);
        }

        console.groupEnd();
      }

      updateSectionProgress("taxonomy", 65);

      setStats({ totalSpecies, counts });

      if (isOwnProfile) {
        setOwnTaxa(loadedTaxa);
      }

      const unlockedGenera = Object.keys(counts.genus);

      const profileSnapForGeneraResolved =
        profileSnapForGenera || (await getDoc(doc(db, "profile", profileUID)));

      if (!isCurrentProfileLoad()) return;

      /*
       * Genus notifications and Community Feed activity are generated by the
       * processSpeciesPhotoActivity Cloud Function. ProfilePage only uses the
       * calculated genera for display.
       */

      const savedFavoriteGenera = profileSnapForGeneraResolved.exists()
        ? profileSnapForGeneraResolved.data().favoriteGenera || []
        : [];

      const validFavoriteGenera = savedFavoriteGenera.filter((genus) =>
        unlockedGenera.includes(genus),
      );

      const fallbackGenera = unlockedGenera.filter(
        (genus) => !validFavoriteGenera.includes(genus),
      );

      const displayGenera = [...validFavoriteGenera, ...fallbackGenera].slice(
        0,
        6,
      );

      setFavoriteGenera(displayGenera);

      const userData = {
        totalSpecies,
        totalPhotos,
        totalLocations: locationSet.size,
        totalGenera: Object.keys(counts.genus || {}).length,
        bySpecies: speciesCounts,
        bySpeciesCountry: speciesCountryMap,
        byKingdom: counts.kingdom,
        byPhylum: counts.phylum,
        byClass: counts.class,
        byOrder: counts.order,
        byFamily: counts.family,
        byGenus: counts.genus,
        byCountry: countrySpeciesMap,
        byRegion: regionSpeciesMap,
      };

      /*
       * Daily photo-upload Community Feed summaries are generated by the
       * processSpeciesPhotoActivity Cloud Function.
       */

      let unlocked = evaluateTitles(userData);

      const hasBeginner = unlocked.some((t) => t.id === "beginner");

      if (!hasBeginner) {
        unlocked = [
          {
            id: "beginner",
            label: "Beginner",
            description: "Starting your collection",
          },
          ...unlocked,
        ];
      }

      setUnlockedTitles(unlocked);

      /*
       * Title notifications and Community Feed activity are generated by the
       * processSpeciesPhotoActivity Cloud Function. ProfilePage only evaluates
       * titles for display and title selection.
       */

      const collectionsMap = {};

      speciesWithPhotosLocal.forEach((sp) => {
        (sp.forms || []).forEach((f) => {
          (f.collections || []).forEach((c) => {
            if (!collectionsMap[c]) {
              collectionsMap[c] = [];
            }

            collectionsMap[c].push({
              ...f,
              species: sp.name,
              common: sp.common || "",
              collectionOrder:
                f.collectionAddedAt?.[c] ||
                f.createdAt?.seconds * 1000 ||
                f.createdAt ||
                0,
            });
          });
        });
      });

      Object.keys(collectionsMap).forEach((name) => {
        collectionsMap[name].sort(
          (a, b) => a.collectionOrder - b.collectionOrder,
        );
      });

      const quizSpeciesLocal = speciesWithPhotosLocal.map((sp) => ({
        name: sp.name,
        genus: sp.name?.split(" ")[0] || "",
      }));

      setCollections(collectionsMap);
      setSpeciesWithPhotos(speciesWithPhotosLocal);
      setQuizSpecies(quizSpeciesLocal);

      uploads.sort((a, b) => b.created - a.created);
      const latestUploads = uploads;
      setRecentUploads(latestUploads);

      const metaSnap = await getDocs(
        collection(db, "users", profileUID, "collectionsMeta"),
      );

      if (!isCurrentProfileLoad()) return;

      const meta = {};
      metaSnap.forEach((doc) => {
        meta[doc.id] = doc.data();

        if (!collectionsMap[doc.id]) {
          collectionsMap[doc.id] = [];
        }
      });

      setCollections(collectionsMap);
      setCollectionMeta(meta);

      updateSectionProgress("taxonomy", 90);

      const profileSnap = profileSnapForGeneraResolved;
      const latestProfile = profileSnap.exists()
        ? {
            ...profileSnap.data(),
            photoURL: isBioIndexProfilePhoto(profileSnap.data().photoURL)
              ? profileSnap.data().photoURL
              : "",
            title: profileSnap.data().title || "Beginner",
          }
        : {
            name: "",
            location: "",
            bio: "",
            photoURL: "",
            favorites: [],
            title: "Beginner",
          };

      const latestProfileData = profileSnap.exists() ? profileSnap.data() : {};
      const taxonomyUpdatedAt =
        getRemoteSectionTime(latestProfileData, "taxonomy") || Date.now();
      const collectionsUpdatedAt =
        getRemoteSectionTime(latestProfileData, "collections") ||
        taxonomyUpdatedAt;
      const profileUpdatedAt =
        getRemoteSectionTime(latestProfileData, "profile") || Date.now();

      const sectionUpdatedAt = {
        profile: profileUpdatedAt,
        taxonomy: taxonomyUpdatedAt,
        collections: collectionsUpdatedAt,
        quiz: taxonomyUpdatedAt,
      };

      if (
        isOwnProfile &&
        latestProfileData?.accountStatus?.status !== "suspended"
      ) {
        await setDoc(
          doc(db, "profile", profileUID),
          {
            speciesCount: totalSpecies,
            sectionUpdatedAt,
          },
          { merge: true },
        );

        if (!isCurrentProfileLoad()) return;
      }

      saveOwnProfileCache({
        profile: {
          ...latestProfile,
          sectionUpdatedAt,
        },
        stats: { totalSpecies, counts },
        favoriteGenera: displayGenera,
        speciesWithPhotos: speciesWithPhotosLocal,
        quizSpecies: quizSpeciesLocal,
        collections: collectionsMap,
        recentUploads: latestUploads,
        collectionMeta: meta,
        titleUserData: userData,
        unlockedTitles: unlocked,
        ownTaxaValues: serializeTaxa(loadedTaxa),
        sectionUpdatedAt,
      });

      finishSectionLoading("taxonomy");
      finishSectionLoading("collections");
      finishSectionLoading("quiz");
    }

    async function refreshStaleSections() {
      const cached = readOwnProfileCache();

      const profileSnap = await getDoc(doc(db, "profile", profileUID));

      if (!isCurrentProfileLoad()) return;

      const profileData = profileSnap.exists() ? profileSnap.data() : {};

      const forceFreshLoad = !isOwnProfile;

      const profileStale =
        forceFreshLoad || isSectionStale(cached, profileData, "profile");

      const taxonomyStale =
        forceFreshLoad || isSectionStale(cached, profileData, "taxonomy");

      const collectionsStale =
        forceFreshLoad || isSectionStale(cached, profileData, "collections");

      if (!cached) {
        setSectionLoading({
          profile: true,
          taxonomy: true,
          collections: true,
          quiz: true,
          social: true,
        });

        setSectionProgress({
          profile: 10,
          taxonomy: 10,
          collections: 10,
          quiz: 10,
          social: 10,
        });
      } else {
        setSectionLoading((prev) => ({
          ...prev,
          profile: profileStale,
          taxonomy: taxonomyStale,
          collections: collectionsStale,
          quiz: taxonomyStale,
        }));

        setSectionProgress((prev) => ({
          ...prev,
          profile: profileStale ? 10 : 100,
          taxonomy: taxonomyStale ? 10 : 100,
          collections: collectionsStale ? 10 : 100,
          quiz: taxonomyStale ? 10 : 100,
        }));
      }

      if (profileStale) {
        await loadProfile(profileSnap);
      } else {
        if (!isCurrentProfileLoad()) return;

        finishSectionLoading("profile");
        setLoadingProfile(false);
      }

      if (!isCurrentProfileLoad()) return;

      if (taxonomyStale || collectionsStale) {
        await loadStats(profileSnap);
      } else {
        finishSectionLoading("taxonomy");
        finishSectionLoading("collections");
        finishSectionLoading("quiz");
      }
    }

    refreshStaleSections().catch((error) => {
      if (!isCurrentProfileLoad()) return;

      if (error?.code === "permission-denied") {
        console.warn(
          "Profile refresh stopped because this account cannot currently perform writes.",
        );

        setLoadingProfile(false);
        finishSectionLoading("profile");
        finishSectionLoading("taxonomy");
        finishSectionLoading("collections");
        finishSectionLoading("quiz");

        return;
      }

      console.error("Failed to refresh profile sections:", error);
    });

    let unsubFriends = null;
    let unsubRequests = null;
    let unsubSent = null;

    const currentUID = auth.currentUser.uid;
    const isOwn = profileUID === currentUID;

    unsubFriends = onSnapshot(
      collection(db, "users", currentUID, "friends"),
      (snap) => {
        setFriends(snap.docs.map((d) => d.id));
      },
      (error) => {
        if (error?.code === "permission-denied") {
          console.warn(
            "Friends access is unavailable while the account is restricted.",
          );
        } else {
          console.error("Failed to load friends:", error);
        }

        setFriends([]);
      },
    );

    if (isOwn) {
      const receivedQuery = query(
        collection(db, "friendRequests"),
        where("toUid", "==", currentUID),
        where("status", "==", "pending"),
      );

      const sentQuery = query(
        collection(db, "friendRequests"),
        where("fromUid", "==", currentUID),
        where("status", "==", "pending"),
      );

      unsubRequests = onSnapshot(
        receivedQuery,
        async (snap) => {
          const requests = await Promise.all(
            snap.docs.map(async (d) => {
              const fromUid = d.data().fromUid;

              try {
                const profileSnap = await getDoc(doc(db, "profile", fromUid));

                if (profileSnap.exists()) {
                  const p = profileSnap.data();

                  return {
                    uid: fromUid,
                    requestId: d.id,
                    name: p.name || "Unknown",
                    photoURL: p.photoURL || "",
                    title: p.title || "",
                    speciesCount: p.speciesCount ?? 0,
                  };
                }
              } catch {}

              return {
                uid: fromUid,
                requestId: d.id,
                name: "Unknown",
                photoURL: "",
                title: "",
                speciesCount: 0,
              };
            }),
          );

          setRequestsReceived(requests);
        },
        (error) => {
          if (error?.code === "permission-denied") {
            console.warn(
              "Friend requests are unavailable while the account is restricted.",
            );
          } else {
            console.error("Failed to load received friend requests:", error);
          }

          setRequestsReceived([]);
        },
      );
      unsubSent = onSnapshot(
        sentQuery,
        (snap) => {
          setRequestsSent(snap.docs.map((d) => d.data().toUid));
        },
        (error) => {
          if (error?.code === "permission-denied") {
            console.warn(
              "Sent friend requests are unavailable while the account is restricted.",
            );
          } else {
            console.error("Failed to load sent friend requests:", error);
          }

          setRequestsSent([]);
        },
      );
    }

    return () => {
      cancelled = true;
      cancelProfileDirectoryStart();
      if (unsubProfiles) unsubProfiles();
      if (unsubFriends) unsubFriends();
      if (unsubRequests) unsubRequests();
      if (unsubSent) unsubSent();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    profileUID,
    blockRelationshipsLoading,
    waitingForAdminModeCheck,
    profileInteractionBlocked,
    profileRefreshKey,
  ]);

  useEffect(() => {
    const currentUid = auth.currentUser?.uid;

    if (!currentUid) {
      setBlockedUserIds([]);
      setBlockedByUserIds([]);
      setBlockRelationshipsLoading(false);
      return undefined;
    }

    let outgoingBlocksLoaded = false;
    let incomingBlocksLoaded = false;

    function finishBlockRelationshipLoading() {
      if (outgoingBlocksLoaded && incomingBlocksLoaded) {
        setBlockRelationshipsLoading(false);
      }
    }

    setBlockRelationshipsLoading(true);

    const outgoingBlocksQuery = query(
      collection(db, "userBlocks"),
      where("blockerUid", "==", currentUid),
    );

    const incomingBlocksQuery = query(
      collection(db, "userBlocks"),
      where("blockedUid", "==", currentUid),
    );

    const unsubscribeOutgoingBlocks = onSnapshot(
      outgoingBlocksQuery,
      (snapshot) => {
        setBlockedUserIds(
          snapshot.docs
            .map((blockDocument) => blockDocument.data().blockedUid)
            .filter(Boolean),
        );

        outgoingBlocksLoaded = true;
        finishBlockRelationshipLoading();
      },
      (error) => {
        console.error("Failed to load blocked users:", error);
        outgoingBlocksLoaded = true;
        finishBlockRelationshipLoading();
      },
    );

    const unsubscribeIncomingBlocks = onSnapshot(
      incomingBlocksQuery,
      (snapshot) => {
        setBlockedByUserIds(
          snapshot.docs
            .map((blockDocument) => blockDocument.data().blockerUid)
            .filter(Boolean),
        );

        incomingBlocksLoaded = true;
        finishBlockRelationshipLoading();
      },
      (error) => {
        console.error("Failed to load incoming blocks:", error);
        incomingBlocksLoaded = true;
        finishBlockRelationshipLoading();
      },
    );

    return () => {
      unsubscribeOutgoingBlocks();
      unsubscribeIncomingBlocks();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!visibleFriends.length) {
      setFriendsTaxa({});
      setFriendQuizCollections({});
      setFriendsTaxaLoading(false);
      return undefined;
    }

    async function loadFriendsTaxa() {
      const result = {};
      const quizCollectionsByFriend = {};

      async function loadFriendTaxa(uid) {
        try {
          const snap = await getDocs(collection(db, "users", uid, "species"));
          const taxa = createEmptyTaxa();
          const uniqueSpeciesKeys = new Set();
          const collectionSpeciesByName = {};

          snap.forEach((doc) => {
            const data = doc.data();
            if (!data.genus) return;

            const t = resolveTaxonomy(data.genus);
            const scientificName = String(data.name || "").trim();
            const scientificNameKey = normalizeScientificName(scientificName);

            if (scientificNameKey) {
              taxa.species.add(scientificName);
              uniqueSpeciesKeys.add(scientificNameKey);
            }

            if (t.genus) taxa.genus.add(t.genus);
            if (t.family) taxa.family.add(t.family);
            if (t.order) taxa.order.add(t.order);
            if (t.className) taxa.class.add(t.className);
            if (t.phylum) taxa.phylum.add(t.phylum);
            if (t.kingdom) taxa.kingdom.add(t.kingdom);

            const speciesCollections = new Set();

            (data.forms || []).forEach((form) => {
              (form.collections || []).forEach((rawCollectionName) => {
                const collectionName = String(rawCollectionName || "").trim();

                if (collectionName) speciesCollections.add(collectionName);
              });
            });

            speciesCollections.forEach((collectionName) => {
              if (!collectionSpeciesByName[collectionName]) {
                collectionSpeciesByName[collectionName] = [];
              }

              collectionSpeciesByName[collectionName].push({
                name: data.name || "Unknown species",
                genus: data.genus || "",
              });
            });
          });

          taxa.speciesCount = uniqueSpeciesKeys.size;

          return {
            uid,
            taxa,
            quizCollections: collectionSpeciesByName,
          };
        } catch {
          return {
            uid,
            taxa: null,
            quizCollections: {},
          };
        }
      }

      const batchSize = 3;

      for (let index = 0; index < visibleFriends.length; index += batchSize) {
        const batch = visibleFriends.slice(index, index + batchSize);
        const batchResults = await Promise.all(batch.map(loadFriendTaxa));

        if (cancelled) return;

        batchResults.forEach(({ uid, taxa, quizCollections }) => {
          result[uid] = taxa;
          quizCollectionsByFriend[uid] = quizCollections;
        });
      }

      if (cancelled) return;

      setFriendsTaxa(result);
      setFriendQuizCollections(quizCollectionsByFriend);
      setFriendsTaxaLoading(false);
    }

    setFriendsTaxaLoading(true);

    const cancelLoadStart = scheduleIdleTask(() => {
      loadFriendsTaxa().catch((error) => {
        if (!cancelled) {
          console.error("Failed to load friend taxonomy summaries:", error);
          setFriendsTaxaLoading(false);
        }
      });
    }, 1500);

    return () => {
      cancelled = true;
      cancelLoadStart();
    };
  }, [visibleFriends, profiles]);

  useEffect(() => {
    if (sectionLoading.taxonomy || sectionLoading.profile) return;
    if (!unlockedTitles.length) return;
    if (!profile.title || profile.title === "Beginner") return;

    const labels = unlockedTitles.map((t) => t.label);

    if (!labels.includes(profile.title)) {
      setProfile((prev) => ({ ...prev, title: "Beginner" }));
    }
  }, [
    unlockedTitles,
    profile.title,
    sectionLoading.taxonomy,
    sectionLoading.profile,
  ]);

  function openAccountStatusAction(action) {
    if (!canManageAccountStatus || accountStatusSaving) {
      return;
    }

    setAccountStatusAction(action);
    setAccountStatusReason("");
    setAccountStatusError("");
  }

  function closeAccountStatusAction() {
    if (accountStatusSaving) return;

    setAccountStatusAction(null);
    setAccountStatusReason("");
    setAccountStatusError("");
  }

  async function confirmAccountStatusAction() {
    if (
      !canManageAccountStatus ||
      !profileUID ||
      !accountStatusAction ||
      accountStatusSaving
    ) {
      return;
    }

    const reason = accountStatusReason.trim();

    if (reason.length < 10) {
      setAccountStatusError(
        "Please provide an administrative reason of at least 10 characters.",
      );
      return;
    }

    if (reason.length > 500) {
      setAccountStatusError(
        "The administrative reason cannot exceed 500 characters.",
      );
      return;
    }

    setAccountStatusSaving(true);
    setAccountStatusError("");

    try {
      const functions = getFunctions(undefined, "southamerica-east1");

      const adminSetAccountSuspension = httpsCallable(
        functions,
        "adminSetAccountSuspension",
      );

      await adminSetAccountSuspension({
        targetUid: profileUID,
        action: accountStatusAction,
        reason,
      });

      const refreshedProfileSnapshot = await getDoc(
        doc(db, "profile", profileUID),
      );

      if (refreshedProfileSnapshot.exists()) {
        const refreshedProfile = refreshedProfileSnapshot.data();

        setProfile((currentProfile) => ({
          ...currentProfile,
          ...refreshedProfile,
          photoURL: isBioIndexProfilePhoto(refreshedProfile.photoURL)
            ? refreshedProfile.photoURL
            : "",
          title: refreshedProfile.title || "Beginner",
        }));
      }

      setAccountStatusAction(null);
      setAccountStatusReason("");
      setAccountStatusError("");
    } catch (error) {
      console.error("Account suspension action failed:", error);

      setAccountStatusError(
        error?.message || "The account status could not be changed.",
      );
    } finally {
      setAccountStatusSaving(false);
    }
  }

  function openAdminDeleteAccount() {
    if (!isAdminManagingProfile || adminAccountDeleteSaving) return;

    setAdminDeleteAccountOpen(true);
    setAdminAccountDeleteReason("");
    setAdminAccountDeleteConfirmation("");
    setAdminAccountDeleteError("");
  }

  function closeAdminDeleteAccount() {
    if (adminAccountDeleteSaving) return;

    setAdminDeleteAccountOpen(false);
    setAdminAccountDeleteReason("");
    setAdminAccountDeleteConfirmation("");
    setAdminAccountDeleteError("");
  }

  async function confirmAdminDeleteAccount() {
    if (!isAdminManagingProfile || !profileUID || adminAccountDeleteSaving) {
      return;
    }

    const reason = adminAccountDeleteReason.trim();

    if (reason.length < 10 || reason.length > 500) {
      setAdminAccountDeleteError(
        "Provide an administrative reason between 10 and 500 characters.",
      );
      return;
    }

    if (adminAccountDeleteConfirmation.trim().toUpperCase() !== "DELETE") {
      setAdminAccountDeleteError(
        'Type "DELETE" to confirm this permanent action.',
      );
      return;
    }

    setAdminAccountDeleteSaving(true);
    setAdminAccountDeleteError("");

    try {
      const functions = getFunctions(undefined, "southamerica-east1");
      const adminDeleteUserAccount = httpsCallable(
        functions,
        "adminDeleteUserAccount",
      );

      await adminDeleteUserAccount({ targetUid: profileUID, reason });

      setAdminDeleteAccountOpen(false);
      navigate("/profile", { replace: true });
    } catch (error) {
      console.error("Administrator account deletion failed:", error);
      setAdminAccountDeleteError(
        error?.message || "The account could not be permanently deleted.",
      );
    } finally {
      setAdminAccountDeleteSaving(false);
    }
  }

  async function commitProfileUpdate(
    updatedProfile,
    { action, reason = "Administrative profile assistance" },
  ) {
    if (!profileUID) {
      throw new Error("No target profile was provided.");
    }

    const profileRef = doc(db, "profile", profileUID);

    if (!isAdminManagingProfile) {
      await setDoc(profileRef, updatedProfile, {
        merge: true,
      });

      return;
    }

    const adminUid = auth.currentUser?.uid;

    if (!adminUid) {
      throw new Error("The administrator is not authenticated.");
    }

    const currentProfileSnap = await getDoc(profileRef);
    const currentProfile = currentProfileSnap.exists()
      ? currentProfileSnap.data()
      : {};

    const adminActionRef = doc(collection(db, "adminActions"));
    const batch = writeBatch(db);

    batch.set(profileRef, updatedProfile, {
      merge: true,
    });

    batch.set(adminActionRef, {
      adminUid,
      targetUid: profileUID,
      targetName: String(
        updatedProfile.name || currentProfile.name || "BioIndex user",
      ),
      action,
      resourceType: "profile",
      resourcePath: `profile/${profileUID}`,
      reason,
      before: profileAuditSnapshot(currentProfile),
      after: profileAuditSnapshot(updatedProfile),
      createdAt: serverTimestamp(),
    });

    await batch.commit();
  }

  async function saveProfile() {
    if (!canEditProfileContent || !profileUID) return;

    const now = Date.now();

    let nextPhotoURL = profile.photoURL || "";
    let nextCoverPhotoURL = useDefaultProfileCover
      ? ""
      : profile.coverPhotoURL || "";
    let uploadedProfilePhotoRef = null;
    let uploadedProfileCoverRef = null;

    try {
      if (pendingProfilePhoto) {
        const safeName = pendingProfilePhoto.name.replace(/[^\w.-]/g, "_");

        uploadedProfilePhotoRef = ref(
          storage,
          `profile/${profileUID}/${Date.now()}_${safeName}`,
        );

        await uploadBytes(uploadedProfilePhotoRef, pendingProfilePhoto, {
          contentType: pendingProfilePhoto.type,
        });

        nextPhotoURL = await getDownloadURL(uploadedProfilePhotoRef);
      }

      if (pendingProfileCover && !useDefaultProfileCover) {
        const safeName = pendingProfileCover.name.replace(/[^\w.-]/g, "_");
        // Same Firebase Storage path as existing profile pictures, so the
        // existing profile/{uid}/{file} storage rules also apply to covers.
        uploadedProfileCoverRef = ref(
          storage,
          `profile/${profileUID}/${Date.now()}_cover_${safeName}`,
        );
        await uploadBytes(uploadedProfileCoverRef, pendingProfileCover, {
          contentType: pendingProfileCover.type,
        });
        nextCoverPhotoURL = await getDownloadURL(uploadedProfileCoverRef);
      }

      const updatedProfile = {
        ...profile,
        name: profileNameDraft,
        bio: profileBioDraft,
        country: profileCountryDraft,
        photoURL: nextPhotoURL,
        coverPhotoURL: nextCoverPhotoURL,
        photoCrop: normalizeProfileImageCrop(
          portraitCropDraft,
          DEFAULT_PORTRAIT_CROP,
        ),
        coverCrop: normalizeProfileImageCrop(
          coverCropDraft,
          DEFAULT_COVER_CROP,
        ),
        photoCropMobile: normalizeProfileImageCrop(
          mobilePortraitCropDraft,
          DEFAULT_PORTRAIT_CROP,
        ),
        coverCropMobile: normalizeProfileImageCrop(
          mobileCoverCropDraft,
          DEFAULT_COVER_CROP,
        ),
        sectionUpdatedAt: {
          ...(profile.sectionUpdatedAt || {}),
          profile: now,
        },
      };

      await commitProfileUpdate(updatedProfile, {
        action: pendingProfilePhoto
          ? "replace_profile_photo"
          : pendingProfileCover || useDefaultProfileCover
            ? "update_profile_cover"
            : "update_profile",
        reason: "Administrative profile assistance",
      });

      setProfile(updatedProfile);

      if (isOwnProfile) {
        saveOwnProfileCache({
          profile: updatedProfile,
          stats,
          favoriteGenera,
          speciesWithPhotos,
          quizSpecies,
          collections,
          recentUploads,
          collectionMeta,
          unlockedTitles,
          sectionUpdatedAt: {
            ...(readOwnProfileCache()?.sectionUpdatedAt || {}),
            profile: now,
          },
        });
      }

      setIsEditing(false);
    } catch (error) {
      /*
       * Avoid leaving an unused Storage object behind if the profile document
       * update fails after the image has already uploaded.
       */
      if (uploadedProfilePhotoRef) {
        try {
          await deleteObject(uploadedProfilePhotoRef);
        } catch (cleanupError) {
          console.error("Failed to remove unused profile photo:", cleanupError);
        }
      }

      if (uploadedProfileCoverRef) {
        try {
          await deleteObject(uploadedProfileCoverRef);
        } catch (cleanupError) {
          console.error("Failed to remove unused profile cover:", cleanupError);
        }
      }

      throw error;
    }
  }

  async function acceptRequest(requestId, requesterUid) {
    const user = auth.currentUser;

    if (
      !user ||
      !requestId ||
      !requesterUid ||
      blockedUserIds.includes(requesterUid) ||
      blockedByUserIds.includes(requesterUid)
    ) {
      return;
    }

    const uid = user.uid;

    await Promise.all([
      setDoc(doc(db, "users", uid, "friends", requesterUid), {
        uid: requesterUid,
        requestId,
        createdAt: serverTimestamp(),
      }),
      setDoc(doc(db, "users", requesterUid, "friends", uid), {
        uid,
        requestId,
        createdAt: serverTimestamp(),
      }),
    ]);

    await deleteDoc(doc(db, "friendRequests", requestId));

    try {
      await removeFriendRequestNotification(uid, requestId);
    } catch (notificationCleanupError) {
      console.error(
        "Friend request accepted, but its original notification could not be removed:",
        notificationCleanupError,
      );
    }

    try {
      const actorName =
        String(
          profile.name ||
            user.displayName ||
            user.email?.split("@")[0] ||
            "BioIndex User",
        ).trim() || "BioIndex User";

      const actorPhotoURL = isBioIndexProfilePhoto(profile.photoURL)
        ? profile.photoURL
        : "";

      await createUserNotification(requesterUid, {
        type: "friend_accepted",

        message: `${actorName} accepted your friend request`,
        detail: "You are now friends on BioIndex.",
        targetRoute: `/profile/${uid}`,
        read: false,

        actorUid: uid,
        actorName,
        actorPhotoURL,

        topicId: "",
        commentId: "",
        parentCommentId: "",

        titleId: "",
        genusName: "",

        friendRequestId: requestId,

        dedupeKey: `friend_accepted_${requestId}`,
      });
    } catch (notificationError) {
      console.error(
        "Friend request accepted, but notification creation failed:",
        notificationError,
      );
    }
  }

  async function declineRequest(requestId) {
    const user = auth.currentUser;

    if (!user || !requestId) return;

    await deleteDoc(doc(db, "friendRequests", requestId));

    try {
      await removeFriendRequestNotification(user.uid, requestId);
    } catch (notificationCleanupError) {
      console.error(
        "Friend request declined, but its original notification could not be removed:",
        notificationCleanupError,
      );
    }
  }

  async function addFriendRequest(targetUid) {
    const user = auth.currentUser;

    if (
      !user ||
      !targetUid ||
      targetUid === user.uid ||
      blockedUserIds.includes(targetUid) ||
      blockedByUserIds.includes(targetUid)
    ) {
      return;
    }

    const existingRequestQuery = query(
      collection(db, "friendRequests"),
      where("fromUid", "==", user.uid),
      where("toUid", "==", targetUid),
      where("status", "==", "pending"),
      limit(1),
    );

    const existingRequestSnap = await getDocs(existingRequestQuery);

    if (!existingRequestSnap.empty) return;

    const requestRef = await addDoc(collection(db, "friendRequests"), {
      fromUid: user.uid,
      toUid: targetUid,
      status: "pending",
      createdAt: serverTimestamp(),
    });

    try {
      const actorName =
        String(
          profile.name ||
            user.displayName ||
            user.email?.split("@")[0] ||
            "BioIndex User",
        ).trim() || "BioIndex User";

      const actorPhotoURL = isBioIndexProfilePhoto(profile.photoURL)
        ? profile.photoURL
        : "";

      await createUserNotification(targetUid, {
        type: "friend_request",

        message: `${actorName} sent you a friend request`,
        detail: "View the request from your BioIndex profile.",
        targetRoute: `/profile/${user.uid}`,
        read: false,

        actorUid: user.uid,
        actorName,
        actorPhotoURL,

        topicId: "",
        commentId: "",
        parentCommentId: "",

        titleId: "",
        genusName: "",

        friendRequestId: requestRef.id,

        dedupeKey: `friend_request_${requestRef.id}`,
      });
    } catch (notificationError) {
      console.error(
        "Friend request sent, but notification creation failed:",
        notificationError,
      );
    }
  }

  async function deleteProfile() {
    const user = auth.currentUser;
    if (!user || deletingProfile) return;

    const profileName = profile.name || user.displayName || "Unnamed user";
    const confirmed = window.confirm(
      `Permanently delete your entire BioIndex account?\n\n` +
        `Profile: ${profileName}\n\n` +
        `Your photographs, collections and account will be deleted, and ` +
        `related activity in friends' accounts will be removed.\n\n` +
        `This cannot be undone.`,
    );
    if (!confirmed) return;

    setDeletingProfile(true);
    try {
      // A single authenticated backend action performs cross-user cleanup,
      // removes Storage assets, then deletes the Firebase Auth user.
      const functions = getFunctions(undefined, "southamerica-east1");
      const deleteAccount = httpsCallable(functions, "deleteMyBioIndexAccount");
      const result = await deleteAccount({});

      if (!result.data?.deleted) {
        throw new Error("Account deletion did not complete.");
      }

      localStorage.removeItem(profileCacheKey);
      await signOut(auth).catch(() => {});
      navigate("/", { replace: true });
    } catch (error) {
      console.error("BioIndex account deletion failed:", error);
      window.alert(
        String(error?.message || "Account deletion could not be completed.") +
          "\n\nIf deletion began and stopped unexpectedly, " +
          "contact BioIndex support before retrying.",
      );
    } finally {
      setDeletingProfile(false);
    }
  }

  async function unfriend(targetUid) {
    const currentUid = auth.currentUser.uid;

    try {
      // Remove the friendship from both users.
      await Promise.all([
        deleteDoc(doc(db, "users", currentUid, "friends", targetUid)),
        deleteDoc(doc(db, "users", targetUid, "friends", currentUid)),
      ]);

      // Remove any friend requests between the two users in either direction.
      const requestsFromCurrentUserQuery = query(
        collection(db, "friendRequests"),
        where("fromUid", "==", currentUid),
        where("toUid", "==", targetUid),
      );

      const requestsFromTargetUserQuery = query(
        collection(db, "friendRequests"),
        where("fromUid", "==", targetUid),
        where("toUid", "==", currentUid),
      );

      // Remove friendship-acceptance notifications from either user's
      // notification history when the friendship no longer exists.
      const currentUserAcceptanceNotificationsQuery = query(
        collection(db, "users", currentUid, "notifications"),
        where("type", "==", "friend_accepted"),
        where("actorUid", "==", targetUid),
      );

      const targetUserAcceptanceNotificationsQuery = query(
        collection(db, "users", targetUid, "notifications"),
        where("type", "==", "friend_accepted"),
        where("actorUid", "==", currentUid),
      );

      const [
        requestsFromCurrentUserSnap,
        requestsFromTargetUserSnap,
        currentUserAcceptanceNotificationsSnap,
        targetUserAcceptanceNotificationsSnap,
      ] = await Promise.all([
        getDocs(requestsFromCurrentUserQuery),
        getDocs(requestsFromTargetUserQuery),
        getDocs(currentUserAcceptanceNotificationsQuery),
        getDocs(targetUserAcceptanceNotificationsQuery),
      ]);

      const deletes = [];

      requestsFromCurrentUserSnap.forEach((requestDocument) => {
        deletes.push(deleteDoc(requestDocument.ref));
      });

      requestsFromTargetUserSnap.forEach((requestDocument) => {
        deletes.push(deleteDoc(requestDocument.ref));
      });

      currentUserAcceptanceNotificationsSnap.forEach((notificationDocument) => {
        deletes.push(deleteDoc(notificationDocument.ref));
      });

      targetUserAcceptanceNotificationsSnap.forEach((notificationDocument) => {
        deletes.push(deleteDoc(notificationDocument.ref));
      });

      await Promise.all(deletes);
    } catch (err) {
      console.error("Unfriend failed:", err);
    }
  }

  async function blockUser(targetUid) {
    const currentUid = auth.currentUser?.uid;

    if (
      !currentUid ||
      !targetUid ||
      currentUid === targetUid ||
      blockingUserId
    ) {
      return;
    }

    setBlockingUserId(targetUid);

    try {
      await setDoc(
        doc(db, "userBlocks", getUserBlockId(currentUid, targetUid)),
        {
          blockerUid: currentUid,
          blockedUid: targetUid,
          createdAt: serverTimestamp(),
        },
      );

      const [
        requestsFromCurrentUserSnap,
        requestsFromTargetUserSnap,
        notificationsFromTargetSnap,
        communityActivityFromTargetSnap,
      ] = await Promise.all([
        getDocs(
          query(
            collection(db, "friendRequests"),
            where("fromUid", "==", currentUid),
            where("toUid", "==", targetUid),
          ),
        ),
        getDocs(
          query(
            collection(db, "friendRequests"),
            where("fromUid", "==", targetUid),
            where("toUid", "==", currentUid),
          ),
        ),
        getDocs(
          query(
            collection(db, "users", currentUid, "notifications"),
            where("actorUid", "==", targetUid),
          ),
        ),
        getDocs(
          query(
            collection(db, "users", currentUid, "communityFeed"),
            where("actorUid", "==", targetUid),
          ),
        ),
      ]);

      const cleanupOperations = [
        deleteDoc(doc(db, "users", currentUid, "friends", targetUid)),
        deleteDoc(doc(db, "users", targetUid, "friends", currentUid)),
      ];

      requestsFromCurrentUserSnap.forEach((requestDocument) => {
        cleanupOperations.push(deleteDoc(requestDocument.ref));
      });

      requestsFromTargetUserSnap.forEach((requestDocument) => {
        cleanupOperations.push(deleteDoc(requestDocument.ref));
      });

      notificationsFromTargetSnap.forEach((notificationDocument) => {
        cleanupOperations.push(deleteDoc(notificationDocument.ref));
      });

      communityActivityFromTargetSnap.forEach((activityDocument) => {
        cleanupOperations.push(deleteDoc(activityDocument.ref));
      });

      await Promise.allSettled(cleanupOperations);
    } catch (error) {
      console.error("Failed to block user:", error);
      window.alert("This user could not be blocked. Please try again.");
    } finally {
      setBlockingUserId("");
    }
  }

  async function unblockUser(targetUid) {
    const currentUid = auth.currentUser?.uid;

    if (!currentUid || !targetUid || blockingUserId) return;

    setBlockingUserId(targetUid);

    try {
      await deleteDoc(
        doc(db, "userBlocks", getUserBlockId(currentUid, targetUid)),
      );
    } catch (error) {
      console.error("Failed to unblock user:", error);
      window.alert("This user could not be unblocked. Please try again.");
    } finally {
      setBlockingUserId("");
    }
  }

  function openFavoriteSelector(index) {
    setFavSlotIndex(index);
    setSelectedSpeciesForFav(null);
    setFavSelectorOpen(true);
  }

  async function assignFavorite(url) {
    const now = Date.now();

    const updated = [...(profile.favorites || [])];
    updated[favSlotIndex] = url;

    const updatedProfile = {
      ...profile,
      favorites: updated,
      sectionUpdatedAt: {
        ...(profile.sectionUpdatedAt || {}),
        profile: now,
      },
    };

    setProfile(updatedProfile);

    await setDoc(doc(db, "profile", auth.currentUser.uid), updatedProfile, {
      merge: true,
    });

    saveOwnProfileCache({
      profile: updatedProfile,
      favoriteGenera,
      stats,
      speciesWithPhotos,
      quizSpecies,
      collections,
      recentUploads,
      collectionMeta,
      unlockedTitles,
      sectionUpdatedAt: {
        ...(readOwnProfileCache()?.sectionUpdatedAt || {}),
        profile: now,
      },
    });

    setFavSelectorOpen(false);
    setSavedMessage(true);
    setTimeout(() => setSavedMessage(false), 1500);
  }

  function findSpeciesAndFormByUrl(url) {
    const speciesDoc = speciesWithPhotos.find((sp) =>
      (sp.forms || []).some((f) => f.url === url),
    );

    if (!speciesDoc) return { speciesDoc: null, form: null };

    return {
      speciesDoc,
      form: speciesDoc.forms.find((f) => f.url === url) || null,
    };
  }

  function enterPhotoEdit(formUrl) {
    if (!canEditObservationMetadata) return;

    const { speciesDoc, form } = findSpeciesAndFormByUrl(formUrl);
    if (!speciesDoc || !form) return;

    const variant = form.variant || "";
    const notes = form.notes || "";
    const formCollections = Array.isArray(form.collections)
      ? form.collections
      : [];

    setEditingSpecies(speciesDoc);

    setEditingForm({
      ...form,
      date: normalizeDate(form.date),
      variant,
      notes,
      collections: formCollections,
      showAdditionalInfo: false,
      showVariantField:
        variant.trim() !== "" && variant.trim().toLowerCase() !== "na",
      showCollectionsField: formCollections.length > 0,
      showNotesField: notes.trim() !== "",
    });

    setRenamingPhoto(false);
  }

  function exitPhotoEdit() {
    setEditingForm(null);
    setEditingSpecies(null);
  }

  async function savePhotoMetadata() {
    if (!canEditObservationMetadata || !profileUID) return;
    if (!editingSpecies?.id || !editingForm) return;

    const originalForm = editingSpecies.forms.find(
      (form) => form.url === editingForm.url,
    );

    if (!originalForm) return;

    const {
      showAdditionalInfo,
      showVariantField,
      showCollectionsField,
      showNotesField,
      ...storedEditingForm
    } = editingForm;

    const cleanedForm = {
      ...storedEditingForm,
      label: (editingForm.label || "Default").trim(),
      date: {
        year: editingForm?.date?.year || "NA",
        month: editingForm?.date?.month || "NA",
        day: editingForm?.date?.day || "NA",
      },
      location: (editingForm.locationName || editingForm.location || "").trim(),
      locationName: (
        editingForm.locationName ||
        editingForm.location ||
        ""
      ).trim(),
      locationLat: editingForm.locationLat ?? null,
      locationLng: editingForm.locationLng ?? null,
      locationCountry: editingForm.locationCountry || "",
      locationAdminArea: editingForm.locationAdminArea || "",
      locationCity: editingForm.locationCity || "",
      locationPlaceId: editingForm.locationPlaceId || "",
      variant: (editingForm.variant || "").trim(),
      notes: (editingForm.notes || "").trim(),
      notesUpdatedAt:
        (editingForm.notes || "").trim() !== (originalForm.notes || "").trim()
          ? new Date()
          : editingForm.notesUpdatedAt || null,
      collections: Array.isArray(editingForm.collections)
        ? editingForm.collections
        : [],
      isDefault: !!editingForm.isDefault,
    };

    const updatedForms = ensureSingleDefault(
      editingSpecies.forms.map((form) =>
        form.url === cleanedForm.url ? cleanedForm : form,
      ),
    );

    const updatedSpecies = {
      ...editingSpecies,
      forms: updatedForms,
    };

    const speciesRef = doc(
      db,
      "users",
      profileUID,
      "species",
      editingSpecies.id,
    );

    const profileRef = doc(db, "profile", profileUID);
    const now = Date.now();

    const updatedSectionMarkers = {
      ...(profile?.sectionUpdatedAt || {}),
      taxonomy: now,
      collections: now,
      quiz: now,
    };

    if (isAdminManagingProfile) {
      const adminUid = auth.currentUser?.uid;

      if (!adminUid) {
        throw new Error("The administrator is not authenticated.");
      }

      const adminActionRef = doc(collection(db, "adminActions"));
      const batch = writeBatch(db);

      batch.update(speciesRef, {
        forms: updatedForms,
      });

      batch.set(
        profileRef,
        {
          sectionUpdatedAt: updatedSectionMarkers,
        },
        {
          merge: true,
        },
      );

      batch.set(adminActionRef, {
        adminUid,
        targetUid: profileUID,
        targetName: String(profile?.name || "BioIndex user"),
        action: "update_observation_metadata",
        resourceType: "speciesObservation",
        resourcePath: `users/${profileUID}/species/${editingSpecies.id}`,
        reason: "Administrative observation assistance",
        before: observationAuditSnapshot(editingSpecies, originalForm),
        after: observationAuditSnapshot(updatedSpecies, cleanedForm),
        createdAt: serverTimestamp(),
      });

      await batch.commit();
    } else {
      await updateDoc(speciesRef, {
        forms: updatedForms,
      });

      await setDoc(
        profileRef,
        {
          sectionUpdatedAt: updatedSectionMarkers,
        },
        {
          merge: true,
        },
      );
    }

    setSpeciesWithPhotos((prev) =>
      prev.map((species) =>
        species.id === editingSpecies.id ? updatedSpecies : species,
      ),
    );

    setRecentUploads((prev) =>
      prev.map((item) =>
        item.url === cleanedForm.url ? { ...item, ...cleanedForm } : item,
      ),
    );

    setCollectionPhotos((prev) =>
      prev.map((item) =>
        item.url === cleanedForm.url ? { ...item, ...cleanedForm } : item,
      ),
    );

    setFavViewSpecies((prev) =>
      prev?.id === updatedSpecies.id ? updatedSpecies : prev,
    );

    setFavViewForm((prev) =>
      prev?.url === cleanedForm.url ? cleanedForm : prev,
    );

    setProfile((prev) => ({
      ...prev,
      sectionUpdatedAt: updatedSectionMarkers,
    }));

    exitPhotoEdit();
  }

  function enterPhotoRename(formUrl) {
    if (!canReclassifyObservations) return;

    const { speciesDoc } = findSpeciesAndFormByUrl(formUrl);
    if (!speciesDoc) return;

    setEditingSpecies(speciesDoc);
    setRenameValue(speciesDoc.name || "");
    setRenameCommon(speciesDoc.common || "");
    setRenameError(null);
    setRenameSuggestions([]);
    setRenamingPhoto(true);
    setEditingForm(null);
  }

  function exitPhotoRename() {
    setRenamingPhoto(false);
    setRenameValue("");
    setRenameCommon("");
    setRenameSaving(false);
    setRenameError(null);
    setRenameSuggestions([]);
    setEditingSpecies(null);
  }

  function updateRenameSuggestions(query, field) {
    const q = (query || "").toLowerCase().trim();

    if (q.length < 3) {
      setRenameSuggestions([]);
      setRenameActiveIndex(0);
      setRenameActiveField(field);
      return;
    }

    const matches = speciesNames.filter(
      (sp) =>
        sp.scientific.toLowerCase().includes(q) ||
        sp.common.toLowerCase().includes(q),
    );

    setRenameSuggestions(matches);
    setRenameActiveIndex(0);
    setRenameActiveField(field);
  }

  function selectRenameSuggestion(sp) {
    setRenameValue(sp.scientific);
    setRenameCommon(sp.common);
    setRenameSuggestions([]);
  }

  function handleRenameKeyDown(e) {
    if (!renameSuggestions.length) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setRenameActiveIndex((i) =>
        Math.min(i + 1, renameSuggestions.length - 1),
      );
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setRenameActiveIndex((i) => Math.max(i - 1, 0));
    }

    if (e.key === "Enter") {
      e.preventDefault();
      selectRenameSuggestion(renameSuggestions[renameActiveIndex]);
    }
  }

  async function handleRenameProfilePhoto(formUrl) {
    if (!canReclassifyObservations || !profileUID) return;
    if (!editingSpecies?.id || !formUrl) return;

    setRenameSaving(true);
    setRenameError(null);

    const parts = renameValue.trim().split(/\s+/);

    if (parts.length < 2) {
      setRenameError("Scientific name must include genus and species.");
      setRenameSaving(false);
      return;
    }

    const genus =
      parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();

    const species = parts[1].toLowerCase();
    const normalizedName = `${genus} ${species}`;
    const newDocId = normalizedName.trim().toLowerCase();
    const normalizedCommonName = (renameCommon || "").trim();

    if (isAdminManagingProfile) {
      const confirmed = window.confirm(
        `Reclassify this observation from ${
          editingSpecies.name || editingSpecies.id
        } to ${normalizedName}?`,
      );

      if (!confirmed) {
        setRenameSaving(false);
        return;
      }
    }

    try {
      /*
       * Administrative reclassification is performed on the server because
       * it can create, update and delete several target-user documents.
       */
      if (isAdminManagingProfile) {
        const functions = getFunctions(undefined, "southamerica-east1");

        const adminReclassifyObservation = httpsCallable(
          functions,
          "adminReclassifyObservation",
        );

        await adminReclassifyObservation({
          targetUid: profileUID,
          sourceSpeciesId: editingSpecies.id,
          formUrl,
          scientificName: normalizedName,
          commonName: normalizedCommonName,
        });

        /*
         * Close the active observation interfaces and refresh the managed
         * profile data in place. This rebuilds taxonomy, collections, recent
         * uploads and quiz data without reloading the website.
         */
        exitPhotoRename();

        setFavViewOpen(false);
        setFavViewSpecies(null);
        setFavViewForm(null);
        setFavViewFullscreen(false);

        setCollectionViewOpen(false);
        setCollectionFullscreen(false);
        setCollectionPhotos([]);
        setCollectionIndex(0);

        backgroundProfileRefreshRef.current = true;
        setProfileRefreshKey((currentKey) => currentKey + 1);

        return;
      }

      /*
       * Preserve the existing owner workflow exactly as before.
       */
      const currentUid = auth.currentUser?.uid;

      if (!currentUid) {
        throw new Error("The profile owner is not authenticated.");
      }

      const oldRef = doc(db, "users", currentUid, "species", editingSpecies.id);

      const oldSnap = await getDoc(oldRef);

      if (!oldSnap.exists()) {
        throw new Error("Original species not found.");
      }

      const oldData = oldSnap.data();
      const oldForms = Array.isArray(oldData.forms) ? oldData.forms : [];

      const formsToMove = oldForms.filter((form) => form.url === formUrl);

      const formsToKeep = oldForms.filter((form) => form.url !== formUrl);

      if (!formsToMove.length) {
        throw new Error("Photo not found.");
      }

      if (newDocId === editingSpecies.id) {
        await setDoc(
          oldRef,
          {
            name: normalizedName,
            genus,
            common: normalizedCommonName,
          },
          {
            merge: true,
          },
        );
      } else {
        const newRef = doc(db, "users", currentUid, "species", newDocId);

        const newSnap = await getDoc(newRef);

        if (newSnap.exists()) {
          const existingData = newSnap.data();

          await updateDoc(newRef, {
            name: normalizedName,
            genus,
            common: normalizedCommonName,
            forms: ensureSingleDefault([
              ...(existingData.forms || []),
              ...formsToMove,
            ]),
          });
        } else {
          await setDoc(newRef, {
            name: normalizedName,
            genus,
            common: normalizedCommonName,
            owner: currentUid,
            createdAt: oldData.createdAt || new Date(),
            forms: ensureSingleDefault(formsToMove),
          });
        }

        if (formsToKeep.length) {
          await updateDoc(oldRef, {
            forms: ensureSingleDefault(formsToKeep),
          });
        } else {
          await deleteDoc(oldRef);
        }
      }

      const now = Date.now();

      await setDoc(
        doc(db, "profile", currentUid),
        {
          sectionUpdatedAt: {
            taxonomy: now,
            collections: now,
            quiz: now,
          },
        },
        {
          merge: true,
        },
      );

      exitPhotoRename();
      setFavViewOpen(false);
      setCollectionViewOpen(false);
    } catch (error) {
      console.error(error);

      setRenameError(
        isAdminManagingProfile
          ? "The observation could not be reclassified."
          : "Failed to rename photo.",
      );
    } finally {
      setRenameSaving(false);
    }
  }

  async function deleteRecentUploadPhoto(formUrl) {
    if (!isOwnProfile || deletingOwnObservation || !profileUID) return;
    if (auth.currentUser?.uid !== profileUID) return;

    const { speciesDoc, form } = findSpeciesAndFormByUrl(formUrl);
    if (!speciesDoc?.id || !form?.url) {
      window.alert("The selected photograph could not be found.");
      return;
    }

    const isLastPhoto = (speciesDoc.forms || []).length === 1;
    const confirmed = window.confirm(
      isLastPhoto
        ? "This is the only photo for this species.\n\nDeleting it will permanently delete the entire species.\n\nAre you sure?"
        : "Are you sure you want to delete this photo?",
    );
    if (!confirmed) return;

    setDeletingOwnObservation(true);
    try {
      const functions = getFunctions(undefined, "southamerica-east1");
      const deleteOwnObservation = httpsCallable(
        functions,
        "deleteOwnObservation",
      );
      await deleteOwnObservation({
        speciesId: speciesDoc.id,
        formUrl: form.url,
      });

      // Close the viewer and clear its edit state before refreshing profile data.
      setEditingForm(null);
      setEditingSpecies(null);
      setRenamingPhoto(false);
      setFavViewOpen(false);
      setFavViewSpecies(null);
      setFavViewForm(null);
      setFavViewFullscreen(false);
      setFavCollectionsPopoverOpen(false);
      setRecentUploadViewerIndex(null);

      setRecentUploads((previous) =>
        previous.filter((photo) => photo.url !== form.url),
      );
      setSpeciesWithPhotos((previous) =>
        previous
          .map((species) =>
            species.id === speciesDoc.id
              ? {
                  ...species,
                  forms: (species.forms || []).filter(
                    (item) => item.url !== form.url,
                  ),
                }
              : species,
          )
          .filter((species) => (species.forms || []).length > 0),
      );

      // Refresh stats, collections, coverage, achievements and cached uploads.
      backgroundProfileRefreshRef.current = true;
      setProfileRefreshKey((key) => key + 1);
    } catch (error) {
      console.error("Recent upload photo deletion failed:", error);
      window.alert(
        error?.message ||
          "The photograph could not be deleted. Please try again.",
      );
    } finally {
      setDeletingOwnObservation(false);
    }
  }

  function openAdminDeleteObservation(formUrl) {
    if (!canDeleteObservations || deletingObservation) return;

    const { speciesDoc, form } = findSpeciesAndFormByUrl(formUrl);

    if (!speciesDoc || !form) {
      window.alert("The selected observation could not be found.");
      return;
    }

    setAdminDeleteTarget({
      speciesId: speciesDoc.id,
      scientificName: speciesDoc.name || speciesDoc.id,
      commonName: speciesDoc.common || "",
      formId: form.id || "",
      formUrl: form.url,
      formLabel: form.label || "Default",
      storagePath: form.storagePath || "",
      isDefault: !!form.isDefault,
      photoCount: Array.isArray(speciesDoc.forms) ? speciesDoc.forms.length : 0,
    });

    setAdminDeleteReason("");
    setAdminDeleteError("");
  }

  function closeAdminDeleteObservation() {
    if (deletingObservation) return;

    setAdminDeleteTarget(null);
    setAdminDeleteReason("");
    setAdminDeleteError("");
  }

  async function confirmAdminDeleteObservation() {
    if (
      !canDeleteObservations ||
      !profileUID ||
      !adminDeleteTarget ||
      deletingObservation
    ) {
      return;
    }

    const reason = adminDeleteReason.trim();

    if (reason.length < 10) {
      setAdminDeleteError(
        "Please provide a moderation reason of at least 10 characters.",
      );
      return;
    }

    if (reason.length > 500) {
      setAdminDeleteError(
        "The moderation reason cannot exceed 500 characters.",
      );
      return;
    }

    setDeletingObservation(true);
    setAdminDeleteError("");

    try {
      const functions = getFunctions(undefined, "southamerica-east1");

      const adminDeleteObservation = httpsCallable(
        functions,
        "adminDeleteObservation",
      );

      await adminDeleteObservation({
        targetUid: profileUID,
        speciesId: adminDeleteTarget.speciesId,
        formUrl: adminDeleteTarget.formUrl,
        reason,
      });

      setAdminDeleteTarget(null);
      setAdminDeleteReason("");
      setAdminDeleteError("");

      setEditingForm(null);
      setEditingSpecies(null);
      setRenamingPhoto(false);

      setFavViewOpen(false);
      setFavViewSpecies(null);
      setFavViewForm(null);
      setFavViewFullscreen(false);
      setFavCollectionsPopoverOpen(false);

      setCollectionViewOpen(false);
      setCollectionFullscreen(false);
      setCollectionPhotos([]);
      setCollectionIndex(0);

      /*
       * Preserve the current page and Admin Profile Mode while rebuilding the
       * affected profile sections in the background.
       */
      backgroundProfileRefreshRef.current = true;
      setProfileRefreshKey((currentKey) => currentKey + 1);
    } catch (error) {
      console.error("Administrator observation deletion failed:", error);

      setAdminDeleteError(
        error?.message ||
          "The observation could not be deleted. Please try again.",
      );
    } finally {
      setDeletingObservation(false);
    }
  }

  function createEmptyTaxa() {
    return {
      species: new Set(),
      genus: new Set(),
      family: new Set(),
      order: new Set(),
      class: new Set(),
      phylum: new Set(),
      kingdom: new Set(),
    };
  }

  function serializeTaxa(taxa) {
    return Object.fromEntries(
      Object.entries(taxa || {}).map(([rank, values]) => [
        rank,
        Array.from(values || []),
      ]),
    );
  }

  function deserializeTaxa(taxaValues) {
    const taxa = createEmptyTaxa();

    Object.keys(taxa).forEach((rank) => {
      taxa[rank] = new Set(
        Array.isArray(taxaValues?.[rank]) ? taxaValues[rank] : [],
      );
    });

    return taxa;
  }

  function buildTaxaFromSpeciesList(speciesList) {
    const taxa = createEmptyTaxa();

    (speciesList || []).forEach((species) => {
      const genus =
        species?.genus ||
        String(species?.name || "")
          .trim()
          .split(/\s+/)[0];
      if (!genus) return;

      const taxonomy = resolveTaxonomy(genus);

      if (species?.name) taxa.species.add(species.name);
      if (taxonomy.genus) taxa.genus.add(taxonomy.genus);
      if (taxonomy.family) taxa.family.add(taxonomy.family);
      if (taxonomy.order) taxa.order.add(taxonomy.order);
      if (taxonomy.className) taxa.class.add(taxonomy.className);
      if (taxonomy.phylum) taxa.phylum.add(taxonomy.phylum);
      if (taxonomy.kingdom) taxa.kingdom.add(taxonomy.kingdom);
    });

    return taxa;
  }

  function resolveTaxonomy(genus) {
    const normalizedGenus = String(genus || "")
      .toLowerCase()
      .trim();
    const family = FAMILY_BY_GENUS.get(normalizedGenus);
    const order = ORDER_BY_FAMILY.get(family);
    const className = CLASS_BY_ORDER.get(order);
    const phylum = PHYLUM_BY_CLASS.get(className);
    const kingdom = KINGDOM_BY_PHYLUM.get(phylum);

    return {
      genus: normalizedGenus,
      family,
      order,
      className,
      phylum,
      kingdom,
    };
  }

  const taxonCounts = useMemo(() => {
    const counts = {
      genus: {},
      family: {},
      order: {},
      class: {},
      phylum: {},
      kingdom: {},
    };

    quizSpecies.forEach((sp) => {
      const t = resolveTaxonomy(sp.genus);
      if (!t) return;

      const g = String(sp.genus).toLowerCase().trim();
      counts.genus[g] = (counts.genus[g] || 0) + 1;
      if (t.family)
        counts.family[t.family] = (counts.family[t.family] || 0) + 1;
      if (t.order) counts.order[t.order] = (counts.order[t.order] || 0) + 1;
      if (t.className)
        counts.class[t.className] = (counts.class[t.className] || 0) + 1;
      if (t.phylum)
        counts.phylum[t.phylum] = (counts.phylum[t.phylum] || 0) + 1;
      if (t.kingdom)
        counts.kingdom[t.kingdom] = (counts.kingdom[t.kingdom] || 0) + 1;
    });

    return counts;
  }, [quizSpecies]);

  const options = useMemo(() => {
    if (!filterType) return [];

    if (filterType === "friend-collection") return [];

    // 🔥 TAXON FILTERS → ONLY WHAT USER HAS
    if (filterType !== "collection") {
      const bucket = taxonCounts[filterType] || {};
      return Object.keys(bucket);
    }

    // 📁 COLLECTIONS (unchanged)
    if (filterType === "collection") {
      return Object.keys(collections);
    }

    return [];
  }, [filterType, collections, taxonCounts]);

  const filteredOptions = useMemo(() => {
    if (!filterType) return options;

    return options.filter((opt) => {
      const key = String(opt).toLowerCase().trim();
      const bucket = taxonCounts[filterType] || {};
      const count = bucket[key] || bucket[opt] || 0;
      return count >= 4 || Object.keys(bucket).length <= 5;
    });
  }, [options, filterType, taxonCounts]);

  const availableSpecies = useMemo(() => {
    if (filterType === "friend-collection") {
      if (!quizFriendUID || !filterValue) return [];

      return friendQuizCollections[quizFriendUID]?.[filterValue] || [];
    }

    if (!filterType || !filterValue) return quizSpecies;

    const target = String(filterValue).toLowerCase().trim();

    return quizSpecies.filter((sp) => {
      // COLLECTION FILTER
      if (filterType === "collection") {
        const speciesWithForms = speciesWithPhotos.find(
          (s) => s.name === sp.name,
        );

        if (!speciesWithForms) return false;

        return speciesWithForms.forms.some((f) =>
          (f.collections || []).includes(filterValue),
        );
      }

      // TAXON FILTER
      const t = resolveTaxonomy(sp.genus);

      const taxonomyKey = filterType === "class" ? "className" : filterType;

      return (
        String(t[taxonomyKey] || "")
          .toLowerCase()
          .trim() === target
      );
    });
  }, [
    friendQuizCollections,
    quizFriendUID,
    quizSpecies,
    filterType,
    filterValue,
    speciesWithPhotos,
  ]);

  function startQuiz() {
    if (
      filterType === "friend-collection" &&
      (!quizFriendUID ||
        !filterValue ||
        !visibleFriends.includes(quizFriendUID))
    ) {
      return;
    }

    if (filterType && !filterValue) return;

    const filters = filterType
      ? filterType === "friend-collection"
        ? { collection: filterValue }
        : { [filterType]: filterValue }
      : null;

    const sourceUID =
      filterType === "friend-collection" ? quizFriendUID : profileUID;

    navigate("/quiz/play", {
      state: {
        quizType,
        filters,
        questionCount: Math.min(questionCount, availableSpecies.length),
        sourceUID,
      },
    });
  }

  async function deleteCollection(collectionToDelete) {
    if (!isOwnProfile || !collectionToDelete || !auth.currentUser) {
      return;
    }

    const confirmed = window.confirm(
      `Delete the collection "${collectionToDelete}"?\n\nThe photos will not be deleted. They will only be removed from this collection. This cannot be undone.`,
    );

    if (!confirmed) return;

    const speciesUpdates = speciesWithPhotos
      .map((sp) => {
        let changed = false;

        const updatedForms = (sp.forms || []).map((form) => {
          const currentCollections = Array.isArray(form.collections)
            ? form.collections
            : [];

          const belongsToCollection =
            currentCollections.includes(collectionToDelete) ||
            Object.prototype.hasOwnProperty.call(
              form.collectionAddedAt || {},
              collectionToDelete,
            );

          if (!belongsToCollection) return form;

          changed = true;

          const updatedCollectionAddedAt = Object.fromEntries(
            Object.entries(form.collectionAddedAt || {}).filter(
              ([collectionName]) => collectionName !== collectionToDelete,
            ),
          );

          return {
            ...form,
            collections: currentCollections.filter(
              (collectionName) => collectionName !== collectionToDelete,
            ),
            collectionAddedAt: updatedCollectionAddedAt,
          };
        });

        return changed
          ? {
              species: sp,
              forms: updatedForms,
            }
          : null;
      })
      .filter(Boolean);

    await Promise.all(
      speciesUpdates.map(({ species, forms }) =>
        updateDoc(
          doc(db, "users", auth.currentUser.uid, "species", species.id),
          { forms },
        ),
      ),
    );

    await deleteDoc(
      doc(
        db,
        "users",
        auth.currentUser.uid,
        "collectionsMeta",
        collectionToDelete,
      ),
    );

    const updatedSpeciesWithPhotos = speciesWithPhotos.map((sp) => {
      const update = speciesUpdates.find(({ species }) => species.id === sp.id);

      return update ? { ...sp, forms: update.forms } : sp;
    });

    const updatedCollections = { ...collections };
    delete updatedCollections[collectionToDelete];

    const updatedCollectionMeta = { ...collectionMeta };
    delete updatedCollectionMeta[collectionToDelete];

    const updatedRecentUploads = recentUploads.map((item) => {
      const currentCollections = Array.isArray(item.collections)
        ? item.collections
        : [];

      const updatedCollectionAddedAt = Object.fromEntries(
        Object.entries(item.collectionAddedAt || {}).filter(
          ([collectionName]) => collectionName !== collectionToDelete,
        ),
      );

      return {
        ...item,
        collections: currentCollections.filter(
          (collectionName) => collectionName !== collectionToDelete,
        ),
        collectionAddedAt: updatedCollectionAddedAt,
      };
    });

    setSpeciesWithPhotos(updatedSpeciesWithPhotos);
    setCollections(updatedCollections);
    setCollectionMeta(updatedCollectionMeta);
    setRecentUploads(updatedRecentUploads);
    setCollectionPhotos([]);
    setCollectionIndex(0);
    setCollectionName("");
    setCollectionViewOpen(false);
    setCollectionFullscreen(false);

    const now = Date.now();

    await setDoc(
      doc(db, "profile", auth.currentUser.uid),
      {
        sectionUpdatedAt: {
          taxonomy: now,
          collections: now,
          quiz: now,
        },
      },
      { merge: true },
    );

    saveOwnProfileCache({
      profile,
      stats,
      favoriteGenera,
      speciesWithPhotos: updatedSpeciesWithPhotos,
      quizSpecies,
      collections: updatedCollections,
      recentUploads: updatedRecentUploads,
      collectionMeta: updatedCollectionMeta,
      unlockedTitles,
      sectionUpdatedAt: {
        ...(readOwnProfileCache()?.sectionUpdatedAt || {}),
        taxonomy: now,
        collections: now,
        quiz: now,
      },
    });
  }

  async function manageAdminCollection(actionData) {
    if (!canManageCollections || !profileUID || !actionData?.action) {
      throw new Error("Administrator collection management is unavailable.");
    }

    const functions = getFunctions(undefined, "southamerica-east1");

    const adminManageCollectionCallable = httpsCallable(
      functions,
      "adminManageCollection",
    );

    const result = await adminManageCollectionCallable({
      targetUid: profileUID,
      ...actionData,
    });

    /*
     * Rebuild the managed user's collection and observation state without
     * replacing the current page with the full-profile loading screen.
     */
    backgroundProfileRefreshRef.current = true;
    setProfileRefreshKey((currentKey) => currentKey + 1);

    return result.data;
  }

  async function saveCollectionOrder(orderedPhotos) {
    if (!isOwnProfile || !collectionName || !auth.currentUser) return;

    const orderBase = Date.now();

    const orderByUrl = new Map(
      orderedPhotos.map((photo, index) => [photo.url, orderBase + index]),
    );

    const speciesUpdates = speciesWithPhotos
      .map((sp) => {
        let changed = false;

        const forms = (sp.forms || []).map((form) => {
          const orderValue = orderByUrl.get(form.url);

          if (!orderValue) return form;

          changed = true;

          return {
            ...form,
            collectionAddedAt: {
              ...(form.collectionAddedAt || {}),
              [collectionName]: orderValue,
            },
          };
        });

        return changed ? { sp, forms } : null;
      })
      .filter(Boolean);

    await Promise.all(
      speciesUpdates.map(({ sp, forms }) =>
        updateDoc(doc(db, "users", auth.currentUser.uid, "species", sp.id), {
          forms,
        }),
      ),
    );

    const updatedSpeciesWithPhotos = speciesWithPhotos.map((sp) => {
      const updated = speciesUpdates.find(({ sp: changedSp }) => {
        return changedSp.id === sp.id;
      });

      return updated ? { ...sp, forms: updated.forms } : sp;
    });

    const updatedCollectionPhotos = orderedPhotos.map((photo, index) => ({
      ...photo,
      collectionOrder: orderBase + index,
      collectionAddedAt: {
        ...(photo.collectionAddedAt || {}),
        [collectionName]: orderBase + index,
      },
    }));

    const updatedCollections = {
      ...collections,
      [collectionName]: updatedCollectionPhotos,
    };

    setSpeciesWithPhotos(updatedSpeciesWithPhotos);
    setCollections(updatedCollections);
    setCollectionPhotos(updatedCollectionPhotos);

    const now = Date.now();

    await setDoc(
      doc(db, "profile", auth.currentUser.uid),
      {
        sectionUpdatedAt: {
          taxonomy: now,
          collections: now,
          quiz: now,
        },
      },
      { merge: true },
    );

    saveOwnProfileCache({
      profile,
      stats,
      favoriteGenera,
      speciesWithPhotos: updatedSpeciesWithPhotos,
      quizSpecies,
      collections: updatedCollections,
      recentUploads,
      collectionMeta,
      unlockedTitles,
      sectionUpdatedAt: {
        ...(readOwnProfileCache()?.sectionUpdatedAt || {}),
        taxonomy: now,
        collections: now,
        quiz: now,
      },
    });
  }

  function openFavoriteViewer(url) {
    if (!url) return;

    const sp = speciesWithPhotos.find((s) =>
      (s.forms || []).some((f) => f.url === url),
    );

    if (!sp) return;

    const form =
      (sp.forms || []).find((f) => f.url === url) || sp.forms?.[0] || null;

    setRecentUploadViewerIndex(null);
    setFavViewSpecies(sp);
    setFavViewForm(form);
    setFavViewImageLoaded(false);
    setFavViewFullscreen(false);
    setFavCollectionsPopoverOpen(false);
    setFavViewOpen(true);
  }

  function openRecentUploadViewer(index) {
    if (index < 0 || index >= recentUploads.length) return;

    const upload = recentUploads[index];
    const species = speciesWithPhotos.find(
      (candidate) => candidate.id === upload.speciesId,
    );

    if (!species) return;

    const form =
      (species.forms || []).find((candidate) => candidate.url === upload.url) ||
      upload;

    setRecentUploadViewerIndex(index);
    setFavViewSpecies(species);
    setFavViewForm(form);
    setFavViewImageLoaded(false);
    setFavViewFullscreen(false);
    setFavCollectionsPopoverOpen(false);
    setFavViewOpen(true);
  }

  function StatLine({ label, data, value, onClick }) {
    const count = value !== undefined ? value : Object.keys(data || {}).length;

    if (!count) return null;

    return (
      <div
        onClick={onClick}
        className="flex justify-between text-sm cursor-pointer hover:bg-slate-50 px-2 py-1 rounded-lg transition"
      >
        <span className="text-slate-600">{label}</span>
        <span className="font-medium">{count}</span>
      </div>
    );
  }

  function getTaxonItems(type) {
    if (type === "species") {
      return speciesWithPhotos.map((s) => ({
        scientific: s.name,
        common: s.common || "",
        genus: s.name.split(" ")[0],
      }));
    }

    return Object.keys(stats.counts[type] || {}).map(
      (x) => x.charAt(0).toUpperCase() + x.slice(1),
    );
  }

  function cleanTaxon(value) {
    return String(value || "")
      .toLowerCase()
      .trim();
  }

  function getKingdomForPhylum(phylum) {
    const target = cleanTaxon(phylum);

    return cleanTaxon(
      Object.keys(phylaByKingdom).find((kingdom) =>
        (phylaByKingdom[kingdom] || []).some((p) => cleanTaxon(p) === target),
      ),
    );
  }

  function getPhylumForClass(className) {
    const target = cleanTaxon(className);

    return cleanTaxon(
      Object.keys(classesByPhylum).find((phylum) =>
        (classesByPhylum[phylum] || []).some((c) => cleanTaxon(c) === target),
      ),
    );
  }

  function getClassForOrder(order) {
    const target = cleanTaxon(order);

    return cleanTaxon(
      Object.keys(ordersByClass).find((className) =>
        (ordersByClass[className] || []).some((o) => cleanTaxon(o) === target),
      ),
    );
  }

  function getOrderForFamily(family) {
    const target = cleanTaxon(family);

    return cleanTaxon(
      Object.keys(familiesByOrder).find((order) =>
        (familiesByOrder[order] || []).some((f) => cleanTaxon(f) === target),
      ),
    );
  }

  function getTaxonRoute(type, item) {
    const base = `/explore/${profileUID}`;

    if (type === "species") {
      const genus = cleanTaxon(item.genus || item.scientific?.split(" ")[0]);
      if (!genus) return null;

      const t = resolveTaxonomy(genus);

      if (
        !t.kingdom ||
        !t.phylum ||
        !t.className ||
        !t.order ||
        !t.family ||
        !t.genus
      ) {
        return null;
      }

      return `${base}/kingdom/${t.kingdom}/phylum/${t.phylum}/class/${t.className}/order/${t.order}/family/${t.family}/genus/${t.genus}`;
    }

    const value = cleanTaxon(item);

    if (type === "kingdom") {
      return `${base}/kingdom/${value}`;
    }

    if (type === "phylum") {
      const kingdom = getKingdomForPhylum(value);
      if (!kingdom) return null;

      return `${base}/kingdom/${kingdom}/phylum/${value}`;
    }

    if (type === "class") {
      const phylum = getPhylumForClass(value);
      const kingdom = getKingdomForPhylum(phylum);
      if (!kingdom || !phylum) return null;

      return `${base}/kingdom/${kingdom}/phylum/${phylum}/class/${value}`;
    }

    if (type === "order") {
      const className = getClassForOrder(value);
      const phylum = getPhylumForClass(className);
      const kingdom = getKingdomForPhylum(phylum);
      if (!kingdom || !phylum || !className) return null;

      return `${base}/kingdom/${kingdom}/phylum/${phylum}/class/${className}/order/${value}`;
    }

    if (type === "family") {
      const order = getOrderForFamily(value);
      const className = getClassForOrder(order);
      const phylum = getPhylumForClass(className);
      const kingdom = getKingdomForPhylum(phylum);
      if (!kingdom || !phylum || !className || !order) return null;

      return `${base}/kingdom/${kingdom}/phylum/${phylum}/class/${className}/order/${order}/family/${value}`;
    }

    if (type === "genus") {
      const t = resolveTaxonomy(value);

      if (
        !t.kingdom ||
        !t.phylum ||
        !t.className ||
        !t.order ||
        !t.family ||
        !t.genus
      ) {
        return null;
      }

      return `${base}/kingdom/${t.kingdom}/phylum/${t.phylum}/class/${t.className}/order/${t.order}/family/${t.family}/genus/${t.genus}`;
    }

    return null;
  }

  function openTaxonItem(type, item) {
    const route = getTaxonRoute(type, item);

    if (!route) {
      if (type === "species" || type === "genus") {
        const taxonName =
          type === "species" ? item?.scientific || "This species" : item;

        setTaxonomyNotice(
          `${taxonName} is not currently represented in the BioIndex taxonomy file.`,
        );
      }

      return;
    }

    setTaxonomyNotice("");
    setTaxonViewOpen(false);
    navigate(route);
  }

  function getTaxonBadge(type, value) {
    if (!isOwnProfile && ownTaxa) {
      const normalizedValue =
        type === "species" ? value : String(value || "").toLowerCase();

      const userHasTaxon =
        type === "species"
          ? ownTaxa.species.has(normalizedValue)
          : ownTaxa[type]?.has(normalizedValue);

      const firstName = (profile.name || "This user").split(" ")[0];

      return {
        label: userHasTaxon ? "You both have this" : `${firstName} has this`,
        tone: userHasTaxon ? "shared" : "friendOnly",
      };
    }

    const totalFriends = Object.keys(friendsTaxa).length;
    if (!totalFriends) return null;

    let count = 0;

    Object.values(friendsTaxa).forEach((taxa) => {
      if (!taxa) return;

      if (type === "species") {
        if (taxa.species.has(value)) count++;
      } else {
        if (taxa[type]?.has(String(value || "").toLowerCase())) count++;
      }
    });

    return {
      label: `${Math.round((count / totalFriends) * 100)}% of friends`,
      tone: "percentage",
    };
  }
  if (
    !isOwnProfile &&
    (blockRelationshipsLoading || waitingForAdminModeCheck)
  ) {
    return (
      <div className="min-h-screen bg-[#374049] px-3 py-8 sm:px-8">
        <div className="mx-auto flex min-h-80 max-w-2xl items-center justify-center rounded-3xl border border-[#93CA87]/40 bg-white shadow-xl">
          <div className="text-center">
            <div className="mx-auto h-9 w-9 animate-spin rounded-full border-4 border-[#93CA87]/40 border-t-[#24C3BE]" />

            <p className="mt-3 text-sm text-slate-500">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (profileInteractionBlocked) {
    return (
      <div className="min-h-screen bg-[#374049] px-3 py-8 sm:px-8">
        <div className="mx-auto max-w-2xl rounded-3xl border border-[#93CA87]/40 bg-white p-8 text-center shadow-xl">
          <h1
            className="text-4xl text-[#374049]"
            style={{ fontFamily: "Cormorant Garamond" }}
          >
            Profile Unavailable
          </h1>

          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-500">
            You cannot view or interact with this profile because there is a
            block between these accounts.
          </p>

          <Link
            to="/profile"
            className="mt-6 inline-flex rounded-xl bg-[#24C3BE] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1faeaa]"
          >
            Return to My Profile
          </Link>
        </div>
      </div>
    );
  }

  if (favSelectorOpen) {
    return (
      <div className="min-h-screen bg-[#374049] px-3 sm:px-8 py-6">
        <div className="max-w-6xl mx-auto">
          <ProfileFavorites
            isOwnProfile={isOwnProfile}
            profile={profile}
            savedMessage={savedMessage}
            openFavoriteViewer={openFavoriteViewer}
            openFavoriteSelector={openFavoriteSelector}
            favSelectorOpen={favSelectorOpen}
            setFavSelectorOpen={setFavSelectorOpen}
            selectedSpeciesForFav={selectedSpeciesForFav}
            setSelectedSpeciesForFav={setSelectedSpeciesForFav}
            speciesWithPhotos={speciesWithPhotos}
            assignFavorite={assignFavorite}
            pickerOnly={true}
          />
        </div>
      </div>
    );
  }

  if (loadingProfile) {
    return (
      <div className="min-h-screen bg-[#374049] px-3 sm:px-8 pb-8">
        <div className="flex justify-center pt-10">
          <div className="bg-white rounded-2xl border border-[#93ca87]/40 shadow-sm w-64 h-64 flex flex-col items-center justify-center text-center">
            <div className="w-6 h-6 border-2 border-slate-400 border-t-transparent rounded-full animate-spin mb-3" />
            <div
              className="text-lg text-slate-700"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              {isOwnProfile
                ? "Loading Profile"
                : `Loading ${profile.name || "Friend"}'s profile`}
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="bioindex-profile-root min-h-screen bg-[#374049] px-3 pb-8 sm:px-8">
      <div className="bioindex-v7-desktop-nav relative z-40 mx-auto w-full max-w-[1360px] pt-3 pb-0">
        <div className="flex w-full items-center gap-3 rounded-[1.6rem] border border-[#93CA87]/35 bg-white px-3 py-3 shadow-[0_16px_40px_rgba(15,36,40,0.18),inset_0_1px_0_rgba(255,255,255,0.72)] sm:px-4 lg:px-6">
          <Link
            to="/"
            aria-label="BioIndex Home"
            className="hidden shrink-0 items-center gap-3 rounded-2xl px-2.5 py-2 text-[#374049] transition hover:bg-[#93CA87]/10 lg:flex"
          >
            <img
              src="/favicon.ico"
              alt=""
              className="h-11 w-11 shrink-0 object-contain drop-shadow-[0_4px_10px_rgba(1,118,122,0.16)]"
            />
            <span className="flex flex-col leading-none">
              <span
                className="text-[29px] text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                BioIndex
              </span>
              <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-[#01767A]/70">
                Profile
              </span>
            </span>
          </Link>
          <span
            className="hidden h-7 w-px bg-[#374049]/10 lg:block"
            aria-hidden="true"
          />
          <nav
            aria-label="BioIndex navigation"
            className="grid min-w-0 flex-1 grid-cols-6 items-center gap-1 rounded-2xl bg-[#374049]/[0.03] p-1.5 sm:flex sm:justify-end sm:bg-transparent sm:p-0"
          >
            <Link
              to="/"
              aria-label="Home"
              className="group flex min-w-0 items-center justify-center rounded-2xl px-1 py-2.5 text-sm font-medium text-[#374049]/80 transition-all duration-200 hover:bg-[#24C3BE]/10 hover:text-[#01767A] hover:shadow-[inset_0_0_0_1px_rgba(36,195,190,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/60 sm:px-3 lg:px-4"
            >
              <img
                src="/HomeIcon.png"
                alt=""
                aria-hidden="true"
                className="h-7 w-7 object-contain sm:hidden"
              />

              <span className="hidden sm:inline">Home</span>
            </Link>

            <Link
              to={profileExplorePath}
              aria-label="Explore Taxonomy"
              className="group flex min-w-0 items-center justify-center rounded-2xl px-1 py-2.5 text-sm font-medium text-[#374049]/80 transition-all duration-200 hover:bg-[#24C3BE]/10 hover:text-[#01767A] hover:shadow-[inset_0_0_0_1px_rgba(36,195,190,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/60 sm:px-3 lg:px-4"
            >
              <img
                src="/ExploreIcon.png"
                alt=""
                aria-hidden="true"
                className="h-7 w-7 object-contain sm:hidden"
              />

              <span className="hidden sm:inline">Explore Taxonomy</span>
            </Link>

            <Link
              to="/upload"
              aria-label="Add Photos"
              className="group flex min-w-0 items-center justify-center rounded-2xl px-1 py-2.5 text-sm font-medium text-[#374049]/80 transition-all duration-200 hover:bg-[#24C3BE]/10 hover:text-[#01767A] hover:shadow-[inset_0_0_0_1px_rgba(36,195,190,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/60 sm:px-3 lg:px-4"
            >
              <img
                src="/PhotosIcon.png"
                alt=""
                aria-hidden="true"
                className="h-7 w-7 object-contain sm:hidden"
              />

              <span className="hidden sm:inline">Add Photos</span>
            </Link>

            <div
              aria-label="Profile"
              aria-current="page"
              className="relative flex min-w-0 cursor-default items-center justify-center rounded-2xl bg-[#24C3BE]/15 px-2 py-2.5 text-sm font-semibold text-[#01767A] ring-1 ring-[#24C3BE]/25 sm:bg-[#24C3BE] sm:px-5 sm:text-white sm:shadow-[0_8px_18px_rgba(1,118,122,0.18)] sm:ring-0"
            >
              <img
                src="/ProfileIcon.png"
                alt=""
                aria-hidden="true"
                className="h-7 w-7 object-contain sm:hidden"
              />

              <span className="hidden sm:inline">Profile</span>
            </div>

            <Link
              to="/forum"
              aria-label="Community"
              className="group relative flex min-w-0 items-center justify-center rounded-2xl px-1 py-2.5 text-sm font-medium text-[#374049]/80 transition-all duration-200 hover:bg-[#24C3BE]/10 hover:text-[#01767A] hover:shadow-[inset_0_0_0_1px_rgba(36,195,190,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/60 sm:px-3 lg:px-4"
            >
              <img
                src="/CommIcon.png"
                alt=""
                aria-hidden="true"
                className="h-7 w-7 object-contain sm:hidden"
              />

              <span className="hidden sm:inline">Community</span>
              <CommunityNavBadge />
            </Link>

            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label="Search"
              className="group flex min-w-0 items-center justify-center rounded-2xl px-1 py-2.5 text-sm font-medium text-[#374049]/80 transition-all duration-200 hover:bg-[#24C3BE]/10 hover:text-[#01767A] hover:shadow-[inset_0_0_0_1px_rgba(36,195,190,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#24C3BE]/60 sm:px-3 lg:px-4"
            >
              <img
                src="/Search.png"
                alt=""
                aria-hidden="true"
                className="h-7 w-7 object-contain sm:hidden"
              />

              <span className="hidden sm:inline">Search</span>
            </button>
          </nav>
        </div>
      </div>

      {isAdminManagingProfile && (
        <div className="mx-auto mb-4 w-full max-w-[1640px]">
          <div className="flex flex-col gap-3 rounded-xl border border-[#24C3BE]/45 bg-[#FCFFF6] px-3 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:px-4">
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#374049] text-white">
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M12 3 19 6v5c0 4.8-2.8 8.2-7 10-4.2-1.8-7-5.2-7-10V6l7-3Z" />
                </svg>
              </div>

              <div className="min-w-0">
                <p className="truncate text-xs font-semibold text-[#374049] sm:text-sm">
                  <span className="mr-1.5 uppercase tracking-[0.14em] text-[#01767A]">
                    Admin mode
                  </span>
                  · {profile.name || "BioIndex user"}
                </p>
                <p className="mt-0.5 text-[10px] text-[#374049]/50">
                  Administrative actions are recorded.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 sm:justify-end">
              <span
                className={`rounded-full border px-2 py-1 text-[10px] font-semibold ${
                  isProfileSuspended
                    ? "border-red-200 bg-red-50 text-red-600"
                    : "border-[#93CA87]/50 bg-white text-[#01767A]"
                }`}
              >
                {isProfileSuspended ? "Suspended" : "Active"}
              </span>

              <Link
                to={`/explore/${profileUID}?adminMode=true`}
                className="rounded-lg border border-[#24C3BE]/55 bg-white px-3 py-1.5 text-center text-[11px] font-semibold text-[#01767A] transition hover:bg-[#24C3BE]/10"
              >
                Explore
              </Link>

              <button
                type="button"
                onClick={() =>
                  openAccountStatusAction(
                    isProfileSuspended ? "restore" : "suspend",
                  )
                }
                disabled={accountStatusSaving}
                className={`rounded-lg border bg-white px-3 py-1.5 text-[11px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                  isProfileSuspended
                    ? "border-[#24C3BE]/55 text-[#01767A] hover:bg-[#24C3BE]/10"
                    : "border-red-200 text-red-500 hover:bg-red-50"
                }`}
              >
                {isProfileSuspended ? "Restore" : "Suspend"}
              </button>

              <button
                type="button"
                onClick={openAdminDeleteAccount}
                disabled={adminAccountDeleteSaving}
                className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-red-400 transition hover:border-red-300 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Delete
              </button>

              <button
                type="button"
                onClick={() =>
                  navigate(`/profile/${profileUID}`, {
                    replace: true,
                  })
                }
                className="rounded-lg border border-[#374049]/25 bg-white px-3 py-1.5 text-[11px] font-semibold text-[#374049]/70 transition hover:bg-[#374049]/5 hover:text-[#374049]"
              >
                Exit
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bioindex-profile-content flex justify-center">
        <div className="bioindex-profile-stack w-full space-y-10">
          {/* Desktop: Profile + Social. Mobile ordering is controlled in BioIndexProfile.css. */}
          <div
            ref={profileEditorDialogRef}
            className={`bioindex-profile-overview${isEditing ? " bioindex-profile-overview--editing" : ""}${isEditing && (profileEditorFocus === "photo" || profileEditorFocus === "cover") ? ` bioindex-profile-overview--focus-${profileEditorFocus}` : ""}`}
            role={isEditing ? "dialog" : undefined}
            aria-modal={isEditing ? true : undefined}
            aria-labelledby={
              isEditing ? "bioindex-profile-edit-dialog-title" : undefined
            }
            tabIndex={isEditing ? -1 : undefined}
          >
            {isEditing && (
              <div className="bioindex-profile-edit-dialog-heading">
                <div>
                  <h2 id="bioindex-profile-edit-dialog-title">Edit Profile</h2>
                  <p>
                    See changes in your real profile layout. Drag either image
                    to reposition it.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeProfileEditor}
                  disabled={savingProfile}
                  aria-label="Close profile editor"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
            {/* PROFILE CARD */}
            <div className="bioindex-profile-primary-card bioindex-profile-cover-card relative rounded-[2rem] border border-[#93CA87]/45 bg-white px-5 py-8 shadow-xl sm:px-8 sm:py-10 md:px-10">
              <div className="bioindex-profile-cover-background">
                <img
                  className="bioindex-profile-cover-image"
                  src={
                    (isEditing && profileCoverPreview) ||
                    (isEditing && useDefaultProfileCover
                      ? "/profile-cover-default.jpg"
                      : isBioIndexProfilePhoto(profile.coverPhotoURL)
                        ? profile.coverPhotoURL
                        : "/profile-cover-default.jpg")
                  }
                  alt=""
                  aria-hidden="true"
                  style={profileImageCropStyle(
                    isMobileFraming
                      ? isEditing
                        ? mobileCoverCropDraft
                        : profile.coverCropMobile
                      : isEditing
                        ? coverCropDraft
                        : profile.coverCrop,
                    DEFAULT_COVER_CROP,
                  )}
                />
                <div
                  className="bioindex-profile-cover-image-shade"
                  aria-hidden="true"
                />
                <svg
                  className="bioindex-profile-cover-wave"
                  viewBox="0 0 1200 160"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <path
                    d="M0 104 C170 25 336 86 510 98 C703 117 825 51 1006 64 C1102 71 1167 101 1200 134 L1200 160 L0 160 Z"
                    fill="#fff"
                  />
                  {/* Only the phone layout displays this calm, pale-green wave. */}
                  <path
                    className="bioindex-profile-mobile-isolated-wave"
                    d="M0 129 C250 126 530 128 800 126 C1030 123 1145 127 1200 151 L1200 160 L0 160 Z"
                    fill="#f3f8ef"
                  />
                  {/* Desktop-only wave: slight rise, near-flat middle, final right-edge dip. */}
                  <path
                    className="bioindex-profile-cover-wave-desktop-shape"
                    d="M0 110 C0 110 0 110 0 110 C112 110 223 110 310 110 C520 110 850 110 1060 110 C1116 110 1164 113 1200 160 L0 160 Z"
                    fill="#fff"
                  />
                </svg>
              </div>
              {isOwnProfile && (
                <button
                  type="button"
                  className={`bioindex-profile-cover-image-edit bioindex-profile-cover-owner-trigger${isEditing ? " bioindex-profile-image-framing" : ""}`}
                  onClick={() =>
                    isEditing
                      ? setProfileEditorFocus("cover")
                      : openProfileImageEditor("cover")
                  }
                  onPointerDown={(event) =>
                    isEditing && beginCropDrag(event, "cover")
                  }
                  onPointerMove={(event) =>
                    isEditing && moveCropDrag(event, "cover")
                  }
                  onPointerUp={endCropDrag}
                  onPointerCancel={endCropDrag}
                  aria-label={
                    isEditing
                      ? "Drag to reposition your cover photo"
                      : "Edit your cover photo"
                  }
                  title={
                    isEditing
                      ? "Drag to reposition cover"
                      : "Click to edit cover"
                  }
                />
              )}
              {(isEditing ? profileCountryDraft : profile.country) &&
                getProfileCountry(
                  isEditing ? profileCountryDraft : profile.country,
                ) && (
                  <div
                    className="bioindex-profile-country-flag"
                    title={
                      getProfileCountry(
                        isEditing ? profileCountryDraft : profile.country,
                      ).name
                    }
                    aria-label={`Country: ${getProfileCountry(isEditing ? profileCountryDraft : profile.country).name}`}
                  >
                    <span aria-hidden="true">
                      {
                        getProfileCountry(
                          isEditing ? profileCountryDraft : profile.country,
                        ).flag
                      }
                    </span>
                    <span className="bioindex-profile-country-label">
                      {
                        getProfileCountry(
                          isEditing ? profileCountryDraft : profile.country,
                        ).name
                      }
                    </span>
                  </div>
                )}
              <SectionRefreshOverlay
                show={sectionLoading.profile}
                progress={sectionProgress.profile}
                label="Refreshing profile"
              />

              <div className="bioindex-profile-hero-grid grid grid-cols-1 gap-9 md:grid-cols-[14rem_minmax(0,1fr)] md:gap-10 lg:grid-cols-[16rem_minmax(0,1fr)] lg:gap-12">
                {/* PROFILE PHOTO + TITLE */}
                <div className="bioindex-profile-avatar-column flex flex-col items-center md:pr-8 lg:pr-10">
                  <div className="bioindex-profile-photo-frame bioindex-profile-photo-edit-host relative w-full max-w-[13rem] sm:max-w-[14rem]">
                    <div className="rounded-t-[999px] border border-[#93CA87]/70 bg-white p-2">
                      <div className="overflow-hidden rounded-t-[999px] border border-[#93CA87] bg-slate-100">
                        <img
                          src={
                            (isEditing && profilePhotoPreview) ||
                            (isBioIndexProfilePhoto(profile.photoURL)
                              ? profile.photoURL
                              : defaultProfile)
                          }
                          alt={`${profile.name || "BioIndex user"} profile`}
                          className="aspect-[4/5] w-full object-cover"
                          style={profileImageCropStyle(
                            isEditing ? portraitCropDraft : profile.photoCrop,
                            DEFAULT_PORTRAIT_CROP,
                          )}
                        />
                      </div>
                    </div>
                    {isOwnProfile && (
                      <button
                        type="button"
                        className={`bioindex-profile-photo-image-edit${isEditing ? " bioindex-profile-image-framing" : ""}`}
                        onClick={() =>
                          isEditing
                            ? setProfileEditorFocus("photo")
                            : openProfileImageEditor("photo")
                        }
                        onPointerDown={(event) =>
                          isEditing && beginCropDrag(event, "photo")
                        }
                        onPointerMove={(event) =>
                          isEditing && moveCropDrag(event, "photo")
                        }
                        onPointerUp={endCropDrag}
                        onPointerCancel={endCropDrag}
                        aria-label={
                          isEditing
                            ? "Drag to reposition your profile photo"
                            : "Edit your profile photo"
                        }
                        title={
                          isEditing
                            ? "Drag to reposition portrait"
                            : "Click to edit portrait"
                        }
                      />
                    )}
                  </div>

                  <div className="bioindex-profile-title-block group relative mt-5 w-full max-w-[14rem]">
                    <button
                      type="button"
                      onClick={() => {
                        if (!isOwnProfile || isEditing) return;

                        setPendingTitle(profile.title || selectedTitle);
                        setTitleViewMode("all");
                        setTitleModalOpen(true);
                      }}
                      disabled={!isOwnProfile || isEditing}
                      className={`block w-full text-[#374049] transition ${
                        isOwnProfile
                          ? "cursor-pointer hover:text-[#01767A]"
                          : "cursor-default"
                      }`}
                    >
                      <span className="flex items-center gap-2">
                        <span className="h-px flex-1 bg-[#93CA87]" />

                        <span
                          aria-hidden="true"
                          className="h-1.5 w-1.5 shrink-0 rotate-45 bg-[#93CA87]"
                        />

                        <span className="h-px flex-1 bg-[#93CA87]" />
                      </span>

                      <span
                        className={`my-2 block break-words px-2 text-center font-semibold uppercase leading-snug ${
                          (profile.title || selectedTitle || "").length > 32
                            ? "text-[8px] tracking-[0.08em]"
                            : (profile.title || selectedTitle || "").length > 25
                              ? "text-[9px] tracking-[0.11em]"
                              : (profile.title || selectedTitle || "").length >
                                  18
                                ? "text-[11px] tracking-[0.15em]"
                                : "text-sm tracking-[0.24em]"
                        }`}
                        style={{ fontFamily: "Cormorant Garamond" }}
                      >
                        {sectionLoading.profile
                          ? "Loading title..."
                          : profile.title || selectedTitle}
                      </span>

                      <span className="flex items-center gap-2">
                        <span className="h-px flex-1 bg-[#93CA87]" />

                        <span
                          aria-hidden="true"
                          className="h-1.5 w-1.5 shrink-0 rotate-45 border border-[#93CA87]"
                        />

                        <span className="h-px flex-1 bg-[#93CA87]" />
                      </span>
                    </button>
                  </div>
                </div>

                {/* NAME + ACTIONS + DESCRIPTION */}
                <div className="bioindex-profile-identity flex min-w-0 flex-col justify-center text-center md:text-left">
                  <h1
                    className="break-words text-4xl leading-[1.05] text-[#374049] sm:text-5xl"
                    style={{ fontFamily: "Cormorant Garamond" }}
                  >
                    {(isEditing ? profileNameDraft : profile.name) ||
                      "Your Name"}
                  </h1>

                  <div className="bioindex-profile-name-accent mx-auto mt-4 h-1 w-14 rounded-full bg-[#93CA87] md:mx-0" />

                  <div className="bioindex-profile-ranking-row mt-3">
                    <ProfileRankingMarkers
                      rankings={profileRankings}
                      loading={sectionLoading.social}
                    />
                  </div>

                  {canEditProfileContent && !isEditing && (
                    <div className="bioindex-profile-owner-actions mt-5 flex flex-col justify-center gap-3 sm:flex-row sm:flex-wrap md:justify-start">
                      <button
                        type="button"
                        onClick={openProfileEditor}
                        aria-label={
                          isAdminManagingProfile
                            ? "Edit User Profile"
                            : "Edit Profile"
                        }
                        title={
                          isAdminManagingProfile
                            ? "Edit User Profile"
                            : "Edit Profile"
                        }
                        className="bioindex-profile-edit-button inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#93CA87] bg-white px-4 py-2 text-xs font-medium text-[#6F8F63] transition hover:border-[#24C3BE] hover:bg-[#93CA87]/10 hover:text-[#01767A]"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="h-3.5 w-3.5 shrink-0"
                          aria-hidden="true"
                        >
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4Z" />
                        </svg>

                        <span className="bioindex-profile-action-label">
                          {isAdminManagingProfile
                            ? "Edit User Profile"
                            : "Edit Profile"}
                        </span>
                      </button>

                      {canManageOwnAccount && (
                        <button
                          type="button"
                          onClick={() => setManageProfileOpen(true)}
                          aria-label="Manage Profile"
                          title="Manage Profile"
                          className="bioindex-profile-manage-button inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#374049]/35 bg-white px-4 py-2 text-xs font-medium text-[#374049]/80 transition hover:border-[#374049]/60 hover:bg-[#374049]/5 hover:text-[#374049]"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="h-3.5 w-3.5 shrink-0"
                            aria-hidden="true"
                          >
                            <circle cx="12" cy="12" r="3" />
                            <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21a2 2 0 1 1-4 0v-.09A1.7 1.7 0 0 0 8.6 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.6 8.6a1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V3a2 2 0 1 1 4 0v.09A1.7 1.7 0 0 0 15.4 4.6a1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c.15.37.36.7.6 1 .3.36.7.57 1.1.6H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.51.4Z" />
                          </svg>

                          <span className="bioindex-profile-action-label">
                            Manage Profile
                          </span>
                        </button>
                      )}
                    </div>
                  )}

                  {canUseProfileSocialControls && (
                    <div className="mt-5">
                      {blockedByUserIds.includes(profileUID) ? (
                        <p className="inline-flex rounded-lg border border-slate-200 bg-slate-50/70 px-3 py-2 text-xs text-slate-500">
                          Interaction with this user is unavailable.
                        </p>
                      ) : blockedUserIds.includes(profileUID) ? (
                        <div className="flex flex-wrap items-center justify-center gap-2 md:justify-start">
                          <span className="rounded-full border border-[#374049]/15 bg-[#374049]/5 px-2.5 py-1 text-[10px] font-semibold text-[#374049]/55">
                            User blocked
                          </span>
                          <button
                            type="button"
                            onClick={async () => {
                              const confirmUnblock = window.confirm(
                                `Unblock ${profile.name || "this user"}?\n\nUnblocking does not restore a previous friendship.`,
                              );

                              if (!confirmUnblock) return;

                              await unblockUser(profileUID);
                            }}
                            disabled={blockingUserId === profileUID}
                            className="rounded-lg border border-[#93CA87]/45 bg-white px-3 py-1.5 text-[11px] font-medium text-[#01767A] transition hover:bg-[#93CA87]/10 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {blockingUserId === profileUID
                              ? "Unblocking..."
                              : "Unblock"}
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center justify-center gap-2 md:justify-start">
                          <Link
                            to={`/explore/${profileUID}`}
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-[#93CA87] bg-white px-4 py-2 text-xs font-medium text-[#6F8F63] transition hover:border-[#24C3BE] hover:bg-[#93CA87]/10 hover:text-[#01767A]"
                          >
                            <img
                              src="/ExploreIcon.png"
                              alt=""
                              aria-hidden="true"
                              className="h-4 w-4 object-contain"
                            />
                            Explore Collection
                          </Link>

                          <details className="group relative">
                            <summary className="flex cursor-pointer list-none items-center justify-center gap-1.5 rounded-lg border border-[#374049]/25 bg-white px-3 py-2 text-xs font-medium text-[#374049]/65 transition hover:border-[#374049]/40 hover:bg-[#374049]/5 hover:text-[#374049] [&::-webkit-details-marker]:hidden">
                              <span>More</span>
                              <svg
                                viewBox="0 0 20 20"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                                className="h-3.5 w-3.5 transition group-open:rotate-180"
                              >
                                <path d="m6 8 4 4 4-4" />
                              </svg>
                            </summary>

                            <div className="absolute right-0 top-full z-30 mt-2 min-w-40 overflow-hidden rounded-xl border border-[#374049]/15 bg-white p-1.5 shadow-xl md:left-0 md:right-auto">
                              {friends.includes(profileUID) && (
                                <button
                                  type="button"
                                  onClick={async (event) => {
                                    event.currentTarget
                                      .closest("details")
                                      ?.removeAttribute("open");

                                    const confirmUnfriend = window.confirm(
                                      `Unfriend ${profile.name || "this user"}?`,
                                    );

                                    if (!confirmUnfriend) return;

                                    await unfriend(profileUID);
                                    navigate("/profile");
                                  }}
                                  className="flex w-full rounded-lg px-3 py-2 text-left text-xs text-[#374049]/70 transition hover:bg-[#374049]/5 hover:text-[#374049]"
                                >
                                  Unfriend
                                </button>
                              )}

                              <button
                                type="button"
                                onClick={async (event) => {
                                  event.currentTarget
                                    .closest("details")
                                    ?.removeAttribute("open");

                                  const confirmBlock = window.confirm(
                                    `Block ${profile.name || "this user"}?\n\nAny friendship or pending friend request between you will be removed.`,
                                  );

                                  if (!confirmBlock) return;

                                  await blockUser(profileUID);
                                  navigate("/profile");
                                }}
                                disabled={blockingUserId === profileUID}
                                className="flex w-full rounded-lg px-3 py-2 text-left text-xs text-red-400 transition hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                {blockingUserId === profileUID
                                  ? "Blocking..."
                                  : "Block User"}
                              </button>
                            </div>
                          </details>
                        </div>
                      )}
                    </div>
                  )}

                  <p
                    className={`bioindex-profile-bio-text mt-7 max-w-3xl whitespace-pre-line text-left text-xs leading-6 text-[#374049]/70 sm:text-[13px] sm:leading-6 ${!profileBioExpanded && String((isEditing ? profileBioDraft : profile.bio) || "").length > 175 ? "bioindex-profile-bio-collapsed" : ""}`}
                  >
                    {(isEditing ? profileBioDraft : profile.bio) &&
                    (isEditing ? profileBioDraft : profile.bio).trim() !==
                      "" ? (
                      isEditing ? (
                        profileBioDraft
                      ) : (
                        profile.bio
                      )
                    ) : isOwnProfile ? (
                      <span className="italic text-[#374049]/40">
                        Welcome to BioIndex! This is your personal biodiversity
                        collection space. Start by adding observations,
                        organizing them into collections, and exploring
                        taxonomy. You can edit this section to tell others a bit
                        about yourself and your interests.
                      </span>
                    ) : (
                      <span className="italic text-[#374049]/40">
                        This user hasn’t added a description yet.
                      </span>
                    )}
                  </p>
                  {!isEditing && String(profile.bio || "").length > 175 && (
                    <button
                      type="button"
                      className="bioindex-profile-bio-toggle"
                      onClick={() => setProfileBioExpanded((value) => !value)}
                      aria-expanded={profileBioExpanded}
                    >
                      {profileBioExpanded ? "Read less" : "Read more"}
                      <span aria-hidden="true">
                        {profileBioExpanded ? " ↑" : " →"}
                      </span>
                    </button>
                  )}
                </div>
              </div>
              {/* Phone-only presentation. The original desktop hero above is not changed. */}
              <div className="bioindex-profile-mobile-isolated">
                {canEditProfileContent && (
                  <div className="bioindex-profile-mobile-isolated-actions">
                    <button
                      type="button"
                      onClick={openProfileEditor}
                      disabled={isEditing}
                      title="Edit Profile"
                      aria-label="Edit Profile"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4Z" />
                      </svg>
                    </button>
                    {canManageOwnAccount && (
                      <button
                        type="button"
                        onClick={() => setManageProfileOpen(true)}
                        disabled={isEditing}
                        title="Manage Profile"
                        aria-label="Manage Profile"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <circle cx="12" cy="12" r="3" />
                          <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21a2 2 0 1 1-4 0v-.09A1.7 1.7 0 0 0 8.6 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.6 8.6a1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V3a2 2 0 1 1 4 0v.09A1.7 1.7 0 0 0 15.4 4.6a1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c.15.37.36.7.6 1 .3.36.7.57 1.1.6H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.51.4Z" />
                        </svg>
                      </button>
                    )}
                    <div
                      className="bioindex-profile-mobile-isolated-share"
                      role="img"
                      aria-label="Share Profile, coming soon"
                      title="Coming soon"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <circle cx="18" cy="5" r="3" />
                        <circle cx="6" cy="12" r="3" />
                        <circle cx="18" cy="19" r="3" />
                        <path d="m8.7 10.6 6.6-4.2m-6.6 9 6.6 4.2" />
                      </svg>
                    </div>
                  </div>
                )}
                <div className="bioindex-profile-mobile-isolated-heading">
                  <div className="bioindex-profile-mobile-isolated-portrait bioindex-profile-photo-edit-host">
                    <div className="bioindex-profile-mobile-isolated-portrait-inner">
                      <img
                        src={
                          (isEditing && profilePhotoPreview) ||
                          (isBioIndexProfilePhoto(profile.photoURL)
                            ? profile.photoURL
                            : defaultProfile)
                        }
                        alt={`${profile.name || "BioIndex user"} profile`}
                        style={profileImageCropStyle(
                          isEditing
                            ? mobilePortraitCropDraft
                            : profile.photoCropMobile,
                          DEFAULT_PORTRAIT_CROP,
                        )}
                      />
                    </div>
                    {isOwnProfile && (
                      <button
                        type="button"
                        className={`bioindex-profile-photo-image-edit${isEditing ? " bioindex-profile-image-framing" : ""}`}
                        onClick={() =>
                          isEditing
                            ? setProfileEditorFocus("photo")
                            : openProfileImageEditor("photo")
                        }
                        onPointerDown={(event) =>
                          isEditing && beginCropDrag(event, "photo")
                        }
                        onPointerMove={(event) =>
                          isEditing && moveCropDrag(event, "photo")
                        }
                        onPointerUp={endCropDrag}
                        onPointerCancel={endCropDrag}
                        aria-label={
                          isEditing
                            ? "Drag to reposition portrait"
                            : "Edit profile photo"
                        }
                      />
                    )}
                  </div>
                  <div className="bioindex-profile-mobile-isolated-identity">
                    <MobileProfileName
                      name={
                        (isEditing ? profileNameDraft : profile.name) ||
                        "Your Name"
                      }
                    />
                    <button
                      type="button"
                      className="bioindex-profile-mobile-isolated-title"
                      onClick={() => {
                        if (!isOwnProfile || isEditing) return;
                        setPendingTitle(profile.title || selectedTitle);
                        setTitleViewMode("all");
                        setTitleModalOpen(true);
                      }}
                      disabled={!isOwnProfile || isEditing}
                    >
                      {sectionLoading.profile
                        ? "Loading title..."
                        : profile.title || selectedTitle}
                    </button>
                  </div>
                </div>
                <div className="bioindex-profile-mobile-isolated-rankings">
                  <ProfileRankingMarkers
                    rankings={profileRankings}
                    loading={sectionLoading.social}
                  />
                </div>
                {canUseProfileSocialControls && (
                  <div className="bioindex-profile-mobile-isolated-visitor-actions">
                    {blockedByUserIds.includes(profileUID) ? (
                      <span>Interaction with this user is unavailable.</span>
                    ) : blockedUserIds.includes(profileUID) ? (
                      <button
                        type="button"
                        onClick={async () => {
                          if (
                            window.confirm(
                              `Unblock ${profile.name || "this user"}?`,
                            )
                          )
                            await unblockUser(profileUID);
                        }}
                      >
                        Unblock
                      </button>
                    ) : (
                      <>
                        <Link to={`/explore/${profileUID}`}>
                          Explore Collection
                        </Link>
                        <details>
                          <summary>More</summary>
                          <div>
                            {friends.includes(profileUID) && (
                              <button
                                type="button"
                                onClick={async () => {
                                  if (
                                    !window.confirm(
                                      `Unfriend ${profile.name || "this user"}?`,
                                    )
                                  )
                                    return;
                                  await unfriend(profileUID);
                                  navigate("/profile");
                                }}
                              >
                                Unfriend
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={async () => {
                                if (
                                  !window.confirm(
                                    `Block ${profile.name || "this user"}?`,
                                  )
                                )
                                  return;
                                await blockUser(profileUID);
                                navigate("/profile");
                              }}
                              disabled={blockingUserId === profileUID}
                            >
                              Block User
                            </button>
                          </div>
                        </details>
                      </>
                    )}
                  </div>
                )}
                <div className="bioindex-profile-mobile-isolated-bio">
                  <p
                    className={
                      !profileBioExpanded &&
                      String((isEditing ? profileBioDraft : profile.bio) || "")
                        .length > 175
                        ? "is-collapsed"
                        : ""
                    }
                  >
                    {(isEditing ? profileBioDraft : profile.bio)?.trim()
                      ? isEditing
                        ? profileBioDraft
                        : profile.bio
                      : isOwnProfile
                        ? "Welcome to BioIndex! This is your personal biodiversity collection space. Start by adding observations, organizing them into collections, and exploring taxonomy. You can edit this section to tell others a bit about yourself and your interests."
                        : "This user hasn’t added a description yet."}
                  </p>
                  {!isEditing && String(profile.bio || "").length > 175 && (
                    <button
                      type="button"
                      onClick={() => setProfileBioExpanded((value) => !value)}
                      aria-expanded={profileBioExpanded}
                    >
                      {profileBioExpanded ? "Read less ↑" : "Read more →"}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Desktop sidebar: social and profile tools for your own profile;
                profile navigation when visiting another collector. */}
            <aside className="bioindex-profile-social-wrapper min-w-0">
              {isEditing && canEditProfileContent && (
                <section
                  className="bioindex-profile-live-editor"
                  aria-label="Profile editing controls"
                >
                  <div
                    className="bioindex-profile-live-editor-tabs"
                    role="group"
                    aria-label="Choose section to edit"
                  >
                    {[
                      ["photo", "Portrait"],
                      ["cover", "Landscape"],
                      ["details", "Details"],
                    ].map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        className={
                          (profileEditorFocus || "details") === key
                            ? "is-selected"
                            : ""
                        }
                        onClick={() => setProfileEditorFocus(key)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {(profileEditorFocus || "details") === "photo" && (
                    <div className="bioindex-profile-live-editor-content">
                      <h3>Profile photo</h3>
                      <p>
                        Drag the portrait inside its oval frame. The profile
                        card shows exactly how it will look.
                      </p>
                      <button
                        type="button"
                        className="bioindex-profile-live-text-button"
                        onClick={() =>
                          (isMobileFraming
                            ? setMobilePortraitCropDraft
                            : setPortraitCropDraft)(DEFAULT_PORTRAIT_CROP)
                        }
                        disabled={savingProfile}
                      >
                        Reset portrait position
                      </button>
                      <input
                        ref={profilePhotoInputRef}
                        id="profile-photo-upload"
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        onChange={handleProfilePhotoSelection}
                        disabled={savingProfile}
                        className="sr-only"
                      />
                      <label
                        htmlFor="profile-photo-upload"
                        className="bioindex-profile-live-upload"
                      >
                        {pendingProfilePhoto
                          ? "Choose another portrait"
                          : "Choose new portrait"}
                      </label>
                      {pendingProfilePhoto && (
                        <button
                          className="bioindex-profile-live-text-button"
                          type="button"
                          onClick={clearPendingProfilePhoto}
                          disabled={savingProfile}
                        >
                          Keep current photo
                        </button>
                      )}
                      <p className="bioindex-profile-live-editor-note">
                        PNG, JPEG, WebP, or GIF; up to 8 MB.
                      </p>
                    </div>
                  )}
                  {profileEditorFocus === "cover" && (
                    <div className="bioindex-profile-live-editor-content">
                      <h3>Landscape cover</h3>
                      <p>
                        Drag the landscape directly behind the wave and profile
                        details to position it.
                      </p>
                      <button
                        type="button"
                        className="bioindex-profile-live-text-button"
                        onClick={() =>
                          (isMobileFraming
                            ? setMobileCoverCropDraft
                            : setCoverCropDraft)(DEFAULT_COVER_CROP)
                        }
                        disabled={savingProfile}
                      >
                        Reset landscape position
                      </button>
                      <input
                        ref={profileCoverInputRef}
                        id="profile-cover-upload"
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        onChange={handleProfileCoverSelection}
                        disabled={savingProfile}
                        className="sr-only"
                      />
                      <label
                        htmlFor="profile-cover-upload"
                        className="bioindex-profile-live-upload"
                      >
                        {pendingProfileCover
                          ? "Choose another landscape"
                          : "Choose new landscape"}
                      </label>
                      {(pendingProfileCover || useDefaultProfileCover) && (
                        <button
                          type="button"
                          className="bioindex-profile-live-text-button"
                          onClick={clearPendingProfileCover}
                          disabled={savingProfile}
                        >
                          Undo cover change
                        </button>
                      )}
                      {!useDefaultProfileCover &&
                        (pendingProfileCover || profile.coverPhotoURL) && (
                          <button
                            type="button"
                            className="bioindex-profile-live-text-button"
                            onClick={resetProfileCoverToDefault}
                            disabled={savingProfile}
                          >
                            Use default landscape
                          </button>
                        )}
                      <p className="bioindex-profile-live-editor-note">
                        Wide landscape recommended (1600 × 600); up to 8 MB.
                      </p>
                    </div>
                  )}
                  {(profileEditorFocus || "details") === "details" && (
                    <div className="bioindex-profile-live-editor-content">
                      <label className="bioindex-profile-live-field">
                        Display name
                        <input
                          value={profileNameDraft}
                          disabled={savingProfile}
                          onChange={(e) => setProfileNameDraft(e.target.value)}
                          placeholder="Your display name"
                        />
                      </label>
                      <label className="bioindex-profile-live-field">
                        Country
                        <select
                          value={profileCountryDraft}
                          disabled={savingProfile}
                          onChange={(e) =>
                            setProfileCountryDraft(e.target.value)
                          }
                        >
                          <option value="">Not specified</option>
                          {PROFILE_COUNTRIES.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="bioindex-profile-live-field">
                        Description
                        <textarea
                          value={profileBioDraft}
                          disabled={savingProfile}
                          rows={5}
                          onChange={(e) => setProfileBioDraft(e.target.value)}
                          placeholder="Tell people about your interests"
                        />
                      </label>
                    </div>
                  )}
                  {profileEditError && (
                    <p role="alert" className="bioindex-profile-live-error">
                      {profileEditError}
                    </p>
                  )}
                  <div className="bioindex-profile-live-editor-actions">
                    <button
                      type="button"
                      className="bioindex-profile-live-cancel"
                      onClick={closeProfileEditor}
                      disabled={savingProfile}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="bioindex-profile-live-save"
                      disabled={savingProfile}
                      onClick={async () => {
                        if (savingProfile) return;
                        setSavingProfile(true);
                        setProfileEditError("");
                        try {
                          await saveProfile();
                        } catch (error) {
                          console.error("Failed to save profile:", error);
                          setProfileEditError(
                            isAdminManagingProfile
                              ? "The user profile could not be saved."
                              : "Your profile could not be saved. Please try again.",
                          );
                        } finally {
                          setSavingProfile(false);
                        }
                      }}
                    >
                      {savingProfile ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </section>
              )}
              {!isEditing && isOwnProfile && (
                <div className="bioindex-profile-sidebar-social">
                  <div className="bioindex-profile-social-panel">
                    <ProfileSocial
                      friends={visibleFriends}
                      profiles={visibleProfiles}
                      requestsReceived={visibleRequestsReceived}
                      requestsSent={visibleRequestsSent}
                      friendsTaxa={friendsTaxa}
                      friendsModalOpen={friendsModalOpen}
                      setFriendsModalOpen={setFriendsModalOpen}
                      requestsModalOpen={requestsModalOpen}
                      setRequestsModalOpen={setRequestsModalOpen}
                      discoverModalOpen={discoverModalOpen}
                      setDiscoverModalOpen={setDiscoverModalOpen}
                      acceptRequest={acceptRequest}
                      declineRequest={declineRequest}
                      navigate={navigate}
                      addFriendRequest={addFriendRequest}
                      SectionRefreshOverlay={SectionRefreshOverlay}
                      sectionLoading={sectionLoading}
                      sectionProgress={sectionProgress}
                      showPanel={true}
                      currentProfileUID={profileUID}
                      friendsTaxaLoading={friendsTaxaLoading}
                      isOwnProfile={isOwnProfile}
                      isAdmin={isAdmin}
                      currentProfileName={profile.name}
                    />
                  </div>
                  <div className="bioindex-profile-friend-previews">
                    {visibleFriends.length > 0 ? (
                      <div
                        className="bioindex-profile-avatar-strip"
                        ref={friendPreviewStripRef}
                      >
                        {shownFriendPreviewUids.map((friendUID) => {
                          const friendProfile = visibleProfiles.find(
                            (candidate) => candidate.uid === friendUID,
                          );
                          return (
                            <button
                              key={friendUID}
                              type="button"
                              title={
                                friendProfile?.name || "View friend profile"
                              }
                              aria-label={`View ${friendProfile?.name || "friend"}'s profile`}
                              onClick={() => navigate(`/profile/${friendUID}`)}
                              className="bioindex-profile-friend-avatar"
                            >
                              <img
                                src={
                                  isBioIndexProfilePhoto(
                                    friendProfile?.photoURL,
                                  )
                                    ? friendProfile.photoURL
                                    : defaultProfile
                                }
                                alt=""
                              />
                            </button>
                          );
                        })}
                        {remainingFriendPreviewCount > 0 && (
                          <button
                            type="button"
                            className="bioindex-profile-avatar-more"
                            onClick={() => setFriendsModalOpen(true)}
                            aria-label={`View all ${visibleFriends.length} friends; ${remainingFriendPreviewCount} more`}
                          >
                            +{remainingFriendPreviewCount}
                          </button>
                        )}
                      </div>
                    ) : (
                      <span className="bioindex-profile-friend-empty">
                        Discover people to connect with other collectors.
                      </span>
                    )}
                  </div>
                </div>
              )}

              {!isOwnProfile && (
                <div className="bioindex-profile-explore-profiles">
                  <div className="bioindex-profile-sidebar-title">
                    <h2>Explore Profiles</h2>
                    <span>Switch collections</span>
                  </div>

                  <button
                    type="button"
                    className="bioindex-profile-switch-profile bioindex-profile-switch-own"
                    onClick={() => navigate("/profile")}
                  >
                    <img
                      src={
                        isBioIndexProfilePhoto(
                          visibleProfiles.find(
                            (person) => person.uid === auth.currentUser?.uid,
                          )?.photoURL,
                        )
                          ? visibleProfiles.find(
                              (person) => person.uid === auth.currentUser?.uid,
                            )?.photoURL
                          : defaultProfile
                      }
                      alt=""
                    />
                    <span>
                      <strong>My Profile</strong>
                      <small>Return to your collection</small>
                    </span>
                    <span
                      className="bioindex-profile-switch-chevron"
                      aria-hidden="true"
                    >
                      ›
                    </span>
                  </button>

                  <div className="bioindex-profile-explore-list-heading">
                    Your friends
                  </div>
                  <div
                    className="bioindex-profile-friend-scroll"
                    role="list"
                    aria-label="Friend profiles"
                  >
                    {[
                      ...new Set([
                        ...(visibleFriends.includes(profileUID)
                          ? []
                          : [profileUID]),
                        ...visibleFriends,
                      ]),
                    ].map((friendUID) => {
                      const isSelected = friendUID === profileUID;
                      const friendProfile = visibleProfiles.find(
                        (person) => person.uid === friendUID,
                      );
                      return (
                        <button
                          key={friendUID}
                          type="button"
                          role="listitem"
                          disabled={isSelected}
                          aria-current={isSelected ? "page" : undefined}
                          onClick={() => navigate(`/profile/${friendUID}`)}
                          className={`bioindex-profile-switch-profile ${isSelected ? "bioindex-profile-switch-selected" : ""}`}
                          title={
                            isSelected
                              ? "Currently viewing"
                              : `View ${friendProfile?.name || "friend"}'s profile`
                          }
                        >
                          <img
                            src={
                              isBioIndexProfilePhoto(friendProfile?.photoURL)
                                ? friendProfile.photoURL
                                : friendUID === profileUID &&
                                    isBioIndexProfilePhoto(profile.photoURL)
                                  ? profile.photoURL
                                  : defaultProfile
                            }
                            alt=""
                          />
                          <span>
                            <strong>
                              {friendUID === profileUID
                                ? profile.name || "Current profile"
                                : friendProfile?.name || "BioIndex Friend"}
                            </strong>
                            <small>
                              {isSelected
                                ? "Currently viewing"
                                : "View profile"}
                            </small>
                          </span>
                          <span
                            className="bioindex-profile-switch-chevron"
                            aria-hidden="true"
                          >
                            {isSelected ? "✓" : "›"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    className="bioindex-profile-discover-shortcut"
                    onClick={() => setDiscoverModalOpen(true)}
                  >
                    <span aria-hidden="true">⌕</span> Discover People
                  </button>
                </div>
              )}

              {/* Mount the existing friendship dialogs when visiting someone else. */}
              {!isOwnProfile && (
                <ProfileSocial
                  friends={visibleFriends}
                  profiles={visibleProfiles}
                  requestsReceived={visibleRequestsReceived}
                  requestsSent={visibleRequestsSent}
                  friendsTaxa={friendsTaxa}
                  friendsModalOpen={friendsModalOpen}
                  setFriendsModalOpen={setFriendsModalOpen}
                  requestsModalOpen={requestsModalOpen}
                  setRequestsModalOpen={setRequestsModalOpen}
                  discoverModalOpen={discoverModalOpen}
                  setDiscoverModalOpen={setDiscoverModalOpen}
                  acceptRequest={acceptRequest}
                  declineRequest={declineRequest}
                  navigate={navigate}
                  addFriendRequest={addFriendRequest}
                  SectionRefreshOverlay={SectionRefreshOverlay}
                  sectionLoading={sectionLoading}
                  sectionProgress={sectionProgress}
                  showPanel={false}
                  currentProfileUID={profileUID}
                  friendsTaxaLoading={friendsTaxaLoading}
                  isOwnProfile={isOwnProfile}
                  isAdmin={isAdmin}
                  currentProfileName={profile.name}
                />
              )}

              {!isEditing && canEditProfileContent && (
                <div className="bioindex-profile-tools-card">
                  <h2>Profile Tools</h2>
                  <div
                    className={`bioindex-profile-tools-buttons bioindex-profile-tools-grid${canManageOwnAccount ? "" : " bioindex-profile-tools-grid--two"}`}
                  >
                    <button
                      type="button"
                      className="bioindex-profile-tool bioindex-profile-tool--primary"
                      onClick={openProfileEditor}
                    >
                      <svg
                        className="bioindex-profile-tool-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L8 18l-4 1 1-4Z" />
                      </svg>
                      <span className="bioindex-profile-tool-copy">
                        <strong>
                          {isAdminManagingProfile
                            ? "Edit User Profile"
                            : "Edit Profile"}
                        </strong>
                      </span>
                      <svg
                        className="bioindex-profile-tool-chevron"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="m9 6 6 6-6 6" />
                      </svg>
                    </button>

                    {canManageOwnAccount && (
                      <button
                        type="button"
                        className="bioindex-profile-tool"
                        onClick={() => setManageProfileOpen(true)}
                      >
                        <svg
                          className="bioindex-profile-tool-icon"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <circle cx="12" cy="12" r="3" />
                          <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21a2 2 0 1 1-4 0v-.09A1.7 1.7 0 0 0 8.6 19.4a1.7 1.7 0 0 0-1.88.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-.6-1 1.7 1.7 0 0 0-1.1-.4H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.6 8.6a1.7 1.7 0 0 0-.34-1.88l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 9 4.6a1.7 1.7 0 0 0 1-.6 1.7 1.7 0 0 0 .4-1.1V3a2 2 0 1 1 4 0v.09A1.7 1.7 0 0 0 15.4 4.6a1.7 1.7 0 0 0 1.88-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9c.15.37.36.7.6 1 .3.36.7.57 1.1.6H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.51.4Z" />
                        </svg>
                        <span className="bioindex-profile-tool-copy">
                          <strong>Manage Profile</strong>
                        </span>
                        <svg
                          className="bioindex-profile-tool-chevron"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="m9 6 6 6-6 6" />
                        </svg>
                      </button>
                    )}

                    <div
                      className="bioindex-profile-tool bioindex-profile-tool--unavailable"
                      aria-label="Share Profile, coming soon"
                    >
                      <svg
                        className="bioindex-profile-tool-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <circle cx="18" cy="5" r="3" />
                        <circle cx="6" cy="12" r="3" />
                        <circle cx="18" cy="19" r="3" />
                        <path d="m8.7 10.6 6.6-4.2m-6.6 9 6.6 4.2" />
                      </svg>
                      <span className="bioindex-profile-tool-copy">
                        <strong>Share Profile</strong>
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </aside>
          </div>

          {/* Desktop: Coverage + Recent Uploads. */}
          <div className="bioindex-profile-metrics-row">
            {/* Taxonomic Coverage: beside the profile on wide desktops. */}
            <div className="bioindex-profile-coverage min-w-0">
              <ProfileTaxonomicCoverage
                isOwnProfile={isOwnProfile}
                stats={stats}
                sectionLoading={sectionLoading}
                sectionProgress={sectionProgress}
                SectionRefreshOverlay={SectionRefreshOverlay}
                StatLine={StatLine}
                getTaxonItems={getTaxonItems}
                setTaxonViewLabel={setTaxonViewLabel}
                setTaxonViewItems={setTaxonViewItems}
                setTaxonViewOpen={setTaxonViewOpen}
              />
            </div>
            {/* Recent uploads */}
            <div className="bioindex-profile-recent-row grid grid-cols-1 lg:grid-cols-10 gap-8">
              {/* STATISTICS (80%) */}

              <ProfileRecentUploads
                isOwnProfile={isOwnProfile}
                profileUID={profileUID}
                recentUploads={recentUploads}
                onOpenRecentUpload={openRecentUploadViewer}
                displayLocation={displayLocation}
                SectionRefreshOverlay={SectionRefreshOverlay}
                sectionLoading={sectionLoading}
                sectionProgress={sectionProgress}
              />
            </div>

            <div className="bioindex-profile-challenge-row">
              <ProfileTaxonomyChallenge
                isOwnProfile={isOwnProfile}
                sectionLoading={sectionLoading}
                sectionProgress={sectionProgress}
                SectionRefreshOverlay={SectionRefreshOverlay}
                quizType={quizType}
                setQuizType={setQuizType}
                questionCount={questionCount}
                setQuestionCount={setQuestionCount}
                filterType={filterType}
                setFilterType={setFilterType}
                filterValue={filterValue}
                setFilterValue={setFilterValue}
                availableSpecies={availableSpecies}
                filteredOptions={filteredOptions}
                friendCollectionOptions={friendCollectionOptions}
                friendCollectionsLoading={friendsTaxaLoading}
                quizFriendUID={quizFriendUID}
                setQuizFriendUID={setQuizFriendUID}
                startQuiz={startQuiz}
              />
            </div>
          </div>

          {/* GENUS CARDS */}
          <div className="bioindex-profile-genus-block">
            <ProfileGenusCards
              isOwnProfile={isOwnProfile}
              profileUID={profileUID}
              profile={profile}
              stats={stats}
              favoriteGenera={favoriteGenera}
              setFavoriteGenera={setFavoriteGenera}
              sectionLoading={sectionLoading}
              sectionProgress={sectionProgress}
              SectionRefreshOverlay={SectionRefreshOverlay}
              showGenusHint={showGenusHint}
              setShowGenusHint={setShowGenusHint}
              editingGenera={editingGenera}
              setEditingGenera={setEditingGenera}
              genusModalOpen={genusModalOpen}
              setGenusModalOpen={setGenusModalOpen}
              genusPickerOpen={genusPickerOpen}
              setGenusPickerOpen={setGenusPickerOpen}
              genusSlotIndex={genusSlotIndex}
              setGenusSlotIndex={setGenusSlotIndex}
              selectedGenus={selectedGenus}
              setSelectedGenus={setSelectedGenus}
              savingGenus={savingGenus}
              setSavingGenus={setSavingGenus}
              resolveTaxonomy={resolveTaxonomy}
              navigate={navigate}
              saveOwnProfileCache={saveOwnProfileCache}
              readOwnProfileCache={readOwnProfileCache}
              setProfile={setProfile}
              speciesWithPhotos={speciesWithPhotos}
              quizSpecies={quizSpecies}
              collections={collections}
              recentUploads={recentUploads}
              collectionMeta={collectionMeta}
              unlockedTitles={unlockedTitles}
            />
          </div>

          {/* COLLECTIONS */}
          {(isOwnProfile || Object.keys(collections).length > 0) && (
            <div className="bioindex-profile-collections-block">
              <ProfileCollections
                isOwnProfile={isOwnProfile}
                collections={collections}
                setCollections={setCollections}
                collectionIntro={collectionIntro}
                setCollectionIntro={setCollectionIntro}
                collectionIntroOpen={collectionIntroOpen}
                setCollectionIntroOpen={setCollectionIntroOpen}
                collectionMeta={collectionMeta}
                setCollectionMeta={setCollectionMeta}
                editingCollection={editingCollection}
                setEditingCollection={setEditingCollection}
                editTitle={editTitle}
                setEditTitle={setEditTitle}
                editDescription={editDescription}
                setEditDescription={setEditDescription}
                setCollectionPhotos={setCollectionPhotos}
                setCollectionIndex={setCollectionIndex}
                setCollectionName={setCollectionName}
                setCollectionViewOpen={setCollectionViewOpen}
                selectedSpeciesForCollection={selectedSpeciesForCollection}
                setSelectedSpeciesForCollection={
                  setSelectedSpeciesForCollection
                }
                selectedPhotoForCollection={selectedPhotoForCollection}
                setSelectedPhotoForCollection={setSelectedPhotoForCollection}
                collectionSelectorOpen={collectionSelectorOpen}
                setCollectionSelectorOpen={setCollectionSelectorOpen}
                confirmAddOpen={confirmAddOpen}
                setConfirmAddOpen={setConfirmAddOpen}
                photoAddedMessage={photoAddedMessage}
                setPhotoAddedMessage={setPhotoAddedMessage}
                speciesWithPhotos={speciesWithPhotos}
                SectionRefreshOverlay={SectionRefreshOverlay}
                sectionLoading={sectionLoading}
                sectionProgress={sectionProgress}
                showCollectionsHint={showCollectionsHint}
                setShowCollectionsHint={setShowCollectionsHint}
              />
            </div>
          )}
          {/* FAVORITES / GALLERY */}
          <div className="bioindex-profile-gallery-block">
            <ProfileFavorites
              isOwnProfile={isOwnProfile}
              profile={profile}
              savedMessage={savedMessage}
              openFavoriteViewer={openFavoriteViewer}
              openFavoriteSelector={openFavoriteSelector}
              favSelectorOpen={favSelectorOpen}
              setFavSelectorOpen={setFavSelectorOpen}
              selectedSpeciesForFav={selectedSpeciesForFav}
              setSelectedSpeciesForFav={setSelectedSpeciesForFav}
              speciesWithPhotos={speciesWithPhotos}
              assignFavorite={assignFavorite}
            />
          </div>

          <footer className="bioindex-profile-footer border-t border-white/10 pt-4 pb-2 text-center">
            <p className="text-[11px] text-white/55">
              © {new Date().getFullYear()} BioIndex™. All rights reserved.
            </p>

            <div className="mt-1 flex justify-center gap-3 text-[10px] text-white/55">
              <Link to="/terms" className="hover:text-[#24C3BE]">
                Terms
              </Link>
              <Link to="/privacy" className="hover:text-[#24C3BE]">
                Privacy
              </Link>
              <Link to="/community-guidelines" className="hover:text-[#24C3BE]">
                Guidelines
              </Link>
              <Link to="/copyright" className="hover:text-[#24C3BE]">
                Copyright
              </Link>
              <Link to="/contact" className="hover:text-[#24C3BE]">
                Contact
              </Link>
            </div>
          </footer>
        </div>
      </div>

      {/* COLLECTION SELECTOR MODAL */}

      {confirmAddOpen && selectedPhotoForCollection && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[70]">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm text-center">
            {!photoAddedMessage ? (
              <>
                <p className="text-lg mb-4">
                  Confirm add to {collectionIntro.name}?
                </p>

                <div className="flex justify-center gap-3">
                  <button
                    onClick={async () => {
                      const updatedForms =
                        selectedSpeciesForCollection.forms.map((form) => {
                          if (form.url === selectedPhotoForCollection.url) {
                            const collections = form.collections || [];
                            if (!collections.includes(collectionIntro.name)) {
                              return {
                                ...form,
                                collections: [
                                  ...collections,
                                  collectionIntro.name,
                                ],
                                collectionAddedAt: {
                                  ...(form.collectionAddedAt || {}),
                                  [collectionIntro.name]: Date.now(),
                                },
                              };
                            }
                          }
                          return form;
                        });

                      await setDoc(
                        doc(
                          db,
                          "users",
                          auth.currentUser.uid,
                          "species",
                          selectedSpeciesForCollection.id,
                        ),
                        { forms: updatedForms },
                        { merge: true },
                      );

                      setPhotoAddedMessage(true);
                    }}
                    className="px-4 py-2 bg-[#24c3be] text-white rounded-xl"
                  >
                    Yes
                  </button>

                  <button
                    onClick={() => {
                      setConfirmAddOpen(false);
                      setSelectedPhotoForCollection(null);
                    }}
                    className="px-4 py-2 border rounded-xl"
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-lg mb-4">
                  Photo added to {collectionIntro.name}!
                </p>

                <button
                  onClick={() => {
                    setConfirmAddOpen(false);
                    setSelectedPhotoForCollection(null);
                    setSelectedSpeciesForCollection(null);
                    setCollectionSelectorOpen(false);

                    // reload collections
                    window.location.reload();
                  }}
                  className="px-4 py-2 bg-[#24c3be] text-white rounded-xl"
                >
                  OK
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <ProfilePhotoViewer
        isOwnProfile={isOwnProfile}
        collectionOwnerUid={profileUID}
        canEditObservationMetadata={canEditObservationMetadata}
        canReclassifyObservations={canReclassifyObservations}
        canDeleteObservations={canDeleteObservations}
        deletingObservation={deletingObservation}
        openAdminDeleteObservation={openAdminDeleteObservation}
        deleteRecentUploadPhoto={deleteRecentUploadPhoto}
        deletingOwnObservation={deletingOwnObservation}
        favViewOpen={favViewOpen}
        setFavViewOpen={setFavViewOpen}
        favViewSpecies={favViewSpecies}
        setFavViewSpecies={setFavViewSpecies}
        favViewForm={favViewForm}
        setFavViewForm={setFavViewForm}
        favViewImageLoaded={favViewImageLoaded}
        setFavViewImageLoaded={setFavViewImageLoaded}
        favViewFullscreen={favViewFullscreen}
        setFavViewFullscreen={setFavViewFullscreen}
        favCollectionsPopoverOpen={favCollectionsPopoverOpen}
        setFavCollectionsPopoverOpen={setFavCollectionsPopoverOpen}
        editingForm={editingForm}
        setEditingForm={setEditingForm}
        openLocationPicker={() => setProfileLocationPickerOpen(true)}
        editingSpecies={editingSpecies}
        renamingPhoto={renamingPhoto}
        renameValue={renameValue}
        setRenameValue={setRenameValue}
        renameCommon={renameCommon}
        setRenameCommon={setRenameCommon}
        renameSaving={renameSaving}
        renameError={renameError}
        renameSuggestions={renameSuggestions}
        renameActiveIndex={renameActiveIndex}
        renameActiveField={renameActiveField}
        updateRenameSuggestions={updateRenameSuggestions}
        handleRenameKeyDown={handleRenameKeyDown}
        selectRenameSuggestion={selectRenameSuggestion}
        enterPhotoEdit={enterPhotoEdit}
        exitPhotoEdit={exitPhotoEdit}
        savePhotoMetadata={savePhotoMetadata}
        enterPhotoRename={enterPhotoRename}
        exitPhotoRename={exitPhotoRename}
        handleRenameProfilePhoto={handleRenameProfilePhoto}
        normalizeDate={normalizeDate}
        formatDateForDisplay={formatDateForDisplay}
        hasMappedLocation={hasMappedLocation}
        displayLocation={displayLocation}
        resolveTaxonomy={resolveTaxonomy}
        DAYS={DAYS}
        MONTHS={MONTHS}
        YEARS={YEARS}
        collections={collections}
        setCollectionPhotos={setCollectionPhotos}
        saveCollectionOrder={saveCollectionOrder}
        recentUploadViewerIndex={recentUploadViewerIndex}
        recentUploadCount={recentUploads.length}
        onPreviousRecentUpload={() =>
          openRecentUploadViewer(recentUploadViewerIndex - 1)
        }
        onNextRecentUpload={() =>
          openRecentUploadViewer(recentUploadViewerIndex + 1)
        }
      />

      {adminDeleteTarget && canDeleteObservations && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/55 px-3 py-6"
          onClick={closeAdminDeleteObservation}
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-3xl border border-red-200 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-red-100 bg-red-50 px-6 py-5 text-center">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-red-600">
                Administrator Moderation
              </div>

              <h3
                className="mt-1 text-3xl text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                Delete Observation
              </h3>
            </div>

            <div className="p-6">
              <div className="rounded-2xl border border-[#93CA87]/35 bg-[#FCFFF6] px-4 py-4 text-center">
                <p className="text-sm font-semibold text-[#374049]">
                  <span className="italic">
                    {adminDeleteTarget.scientificName}
                  </span>

                  {adminDeleteTarget.commonName && (
                    <span className="font-normal">
                      {" "}
                      ({adminDeleteTarget.commonName})
                    </span>
                  )}
                </p>

                <p className="mt-1 text-xs text-slate-500">
                  Lifeform: {adminDeleteTarget.formLabel}
                </p>

                {adminDeleteTarget.photoCount === 1 && (
                  <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
                    This is the species record’s only photo. Deleting it will
                    also remove the empty species record.
                  </p>
                )}
              </div>

              <p className="mt-5 text-sm leading-relaxed text-slate-600">
                This permanently removes the observation from the user’s
                profile, collections, favorites, and Firebase Storage. This
                action cannot be undone.
              </p>

              <label className="mt-5 block">
                <span className="text-xs font-semibold uppercase tracking-wide text-[#374049]">
                  Moderation reason
                </span>

                <textarea
                  value={adminDeleteReason}
                  onChange={(event) => {
                    setAdminDeleteReason(event.target.value);
                    setAdminDeleteError("");
                  }}
                  disabled={deletingObservation}
                  maxLength={500}
                  rows={4}
                  placeholder="Explain why this observation is being removed..."
                  className="mt-2 w-full resize-none rounded-2xl border border-[#93CA87]/45 bg-white px-4 py-3 text-sm text-[#374049] outline-none transition focus:border-[#24C3BE] focus:ring-2 focus:ring-[#24C3BE]/15 disabled:cursor-not-allowed disabled:bg-slate-50"
                />
              </label>

              <div className="mt-1 text-right text-[10px] text-slate-400">
                {adminDeleteReason.length}/500
              </div>

              {adminDeleteError && (
                <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-600">
                  {adminDeleteError}
                </div>
              )}

              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeAdminDeleteObservation}
                  disabled={deletingObservation}
                  className="rounded-xl border border-[#93CA87]/45 bg-white px-5 py-2.5 text-sm font-semibold text-[#374049] transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={confirmAdminDeleteObservation}
                  disabled={
                    deletingObservation || adminDeleteReason.trim().length < 10
                  }
                  className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
                >
                  {deletingObservation
                    ? "Deleting Observation..."
                    : "Permanently Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <BioIndexProfileMobileNav
        onSearch={() => setSearchOpen(true)}
        onVoiceSearch={() => setVoiceOpen(true)}
        explorePath={profileExplorePath}
      />

      <SearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        uid={profileUID}
      />
      {voiceOpen && (
        <SearchModal
          open
          onClose={() => setVoiceOpen(false)}
          uid={profileUID}
          voiceOnly
        />
      )}

      {adminDeleteAccountOpen && isAdminManagingProfile && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 px-3 py-6"
          onClick={closeAdminDeleteAccount}
        >
          <div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="admin-delete-account-title"
            className="w-full max-w-lg overflow-hidden rounded-3xl border border-red-200 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="border-b border-red-100 bg-red-50 px-6 py-5 text-center">
              <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-red-500">
                Permanent administrator action
              </div>
              <h3
                id="admin-delete-account-title"
                className="mt-1 text-3xl text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                Delete Account
              </h3>
            </div>

            <div className="p-6">
              <div className="rounded-2xl border border-[#93CA87]/35 bg-[#FCFFF6] px-4 py-4 text-center">
                <p className="text-sm font-semibold text-[#374049]">
                  {profile.name || "BioIndex user"}
                </p>
                <p className="mt-1 break-all text-[10px] text-[#374049]/45">
                  {profileUID}
                </p>
              </div>

              <p className="mt-4 text-sm leading-relaxed text-[#374049]/70">
                This permanently removes the user’s authentication account,
                profile, observations, collections, friendships, requests,
                blocks, and associated uploaded files. This cannot be undone.
              </p>

              <label className="mt-5 block">
                <span className="text-xs font-semibold text-[#374049]">
                  Administrative reason
                </span>
                <textarea
                  value={adminAccountDeleteReason}
                  onChange={(event) => {
                    setAdminAccountDeleteReason(event.target.value);
                    setAdminAccountDeleteError("");
                  }}
                  disabled={adminAccountDeleteSaving}
                  maxLength={500}
                  rows={3}
                  placeholder="Explain why this account must be deleted..."
                  className="mt-2 w-full resize-none rounded-2xl border border-[#93CA87]/45 bg-white px-4 py-3 text-sm text-[#374049] outline-none transition focus:border-[#24C3BE] focus:ring-2 focus:ring-[#24C3BE]/15 disabled:bg-slate-50"
                />
              </label>

              <label className="mt-4 block">
                <span className="text-xs font-semibold text-[#374049]">
                  Type DELETE to confirm
                </span>
                <input
                  type="text"
                  value={adminAccountDeleteConfirmation}
                  onChange={(event) => {
                    setAdminAccountDeleteConfirmation(event.target.value);
                    setAdminAccountDeleteError("");
                  }}
                  disabled={adminAccountDeleteSaving}
                  autoComplete="off"
                  className="mt-2 h-11 w-full rounded-2xl border border-red-200 bg-white px-4 text-sm font-semibold tracking-wide text-[#374049] outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-100 disabled:bg-slate-50"
                />
              </label>

              {adminAccountDeleteError && (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-600">
                  {adminAccountDeleteError}
                </div>
              )}

              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeAdminDeleteAccount}
                  disabled={adminAccountDeleteSaving}
                  className="rounded-xl border border-[#374049]/20 bg-white px-5 py-2.5 text-sm font-semibold text-[#374049] transition hover:bg-[#374049]/5 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmAdminDeleteAccount}
                  disabled={
                    adminAccountDeleteSaving ||
                    adminAccountDeleteReason.trim().length < 10 ||
                    adminAccountDeleteConfirmation.trim().toUpperCase() !==
                      "DELETE"
                  }
                  className="rounded-xl bg-red-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-red-300"
                >
                  {adminAccountDeleteSaving
                    ? "Deleting Account..."
                    : "Permanently Delete Account"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {accountStatusAction && canManageAccountStatus && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 px-3 py-6"
          onClick={closeAccountStatusAction}
        >
          <div
            className="w-full max-w-lg overflow-hidden rounded-3xl border border-[#93CA87]/40 bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div
              className={`border-b px-6 py-5 text-center ${
                accountStatusAction === "suspend"
                  ? "border-red-100 bg-red-50"
                  : "border-[#24C3BE]/25 bg-[#FCFFF6]"
              }`}
            >
              <div
                className={`text-[10px] font-semibold uppercase tracking-[0.18em] ${
                  accountStatusAction === "suspend"
                    ? "text-red-600"
                    : "text-[#01767A]"
                }`}
              >
                Administrator Account Action
              </div>

              <h3
                className="mt-1 text-3xl text-[#374049]"
                style={{
                  fontFamily: "Cormorant Garamond",
                }}
              >
                {accountStatusAction === "suspend"
                  ? "Suspend Account"
                  : "Restore Account"}
              </h3>
            </div>

            <div className="p-6">
              <div className="rounded-2xl border border-[#93CA87]/35 bg-[#FCFFF6] px-4 py-4 text-center">
                <p className="text-sm font-semibold text-[#374049]">
                  {profile.name || "BioIndex user"}
                </p>

                <p className="mt-1 break-all text-[10px] text-slate-400">
                  {profileUID}
                </p>
              </div>

              <p className="mt-5 text-sm leading-relaxed text-slate-600">
                {accountStatusAction === "suspend"
                  ? "The user will immediately lose access to uploads, profile changes, forum participation, voting, friend actions, kudos, and other account writes. Existing content will remain intact."
                  : "The user will regain normal access to BioIndex. Existing profile, observation, collection, forum, and social content will remain unchanged."}
              </p>

              <label className="mt-5 block">
                <span className="text-xs font-semibold uppercase tracking-wide text-[#374049]">
                  Administrative reason
                </span>

                <textarea
                  value={accountStatusReason}
                  onChange={(event) => {
                    setAccountStatusReason(event.target.value);
                    setAccountStatusError("");
                  }}
                  disabled={accountStatusSaving}
                  maxLength={500}
                  rows={4}
                  placeholder={
                    accountStatusAction === "suspend"
                      ? "Explain why this account is being suspended..."
                      : "Explain why this account is being restored..."
                  }
                  className="mt-2 w-full resize-none rounded-2xl border border-[#93CA87]/45 bg-white px-4 py-3 text-sm text-[#374049] outline-none transition focus:border-[#24C3BE] focus:ring-2 focus:ring-[#24C3BE]/15 disabled:bg-slate-50"
                />
              </label>

              <div className="mt-1 text-right text-[10px] text-slate-400">
                {accountStatusReason.length}/500
              </div>

              {accountStatusError && (
                <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-600">
                  {accountStatusError}
                </div>
              )}

              <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={closeAccountStatusAction}
                  disabled={accountStatusSaving}
                  className="rounded-xl border border-[#93CA87]/45 bg-white px-5 py-2.5 text-sm font-semibold text-[#374049] transition hover:bg-slate-50 disabled:opacity-50"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={confirmAccountStatusAction}
                  disabled={
                    accountStatusSaving ||
                    accountStatusReason.trim().length < 10
                  }
                  className={`rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50 ${
                    accountStatusAction === "suspend"
                      ? "bg-red-600 hover:bg-red-700"
                      : "bg-[#01767A] hover:bg-[#24C3BE]"
                  }`}
                >
                  {accountStatusSaving
                    ? "Applying Action..."
                    : accountStatusAction === "suspend"
                      ? "Suspend Account"
                      : "Restore Account"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {manageProfileOpen && isOwnProfile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-3 py-6"
          onClick={() => setManageProfileOpen(false)}
        >
          <div
            className="w-full max-w-md rounded-3xl border border-[#93CA87]/40 bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h3
              className="text-center text-3xl text-[#374049]"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              Manage Profile
            </h3>

            <p className="mt-2 text-center text-sm leading-relaxed text-slate-500">
              Manage your profile settings and preferences.
            </p>

            <div className="mt-6 space-y-3">
              <button
                type="button"
                onClick={() => {
                  setManageProfileOpen(false);
                  setBlockedUsersOpen(true);
                }}
                className="flex w-full items-center gap-4 rounded-2xl border border-[#93CA87]/40 bg-[#FCFFF6] px-4 py-4 text-left transition hover:border-[#24C3BE] hover:bg-[#93CA87]/10"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-[#374049]">Blocked Users</p>

                  <p className="mt-1 text-xs leading-relaxed text-slate-500">
                    View and unblock users you have blocked.
                  </p>
                </div>

                {blockedUserIds.length > 0 && (
                  <span className="rounded-full bg-[#374049] px-2.5 py-1 text-xs font-semibold text-white">
                    {blockedUserIds.length}
                  </span>
                )}

                <span
                  aria-hidden="true"
                  className="text-2xl leading-none text-[#374049]/45"
                >
                  ›
                </span>
              </button>

              <button
                type="button"
                onClick={async () => {
                  setManageProfileOpen(false);
                  await deleteProfile();
                }}
                disabled={deletingProfile}
                className="flex w-full items-center gap-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-left transition hover:border-red-300 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-red-700">Delete Profile</p>

                  <p className="mt-1 text-xs leading-relaxed text-red-600/70">
                    Permanently delete your account, observations, collections,
                    and profile data.
                  </p>
                </div>

                <span
                  aria-hidden="true"
                  className="text-2xl leading-none text-red-500/60"
                >
                  ›
                </span>
              </button>
            </div>

            <button
              type="button"
              onClick={() => setManageProfileOpen(false)}
              className="mt-5 w-full rounded-xl bg-[#374049] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#01767A]"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {blockedUsersOpen && isOwnProfile && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-3 py-6"
          onClick={() => setBlockedUsersOpen(false)}
        >
          <div
            className="w-full max-w-lg rounded-3xl border border-[#93CA87]/40 bg-white p-6 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h3
              className="text-center text-3xl text-[#374049]"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              Blocked Users
            </h3>

            <p className="mx-auto mt-2 max-w-md text-center text-xs leading-relaxed text-slate-500">
              Blocked users cannot send you friend requests or social
              notifications. Unblocking someone does not restore a previous
              friendship.
            </p>

            {blockedUserIds.length > 0 ? (
              <div className="mt-5 max-h-[50vh] space-y-2 overflow-y-auto pr-1">
                {blockedUserIds.map((blockedUid) => {
                  const blockedProfile = profiles.find(
                    (candidate) => candidate.uid === blockedUid,
                  );

                  const blockedName = blockedProfile?.name || "BioIndex User";

                  return (
                    <div
                      key={blockedUid}
                      className="flex items-center gap-3 rounded-2xl border border-[#93CA87]/35 bg-[#FCFFF6] p-3"
                    >
                      <img
                        src={
                          isBioIndexProfilePhoto(blockedProfile?.photoURL)
                            ? blockedProfile.photoURL
                            : defaultProfile
                        }
                        alt=""
                        className="h-11 w-11 shrink-0 rounded-xl border border-[#93CA87]/40 object-cover"
                      />

                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold text-[#374049]">
                          {blockedName}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={async () => {
                          const confirmUnblock = window.confirm(
                            `Unblock ${blockedName}?\n\nUnblocking does not restore a previous friendship.`,
                          );

                          if (!confirmUnblock) return;

                          await unblockUser(blockedUid);
                        }}
                        disabled={blockingUserId === blockedUid}
                        className="shrink-0 rounded-xl border border-[#93CA87]/50 bg-white px-3 py-2 text-xs font-semibold text-[#01767A] transition hover:bg-[#93CA87]/10 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {blockingUserId === blockedUid
                          ? "Unblocking..."
                          : "Unblock"}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="mt-5 rounded-2xl border border-dashed border-[#93CA87]/50 bg-[#FCFFF6] px-4 py-8 text-center text-sm text-slate-500">
                You have not blocked anyone.
              </div>
            )}

            <button
              type="button"
              onClick={() => setBlockedUsersOpen(false)}
              className="mt-5 w-full rounded-xl bg-[#374049] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#01767A]"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {titleModalOpen && isOwnProfile && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 overflow-y-auto px-3 pt-12 pb-6">
          <div className="bg-white rounded-3xl p-6 w-full max-w-[680px] border border-[#93ca87]/40 shadow-2xl">
            <h3
              className="text-3xl text-[#374049] text-center mb-1"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              Select Title
            </h3>

            <p className="text-sm text-slate-500 text-center mb-4 leading-relaxed max-w-xl mx-auto">
              Titles reflect your progress as a collector. They are unlocked by
              expanding your collection, increasing taxonomic diversity, and
              contributing more observations. As you grow your dataset, new
              titles will become available.
            </p>

            <div className="mb-4 flex justify-center">
              <div className="inline-flex rounded-full border border-[#93ca87]/40 bg-[#f4f6f8] p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => setTitleViewMode("all")}
                  className={`rounded-full px-4 py-2 text-xs font-medium transition ${
                    titleViewMode === "all"
                      ? "bg-[#24C3BE] text-white shadow-sm"
                      : "text-[#374049]/65 hover:bg-white hover:text-[#24C3BE]"
                  }`}
                >
                  All Titles
                </button>

                <button
                  type="button"
                  onClick={() => setTitleViewMode("unlocked")}
                  className={`rounded-full px-4 py-2 text-xs font-medium transition ${
                    titleViewMode === "unlocked"
                      ? "bg-[#24C3BE] text-white shadow-sm"
                      : "text-[#374049]/65 hover:bg-white hover:text-[#24C3BE]"
                  }`}
                >
                  Unlocked
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-[430px] overflow-y-auto rounded-2xl bg-[#374049] border border-[#374049]/10 p-3 pr-2">
              {[
                {
                  id: "beginner",
                  label: "Beginner",
                  description: "Starting your collection",
                },
                ...TITLES,
              ]
                .filter((t) => {
                  if (titleViewMode === "all") return true;

                  const unlockedTitleIds = new Set(
                    unlockedTitles.map((u) => u.id),
                  );

                  return t.id === "beginner" || unlockedTitleIds.has(t.id);
                })
                .map((t) => {
                  const unlockedTitleIds = new Set(
                    unlockedTitles.map((u) => u.id),
                  );
                  const isUnlocked =
                    t.id === "beginner" || unlockedTitleIds.has(t.id);
                  const isSelected = pendingTitle === t.label;

                  return (
                    <div
                      key={t.id || t.label}
                      onClick={() => {
                        if (!isUnlocked) return;
                        setPendingTitle(t.label);
                      }}
                      className={`relative min-h-[72px] p-3 border rounded-2xl transition overflow-hidden ${
                        isUnlocked
                          ? isSelected
                            ? "cursor-pointer border-[#01767a] bg-[#93CA87] shadow-sm ring-2 ring-[#01767a]/25"
                            : "cursor-pointer border-[#374049]/25 bg-white hover:bg-[#EFF8ED] hover:border-[#93CA87] hover:shadow-sm"
                          : "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                      }`}
                    >
                      {!isUnlocked && (
                        <div className="absolute top-3 right-3 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                          <span>🔒</span>
                          <span>Locked</span>
                        </div>
                      )}

                      <div
                        className={`text-sm font-semibold pr-20 ${
                          isUnlocked
                            ? isSelected
                              ? "text-white"
                              : "text-[#374049]"
                            : "text-slate-400"
                        }`}
                      >
                        {isUnlocked ? t.label : "Locked Title"}
                      </div>

                      <div
                        className={`mt-0.5 text-[11px] leading-snug ${
                          isUnlocked
                            ? isSelected
                              ? "text-white/80"
                              : "text-[#374049]/65"
                            : "text-slate-400"
                        }`}
                      >
                        {t.description}
                      </div>
                    </div>
                  );
                })}
            </div>

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => {
                  setPendingTitle(profile.title || selectedTitle);
                  setTitleModalOpen(false);
                }}
                className="flex-1 px-4 py-2 border border-[#93ca87]/40 rounded-xl text-[#374049] hover:bg-slate-50 transition"
              >
                Cancel
              </button>

              <button
                onClick={async () => {
                  if (!pendingTitle || savingTitle) return;

                  setSavingTitle(true);

                  try {
                    const now = Date.now();

                    const updatedProfile = {
                      ...profile,
                      title: pendingTitle,
                      sectionUpdatedAt: {
                        ...(profile.sectionUpdatedAt || {}),
                        profile: now,
                      },
                    };

                    setProfile(updatedProfile);

                    await setDoc(
                      doc(db, "profile", auth.currentUser.uid),
                      {
                        title: pendingTitle,
                        sectionUpdatedAt: {
                          ...(profile.sectionUpdatedAt || {}),
                          profile: now,
                        },
                      },
                      { merge: true },
                    );

                    saveOwnProfileCache({
                      profile: updatedProfile,
                      stats,
                      favoriteGenera,
                      speciesWithPhotos,
                      quizSpecies,
                      collections,
                      recentUploads,
                      collectionMeta,
                      unlockedTitles,
                      sectionUpdatedAt: {
                        ...(readOwnProfileCache()?.sectionUpdatedAt || {}),
                        profile: now,
                      },
                    });

                    setTitleModalOpen(false);
                  } catch (err) {
                    console.error("Failed to save title:", err);
                  } finally {
                    setSavingTitle(false);
                  }
                }}
                disabled={savingTitle}
                className={`flex-1 px-4 py-2 rounded-xl text-white transition ${
                  savingTitle
                    ? "bg-slate-400 cursor-not-allowed"
                    : "bg-[#24c3be] hover:bg-[#1faeaa]"
                }`}
              >
                {savingTitle ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {taxonViewOpen && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={() => setTaxonViewOpen(false)}
        >
          <div
            className="bg-white rounded-[2rem] border border-[#93ca87]/40 shadow-2xl p-6 w-full max-w-3xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 mb-5">
              <h3
                className="text-3xl text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                {taxonViewLabel}
              </h3>

              {taxonViewLabel === "Species" && (
                <div className="flex rounded-full border border-[#93ca87]/40 bg-[#f4f6f8] p-1 shadow-sm">
                  <button
                    type="button"
                    onClick={() => setTaxonSortMode("scientific")}
                    className={`h-8 min-w-12 rounded-full px-2 transition ${
                      taxonSortMode === "scientific"
                        ? "bg-[#24C3BE] text-white shadow-sm"
                        : "text-[#374049]/60 hover:bg-white hover:text-[#24C3BE]"
                    }`}
                    title="Sort by scientific name"
                  >
                    <img
                      src={scientificNameTagIcon}
                      alt=""
                      className="h-6 w-14 object-contain"
                    />
                  </button>

                  <button
                    type="button"
                    onClick={() => setTaxonSortMode("common")}
                    className={`h-8 min-w-12 rounded-full px-2 transition ${
                      taxonSortMode === "common"
                        ? "bg-[#24C3BE] text-white shadow-sm"
                        : "text-[#374049]/60 hover:bg-white hover:text-[#24C3BE]"
                    }`}
                    title="Sort by common name"
                  >
                    <img
                      src={commonNameTagIcon}
                      alt=""
                      className="h-6 w-14 object-contain"
                    />
                  </button>
                </div>
              )}
            </div>

            {taxonomyNotice && (
              <div
                role="alert"
                className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
              >
                {taxonomyNotice}
              </div>
            )}

            {taxonViewLabel === "Species" &&
              taxonViewItems.some((item) => !getTaxonRoute("species", item))}

            <div className="space-y-2.5">
              {" "}
              {[...taxonViewItems]
                .sort((a, b) => {
                  if (taxonViewLabel !== "Species") return 0;

                  if (taxonSortMode === "common") {
                    const aHasCommon = !!a.common?.trim();
                    const bHasCommon = !!b.common?.trim();

                    if (aHasCommon && !bHasCommon) return -1;
                    if (!aHasCommon && bHasCommon) return 1;

                    if (!aHasCommon && !bHasCommon) {
                      return (a.scientific || "").localeCompare(
                        b.scientific || "",
                      );
                    }

                    return a.common.localeCompare(b.common);
                  }

                  return (a.scientific || "").localeCompare(b.scientific || "");
                })
                .map((item, i) => {
                  const isSpecies = taxonViewLabel === "Species";

                  const value = isSpecies ? item.scientific : item;
                  const labelToType = {
                    Kingdoms: "kingdom",
                    Phyla: "phylum",
                    Classes: "class",
                    Orders: "order",
                    Families: "family",
                    Genera: "genus",
                  };

                  const type = isSpecies
                    ? "species"
                    : labelToType[taxonViewLabel];
                  const taxonBadge = getTaxonBadge(type, value);
                  const route = getTaxonRoute(type, item);
                  const taxonomyMissing =
                    !route && (type === "species" || type === "genus");

                  return (
                    <div
                      key={i}
                      onClick={() => openTaxonItem(type, item)}
                      title={
                        taxonomyMissing
                          ? `This ${type} is not currently represented in the BioIndex taxonomy file.`
                          : undefined
                      }
                      className={`px-3 py-3 border rounded-2xl text-sm text-[#374049] flex flex-col items-start gap-2 sm:flex-row sm:justify-between sm:items-center sm:gap-4 sm:px-4 transition ${
                        taxonomyMissing
                          ? "cursor-pointer border-red-200 bg-red-50/70 hover:border-red-300 hover:bg-red-100/70"
                          : route
                            ? "cursor-pointer border-[#93ca87]/40 bg-[#FCFFF6] hover:bg-[#24c3be]/10 hover:border-[#24c3be]/40"
                            : "cursor-not-allowed opacity-50 border-slate-200 bg-slate-50"
                      }`}
                    >
                      {isSpecies ? (
                        <div className="min-w-0 w-full text-base leading-snug sm:truncate">
                          {taxonSortMode === "common" ? (
                            <>
                              <span>
                                {item.common || "Unknown Common Name"}
                              </span>
                              <span className="not-italic text-slate-500">
                                {" "}
                                (
                                <span className="italic">
                                  {item.scientific}
                                </span>
                                )
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="italic">{item.scientific}</span>
                              {item.common && (
                                <span className="not-italic">
                                  {" "}
                                  ({item.common})
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      ) : (
                        <div
                          className={`min-w-0 w-full break-words text-base leading-snug sm:truncate ${
                            taxonViewLabel === "Genera" ? "italic" : ""
                          }`}
                        >
                          {item}
                        </div>
                      )}

                      {taxonomyMissing ? (
                        <div className="shrink-0 self-start rounded-full bg-red-600 px-2.5 py-1 text-[11px] font-medium text-white sm:self-auto sm:px-3 sm:text-xs">
                          Taxonomy pending
                        </div>
                      ) : taxonBadge ? (
                        <div
                          className={`shrink-0 self-start rounded-full px-2.5 py-1 text-[11px] font-medium text-white sm:self-auto sm:px-3 sm:text-xs ${
                            taxonBadge.tone === "shared"
                              ? "bg-[#24c3be]"
                              : "bg-[#374049]"
                          }`}
                        >
                          {taxonBadge.label}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
            </div>

            <button
              onClick={() => setTaxonViewOpen(false)}
              className="mt-4 px-4 py-2 border rounded-xl w-full"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <ProfileCollectionViewer
        isOwnProfile={isOwnProfile}
        canEditObservationMetadata={canEditObservationMetadata}
        canManageCollections={canManageCollections}
        adminManageCollection={manageAdminCollection}
        collectionViewOpen={collectionViewOpen}
        setCollectionViewOpen={setCollectionViewOpen}
        collectionPhotos={collectionPhotos}
        collectionIndex={collectionIndex}
        setCollectionIndex={setCollectionIndex}
        collectionName={collectionName}
        collectionImageLoaded={collectionImageLoaded}
        setCollectionImageLoaded={setCollectionImageLoaded}
        collectionFullscreen={collectionFullscreen}
        setCollectionFullscreen={setCollectionFullscreen}
        editingForm={editingForm}
        setEditingForm={setEditingForm}
        setEditingSpecies={setEditingSpecies}
        speciesWithPhotos={speciesWithPhotos}
        savePhotoMetadata={savePhotoMetadata}
        displayLocation={displayLocation}
        hasMappedLocation={hasMappedLocation}
        formatDateForDisplay={formatDateForDisplay}
        resolveTaxonomy={resolveTaxonomy}
        collections={collections}
        setCollectionPhotos={setCollectionPhotos}
        saveCollectionOrder={saveCollectionOrder}
        deleteCollection={deleteCollection}
        collectionMeta={collectionMeta}
        setCollectionMeta={setCollectionMeta}
      />

      {profileLocationPickerOpen && editingForm && (
        <LocationPickerModal
          initialLocation={formToLocationData(editingForm)}
          onClose={() => setProfileLocationPickerOpen(false)}
          onConfirm={(locationData) => {
            setEditingForm((prev) => ({
              ...prev,
              location: locationData.name,
              locationName: locationData.name,
              locationLat: locationData.lat ?? null,
              locationLng: locationData.lng ?? null,
              locationCountry: locationData.country || "",
              locationAdminArea: locationData.adminArea || "",
              locationCity: locationData.city || "",
              locationPlaceId: locationData.placeId || "",
            }));

            setProfileLocationPickerOpen(false);
          }}
        />
      )}
      {deletingProfile && (
        <div className="fixed inset-0 z-[90] bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-3xl shadow-xl border border-[#93CA87]/40 px-8 py-7 flex flex-col items-center text-center">
            <div className="w-10 h-10 border-4 border-slate-200 border-t-[#24C3BE] rounded-full animate-spin mb-4" />
            <div className="text-sm text-[#374049] font-medium">
              Deleting profile...
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Please do not close this window.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
